import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import ReferenceChip from '@/components/ReferenceChip';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { formatINR, formatCarats, formatGrams, formatDate, formatDateTime, formatRequirementDropdownLabel } from '@/lib/format';
import { hierarchyRank as diamondHierarchyRank } from '@/lib/diamondHierarchy';
import { extractError } from '@/lib/errors';
import { InlineReference } from '@/components/InlineReference';
import {
  ArrowLeft, ArrowUp, ArrowDown, X, Hammer, Send, Plus, Trash2, ChevronDown, ChevronRight,
  Gem, Coins, ExternalLink, ArrowDownToLine, Link2, FileText, Printer, Download, Sparkles, Circle, Receipt,
  Columns, FileSpreadsheet, MessageSquare, Search, User, ImageOff, Loader2, LayoutGrid, PanelTop,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ReferenceFilter, ReferenceFilterChips } from '@/components/orders/ReferenceFilter';
import { CustomerFilter, CustomerFilterChips } from '@/components/orders/CustomerFilter';
import { OrderFilter, OrderFilterChips } from '@/components/orders/OrderFilter';
import { RawMaterialFilter, RawMaterialFilterChips } from '@/components/orders/RawMaterialFilter';
import { useReferenceConfig } from '@/hooks/useReferenceConfig';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { StageBadge, activeStepsMatch, activeStepKeys } from './Orders';
import AttributeDialog from '@/components/phase7/AttributeDialog';
import ReturnDialog from '@/components/phase7/ReturnDialog';
import MemoLink from '@/components/phase7/MemoLink';
import { PdfDownloadDialog } from '@/components/phase11/PdfDownloadDialog';
import { RepairItemsSection } from '@/components/repairs/RepairItemsSection';
// Wave 3.99k — Sticky section navigator + back-to-top pill on long pages.
import { StickySectionNav } from '@/components/StickySectionNav';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
// Wave 3.99n — Section tint helper (coloured mat + matching left strip).
import { sectionMat } from '@/lib/sectionTints';

// ============================================================================
// Gold purity view (999 ↔ 995)
// ----------------------------------------------------------------------------
// The goldsmith gold + unattributed ledgers store every gold figure in the
// canonical 999 basis. Some users prefer to read the ledger in 995 grams.
// Per user spec, 999 is treated as 100% pure and 995 as 99.5% pure, so to
// hold the *same* pure gold the 995 figure is slightly higher:
//     grams_995 = grams_999 / 0.995
// The choice is remembered in localStorage and shared (via lifted state in
// ``MaterialsWithGoldsmithSection``) across the Gold Ledger popup, the Raw
// Material (Unattributed) ledger gold section, and the summary pill.
// ============================================================================
const GOLD_PURITY_KEY = 'goldsmith.goldLedger.purityView';

function readGoldPurity() {
  try {
    return localStorage.getItem(GOLD_PURITY_KEY) === '995' ? '995' : '999';
  } catch {
    return '999';
  }
}

// Convert a 999-basis gram figure into the selected display basis.
function convertG999(grams999, purity) {
  const n = Number(grams999) || 0;
  return purity === '995' ? n / 0.995 : n;
}

// Short column-suffix label, e.g. "g999" / "g995".
function purityColLabel(purity) {
  return purity === '995' ? 'g995' : 'g999';
}

// Bare basis label used inside the bifurcation tail, e.g. "(995)".
function purityUnitLabel(purity) {
  return purity === '995' ? '995' : '999';
}

// Segmented Gold 999 / Gold 995 toggle. Gold-themed (amber) to match the
// rest of the gold accents on this page. Fully keyboard-accessible.
function GoldPurityToggle({ purity, onChange, testId = 'gold-purity-toggle' }) {
  const opts = [
    { v: '999', label: 'Gold 999', hint: '100%' },
    { v: '995', label: 'Gold 995', hint: '99.5%' },
  ];
  return (
    <div
      className="inline-flex items-center rounded-md border border-amber-300 overflow-hidden text-[11px] shadow-sm"
      role="group"
      aria-label="Gold purity view"
      data-testid={testId}
    >
      {opts.map((o) => {
        const active = purity === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            aria-pressed={active}
            className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:relative ${
              active
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-600 hover:bg-amber-50'
            }`}
            data-testid={`${testId}-${o.v}`}
          >
            {o.label} <span className="opacity-70">({o.hint})</span>
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// Ledger view controls (date sort + From/To range) — shared across the
// Gold Ledger popup and the Labour Ledger section.
// ----------------------------------------------------------------------------
// Both ledgers arrive from the backend as chronological (ascending) rows that
// already carry a running balance. The controls below let the user:
//   • flip the display order (Oldest first ↔ Newest first), and
//   • narrow the visible rows to a From/To date window.
// When a From date is set, the running balance carried into that date is shown
// as an "Opening Balance" so the window reads like a period statement. The
// header summary totals stay full-ledger (per user spec) — only the row list
// is filtered. The sort direction is remembered in localStorage.
// ============================================================================
const LEDGER_SORT_KEY = 'goldsmith.ledger.sortDir';

function readLedgerSort() {
  try {
    return localStorage.getItem(LEDGER_SORT_KEY) === 'desc' ? 'desc' : 'asc';
  } catch {
    return 'asc';
  }
}

// Filter chronological (ascending, balance-carrying) rows to a date range and
// apply the chosen display sort. Returns the display rows, the opening balance
// (running balance carried into the From date), and range metadata.
function applyLedgerView(rows, { from, to, sortDir, tsKey = 'timestamp', balanceKey }) {
  const fromT = from ? new Date(`${from}T00:00:00`) : null;
  const toT = to ? new Date(`${to}T23:59:59.999`) : null;
  // Normalise to chronological ascending first — different ledgers return
  // rows in different orders (gold ascending, labour descending). Each row
  // already carries its own running balance, so re-sorting by timestamp keeps
  // the balances correct while making the opening-balance math order-agnostic.
  const chron = [...(rows || [])].sort(
    (a, b) => String(a[tsKey] || '').localeCompare(String(b[tsKey] || '')),
  );
  let opening = 0;
  const inRange = [];
  for (const r of chron) {
    const t = new Date(r[tsKey]);
    if (fromT && t < fromT) {
      if (balanceKey) opening = Number(r[balanceKey]) || 0;
      continue;
    }
    if (toT && t > toT) continue;
    inRange.push(r);
  }
  const display = sortDir === 'desc' ? [...inRange].reverse() : inRange;
  return {
    display,
    openingBalance: fromT ? opening : 0,
    showOpening: !!fromT,
    hasRange: !!(fromT || toT),
  };
}

function LedgerViewControls({ view, testId = 'ledger-view-controls' }) {
  if (!view) return null;
  const { sortDir, onSortChange, from, to, onFromChange, onToChange } = view;
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={testId}>
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wide text-slate-500 font-sans">From</label>
        <DatePicker
          value={from || ''}
          onValueChange={(v) => onFromChange(v)}
          placeholder="DD-MM-YYYY"
          className="h-9 w-[150px]"
          maxDate={to || undefined}
          data-testid={`${testId}-from`}
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wide text-slate-500 font-sans">To</label>
        <DatePicker
          value={to || ''}
          onValueChange={(v) => onToChange(v)}
          placeholder="DD-MM-YYYY"
          className="h-9 w-[150px]"
          minDate={from || undefined}
          data-testid={`${testId}-to`}
        />
      </div>
      {(from || to) && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 text-xs text-slate-500 hover:text-slate-700"
          onClick={() => { onFromChange(''); onToChange(''); }}
          data-testid={`${testId}-clear`}
        >
          <X size={13} className="mr-1" /> Clear dates
        </Button>
      )}
      <div
        className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-[11px] shadow-sm self-end"
        role="group"
        aria-label="Sort ledger by date"
        data-testid={`${testId}-sort`}
      >
        <button
          type="button"
          onClick={() => onSortChange('asc')}
          aria-pressed={sortDir === 'asc'}
          className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
            sortDir === 'asc' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
          data-testid={`${testId}-sort-asc`}
        >
          <ArrowUp size={12} className="inline mr-1 -mt-0.5" />Oldest first
        </button>
        <button
          type="button"
          onClick={() => onSortChange('desc')}
          aria-pressed={sortDir === 'desc'}
          className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
            sortDir === 'desc' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
          data-testid={`${testId}-sort-desc`}
        >
          <ArrowDown size={12} className="inline mr-1 -mt-0.5" />Newest first
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Wave 3.98 — Collapsible section wrapper for the Goldsmith Detail page.
//
// Every content block on this page ("Action Buttons", "Pending Orders", "Repair
// Items", etc.) is nested inside a ``SectionCollapsible`` so the operator sees
// a compact list of section titles on load and only expands the ones relevant
// to their current task. Defaults to closed (per user request).
//
// The header row is a clickable button (hover + focus-visible states) that
// toggles the section; an optional ``right`` slot renders inline actions
// (e.g. filters) without triggering the toggle.
// ============================================================================

// Wave 3.98 — Section-header badge (pill) used in the ``right`` slot of
// ``SectionCollapsible`` so the operator can see key counts / amounts
// without expanding the section. Renders nothing when ``count`` is
// falsy (or 0) unless ``showZero`` is explicitly set.
function SectionBadge({ tone = 'slate', label, testId, showZero = false, count }) {
  // Hide when count is 0/undefined/null unless the caller opts in.
  if ((count === 0 || count === undefined || count === null) && !showZero) return null;
  const palette = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    navy: 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] border-[var(--jewellery-icon)]/20',
    rose: 'bg-rose-50 text-rose-800 border-rose-200',
  }[tone] || 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${palette}`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

function SectionCollapsible({
  title,
  description,
  icon: Icon = null,
  defaultOpen = true,
  testId,
  right = null,
  children,
}) {  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Card className="overflow-hidden" data-testid={testId}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex items-center gap-3 pr-3">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex-1 flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-slate-50/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--gold-accent)] transition-colors"
              data-testid={testId ? `${testId}-trigger` : undefined}
              aria-expanded={open}
            >
              <div className="min-w-0 flex-1">
                <div className="text-base font-medium text-[var(--ink-navy)] flex items-center gap-2">
                  {Icon ? <Icon size={16} className="text-[var(--gold-accent)] shrink-0" /> : null}
                  <span className="truncate">{title}</span>
                </div>
                {description ? (
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{description}</div>
                ) : null}
              </div>
              <ChevronDown
                size={16}
                className={`text-slate-500 shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
          {right ? (
            <div
              className="shrink-0 flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
              data-testid={testId ? `${testId}-right-slot` : undefined}
            >
              {right}
            </div>
          ) : null}
        </div>
        <CollapsibleContent>
          <div className="border-t border-[var(--divider)]">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default function GoldsmithDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Goldsmith Detail"
      subtitle="Orders & material with this goldsmith. Issue materials in a single multi-row form."
      right={
        <Button variant="outline" onClick={() => navigate(-1)} className="h-9" data-testid="goldsmith-back-button">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
      }
    >
      <CompanyGate>
        <GoldsmithDetailContent id={id} companyId={effectiveCompanyId} />
      </CompanyGate>
      {/* Wave 3.99j — Global back-to-top pill on this long detail page. */}
      <ScrollToTopButton />
    </AppShell>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Portal Jewellery Repair — re-open an existing received piece as a
// reduced-checklist repair order (RPR-…) assigned to this goldsmith.
// ─────────────────────────────────────────────────────────────────────
function PortalRepairSection({ goldsmith, companyId, onChanged }) {
  const navigate = useNavigate();
  const [eligible, setEligible] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState('');
  const [note, setNote] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const loadEligible = React.useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/portal-repairs/eligible-jewellery', { params: { company_id: companyId } });
      setEligible(r.data || []);
    } catch (e) {
      setEligible([]);
    } finally {
      setLoadingList(false);
    }
  }, [companyId]);

  React.useEffect(() => { loadEligible(); }, [loadEligible]);

  const selected = eligible.find((j) => j.id === selectedId) || null;

  const submit = async () => {
    if (!selectedId) { toast.error('Select a jewellery number to send for repair'); return; }
    setSending(true);
    try {
      const r = await api.post('/portal-repairs', {
        company_id: companyId,
        jewellery_id: selectedId,
        goldsmith_id: goldsmith.id,
        note: note || '',
      });
      toast.success(`Repair order ${r.data?.order_no || ''} created for ${r.data?.jewellery_no || ''}`);
      setSelectedId(''); setNote('');
      onChanged && onChanged();
      if (r.data?.order_id) navigate(`/orders/${r.data.order_id}`);
    } catch (e) {
      toast.error(extractError(e, 'Failed to send for repair'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="portal-repair-section">
      <div className="text-[12px] text-slate-500 leading-relaxed">
        Select a jewellery piece (by its Jewellery No) that was received on the portal to re-open it as a
        repair order for <span className="font-medium text-slate-700">{goldsmith.name}</span>. The piece's
        existing materials are carried forward; it runs a short checklist (Setting → Quality Check → Receive).
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className="block text-[11px] font-medium text-slate-600 mb-1">Jewellery No</label>
          <SearchableSelect
            items={eligible}
            value={selectedId}
            onValueChange={(v) => setSelectedId(v || '')}
            itemToValue={(j) => j.id}
            itemToLabel={(j) => `${j.jewellery_no}${j.title ? ' — ' + j.title : ''}`}
            itemToSubLabel={(j) => (j.last_repaired_at ? `Last repaired ${formatDate(j.last_repaired_at)}` : (j.source_order_no || ''))}
            searchKeys={['jewellery_no', 'title']}
            placeholder={loadingList ? 'Loading…' : 'Search jewellery number…'}
            allowClear
            testId="portal-repair-jewellery-select"
          />
        </div>
        <Button onClick={submit} disabled={!selectedId || sending} className="luxury-button" data-testid="portal-repair-submit">
          {sending ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Hammer size={15} className="mr-1.5" />}
          Send for Repair
        </Button>
      </div>
      {selected && (
        <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white/70 p-3" data-testid="portal-repair-selected">
          <div className="w-12 h-12 rounded-md bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
            {selected.image
              ? <img src={selected.image} alt={selected.jewellery_no} className="w-full h-full object-contain p-0.5" />
              : <Gem size={18} className="text-slate-300" />}
          </div>
          <div className="min-w-0">
            <div className="font-medium text-sm text-slate-800" data-testid="portal-repair-selected-no">{selected.jewellery_no}</div>
            <div className="text-[12px] text-slate-500 truncate">{selected.title || '—'}</div>
          </div>
        </div>
      )}
      <div>
        <label className="block text-[11px] font-medium text-slate-600 mb-1">Repair note (optional)</label>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Re-polish + fix clasp" data-testid="portal-repair-note" />
      </div>
    </div>
  );
}


// Wave 3.98 — Collect all image + PDF attachments on an order for the
// hover preview popover. Mirrors ``collectHoverAttachments`` in
// Orders.jsx so both pages surface the same set of previews.
function collectHoverAttachments(order) {
  const imgs = Array.isArray(order?.images) ? order.images : [];
  const thumbs = Array.isArray(order?.images_thumb) ? order.images_thumb : [];
  const titles = Array.isArray(order?.image_titles) ? order.image_titles : [];
  const out = [];
  for (let i = 0; i < imgs.length; i += 1) {
    const raw = imgs[i];
    const thumb = thumbs[i];
    const title = titles[i] || '';
    if (typeof raw === 'string' && raw.startsWith('data:image/')) {
      out.push({ src: raw, isPdf: false, title, index: i });
    } else if (typeof raw === 'string' && raw.startsWith('data:application/pdf')) {
      out.push({
        src: (typeof thumb === 'string' && thumb.startsWith('data:image/')) ? thumb : null,
        isPdf: true,
        title,
        index: i,
      });
    }
  }
  return out;
}

// Wave 3.98 — Single order card for the "Pending Orders with this
// Goldsmith" section. Extracted so we can keep the HoverCard state
// (which attachment is currently displayed) local to each card.
function PendingOrderCard({ order: o, thumb, pickOrderThumb: _pt, refEnabled = false, refLabelShort = 'Page' }) {
  const hoverAttachments = React.useMemo(() => collectHoverAttachments(o), [o]);
  const [hoverIdx, setHoverIdx] = React.useState(0);
  const activeHover = hoverAttachments[hoverIdx] || hoverAttachments[0] || null;
  return (
    <Link
      to={`/orders/${o.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex gap-3 p-3 rounded-md border border-[var(--divider)] bg-white hover:border-[var(--ink-navy)]/30 hover:shadow-sm transition-shadow duration-150"
      data-testid={`goldsmith-order-card-${o.order_no}`}
    >
      {/* Thumbnail — wrapped in a HoverCard so operators can peek at
          the full-res image + all attachments without opening the
          order. HoverCardContent is portalled to <body> so it renders
          outside the surrounding <Link>. */}
      <HoverCard openDelay={200} closeDelay={80}>
        <HoverCardTrigger asChild>
          <div
            className="shrink-0 w-14 h-14 rounded-md overflow-hidden border border-[var(--divider)] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center"
            data-testid={`goldsmith-order-card-thumb-${o.order_no}`}
          >
            {thumb ? (
              <img
                src={thumb}
                alt={o.title || o.order_no}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageOff size={20} className="text-slate-300" aria-hidden />
            )}
          </div>
        </HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="start"
          className="w-[340px] p-3 shadow-xl border-[var(--divider)]"
          data-testid={`goldsmith-order-card-hover-${o.order_no}`}
          onClick={(e) => {
            // Prevent the hover content from triggering navigation on
            // the parent <Link> (it renders in a portal but the click
            // still bubbles synthetically in React).
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-2">
              <div
                className="text-[13px] font-semibold text-[var(--ink-navy)] leading-snug line-clamp-2"
                title={o.title || ''}
              >
                {o.title || o.order_no}
              </div>
              <span className="font-mono text-[10px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-1.5 py-0.5 rounded shrink-0">
                {o.order_no}
              </span>
            </div>
            {activeHover ? (
              <div className="rounded-md overflow-hidden bg-slate-100 border border-[var(--divider)]">
                {activeHover.src ? (
                  <img
                    src={activeHover.src}
                    alt={activeHover.title || o.title || o.order_no}
                    className="w-full max-h-[260px] object-contain bg-slate-900/5"
                    data-testid={`goldsmith-order-card-hover-image-${o.order_no}`}
                  />
                ) : (
                  <div className="w-full h-[180px] flex flex-col items-center justify-center text-slate-500 bg-gradient-to-br from-slate-50 to-slate-100">
                    <FileText size={40} strokeWidth={1.2} />
                    <div className="text-[11px] mt-1 uppercase tracking-wider">PDF · preview unavailable</div>
                  </div>
                )}
                {(activeHover.title || activeHover.isPdf) && (
                  <div className="px-2 py-1 flex items-center justify-between gap-2 bg-white border-t border-[var(--divider)]">
                    <div className="text-[11px] text-slate-600 truncate" title={activeHover.title}>
                      {activeHover.title || (activeHover.isPdf ? 'PDF attachment' : 'Image attachment')}
                    </div>
                    {activeHover.isPdf && (
                      <Badge variant="outline" className="text-[9px] border-rose-200 bg-rose-50 text-rose-700 shrink-0">
                        PDF
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-[150px] rounded-md flex flex-col items-center justify-center text-slate-400 bg-gradient-to-br from-slate-50 to-slate-100 border border-[var(--divider)]">
                <FileText size={32} strokeWidth={1.2} />
                <div className="text-[11px] mt-1">No attachments</div>
              </div>
            )}
            {/* Attachment strip — only rendered when there are 2+ attachments. */}
            {hoverAttachments.length > 1 && (
              <div
                className="flex items-center gap-1.5 overflow-x-auto pb-1"
                data-testid={`goldsmith-order-card-hover-strip-${o.order_no}`}
              >
                {hoverAttachments.map((att, idx) => (
                  <button
                    type="button"
                    key={att.index}
                    onMouseEnter={() => setHoverIdx(idx)}
                    onFocus={() => setHoverIdx(idx)}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setHoverIdx(idx); }}
                    className={`shrink-0 relative w-11 h-11 rounded overflow-hidden border transition-all ${
                      idx === hoverIdx
                        ? 'border-[var(--gold-accent)] ring-1 ring-[var(--gold-accent)]'
                        : 'border-[var(--divider)] hover:border-slate-400'
                    }`}
                    aria-label={`Preview attachment ${idx + 1}`}
                  >
                    {att.src ? (
                      <img src={att.src} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
                        <FileText size={12} />
                      </div>
                    )}
                    {att.isPdf && (
                      <span className="absolute bottom-0 right-0 text-[7px] px-1 bg-rose-600 text-white font-bold">
                        PDF
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-slate-500 pt-1 border-t border-[var(--divider)]">
              {o.customer_name && <div className="truncate"><span className="text-slate-400">Customer · </span>{o.customer_name}</div>}
              {o.stage && <div className="truncate"><span className="text-slate-400">Stage · </span>{o.stage}{o.substage ? ` · ${o.substage}` : ''}</div>}
              {(o.requirements || []).length > 0 && (
                <div className="truncate font-mono tabular-nums">
                  <span className="text-slate-400">Reqs · </span>{(o.requirements || []).length}
                </div>
              )}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded group-hover:underline">{o.order_no}</span>
            {refEnabled && o.reference_no != null && (
              <InlineReference
                referenceNo={o.reference_no}
                labelShort={refLabelShort}
                testId={`goldsmith-order-${o.order_no}-page-no`}
                className="shrink-0"
              />
            )}
          </div>
          {/* Wave 3.98 — Multi-pill Stage badge (matches Orders page). */}
          <StageBadge
            stage={o.stage}
            substage={o.substage}
            holdStatus={o.hold_status}
            activeSteps={o.active_steps}
          />
        </div>
        <div className="text-sm font-medium text-[var(--ink-navy)] truncate" title={o.title || ''}>
          {o.title || <span className="italic text-slate-400">Untitled jewellery</span>}
        </div>
        <div className="text-[11px] text-slate-500 truncate mt-0.5 flex items-center gap-1" title={o.customer_name || ''}>
          <User size={10} className="shrink-0 text-slate-400" aria-hidden />
          {o.customer_name ? (
            <span data-testid={`goldsmith-order-${o.order_no}-customer`}>{o.customer_name}</span>
          ) : (
            <span className="italic text-slate-400">No customer</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
          {(o.requirements || []).length > 0 && (
            <span className="inline-flex items-center gap-1" data-testid={`goldsmith-order-${o.order_no}-req-count`}>
              <Sparkles size={9} /> {o.requirements.length} req{o.requirements.length === 1 ? '' : 's'}
            </span>
          )}
          {o.description && (
            <span className="truncate" title={o.description}>· {o.description}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

function GoldsmithDetailContent({ id, companyId }) {
  // Wave 3.41 — Restricted users hide value column in the Recent Issues
  // table; the per-section MaterialsWithGoldsmithSection already reads
  // its own copy of ``canViewValues`` for its pills.
  const { canViewValues } = useAuth();
  const [goldsmith, setGoldsmith] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [filter, setFilter] = useState({ status: 'active' });
  const [issueOpen, setIssueOpen] = useState(false);
  const [recentIssues, setRecentIssues] = useState([]);
  const [balances, setBalances] = useState(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [attrCtx, setAttrCtx] = useState(null); // {issueId, lineId, lineMeta, issueMemoNo}
  const [returnCtx, setReturnCtx] = useState(null); // {filter}
  // Wave 3.37 — Image List PDF dialog (asks for branding choice).
  const [imageListPdfOpen, setImageListPdfOpen] = useState(false);
  // Wave 3.98 — Pending Orders section: universal search + stage filter.
  // ``ordersSearch`` is the raw text the user is typing; ``ordersSearchDebounced``
  // is what actually hits the backend (300 ms after keystrokes settle) so
  // we don't spam the server on every character.
  const [ordersSearch, setOrdersSearch] = useState('');
  const [ordersSearchDebounced, setOrdersSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setOrdersSearchDebounced(ordersSearch), 300);
    return () => clearTimeout(t);
  }, [ordersSearch]);
  const [ordersStage, setOrdersStage] = useState('all');
  // Reference-Number system — enabled per company + Pending Orders filter.
  const { enabled: refEnabled, label: refLabel, labelShort: refLabelShort } = useReferenceConfig(companyId);
  const [ordersReferenceNos, setOrdersReferenceNos] = useState([]);
  // Wave 4.x — multi-select Customer filter for Pending Orders (client-side,
  // composes with Universal Search + Stage + Page No filters).
  const [customerFilter, setCustomerFilter] = useState([]);
  // Wave 3.98 — Collapsed-section badge summaries.
  // ``repairSummary`` is the ``{external: {totals:{count}}, internal:…}``
  // payload from the repairs endpoint; ``labourSummary`` is the ledger's
  // ``{totals: {closing_balance_inr, ...}, rows: [...]}`` shape used to
  // derive unpaid labour. Both are optional (fetch failures leave the
  // badge hidden — e.g. when the user lacks finance role for labour).
  const [repairSummary, setRepairSummary] = useState(null);
  const [labourSummary, setLabourSummary] = useState(null);

  // Wave 4.x — Progressive loading for Pending Orders (mirrors the Orders
  // page). A fast first page (15) renders almost instantly, a cheap
  // ``count_only`` request fills the total up front, then the full set
  // streams in the background to power tabs 2+ and client-side filters.
  const ordersLoadSeq = React.useRef(0);
  const [ordersFullyLoaded, setOrdersFullyLoaded] = useState(false);
  const [ordersServerCount, setOrdersServerCount] = useState(null);

  // Shared ledger view (date sort + From/To range) for BOTH the Gold Ledger
  // popup and the Labour Ledger section (per user spec — one setting drives
  // both). Sort direction is remembered across visits; the date range is
  // session-only (resets on reload).
  const [ledgerSortDir, setLedgerSortDir] = useState(readLedgerSort);
  const changeLedgerSort = (v) => {
    const next = v === 'desc' ? 'desc' : 'asc';
    setLedgerSortDir(next);
    try { localStorage.setItem(LEDGER_SORT_KEY, next); } catch { /* noop */ }
  };
  const [ledgerFrom, setLedgerFrom] = useState('');
  const [ledgerTo, setLedgerTo] = useState('');
  const ledgerView = {
    sortDir: ledgerSortDir,
    onSortChange: changeLedgerSort,
    from: ledgerFrom,
    to: ledgerTo,
    onFromChange: setLedgerFrom,
    onToChange: setLedgerTo,
  };

  const load = async () => {
    try {
      // Wave 3.98 — Sync Pending Orders with the Order Forms page:
      // send ``stage`` (accepts legacy stage OR checklist-step key)
      // and ``search`` to the backend so filtering happens server-side
      // with the same logic used by ``/orders``.
      const ordersParams = {};
      if (filter.status !== 'all') ordersParams.status = filter.status;
      if (ordersStage && ordersStage !== 'all') ordersParams.stage = ordersStage;
      if (ordersSearchDebounced && ordersSearchDebounced.trim()) ordersParams.search = ordersSearchDebounced.trim();

      // Wave 4.x — PROGRESSIVE LOAD for Pending Orders (mirrors Orders.jsx).
      // Fire these independently of the shell data so a large order set never
      // blocks the page from rendering. ``ordersLoadSeq`` guards against
      // out-of-order responses when filters change rapidly.
      const seq = ++ordersLoadSeq.current;
      setOrdersFullyLoaded(false);
      // (a) cheap total — fills the count/badge/pagination bar immediately.
      api.get(`/orders/by-goldsmith/${id}`, { params: { ...ordersParams, count_only: true } })
        .then(({ data }) => {
          if (seq === ordersLoadSeq.current) {
            setOrdersServerCount(typeof data?.count === 'number' ? data.count : null);
          }
        })
        .catch(() => { if (seq === ordersLoadSeq.current) setOrdersServerCount(null); });
      // (b) fast first page (15) so the first range tab renders instantly.
      try {
        const { data } = await api.get(`/orders/by-goldsmith/${id}`, { params: { ...ordersParams, limit: 15 } });
        if (seq === ordersLoadSeq.current) setAllOrders(Array.isArray(data) ? data : []);
      } catch { if (seq === ordersLoadSeq.current) setAllOrders([]); }
      // (c) full set in the background — replaces the fast page when ready so
      //     tabs 2+ and client-side search/reference filters see everything.
      api.get(`/orders/by-goldsmith/${id}`, { params: ordersParams })
        .then(({ data }) => {
          if (seq !== ordersLoadSeq.current) return;
          setAllOrders(Array.isArray(data) ? data : []);
          setOrdersFullyLoaded(true);
        })
        .catch(() => { if (seq === ordersLoadSeq.current) setOrdersFullyLoaded(true); });

      // Shell data (goldsmith identity + recent issues + balances) is small
      // and loads in parallel — the page shell renders as soon as this
      // resolves, regardless of how large the order set is.
      const [g, gi, bal] = await Promise.all([
        api.get(`/masters/goldsmiths`, { params: { company_id: companyId } }),
        api.get(`/goldsmith-issues/`, { params: { goldsmith_id: id, company_id: companyId, limit: 50 } }),
        api.get(`/goldsmith-issues/balance/by-goldsmith/${id}`).catch(() => ({ data: null })),
      ]);
      setGoldsmith((g.data || []).find((x) => x.id === id));
      setRecentIssues(gi.data || []);
      setBalances(bal.data);
      // Wave 3.98 — Kick off badge-summary fetches in the background.
      // Failures (permission / network) leave the badge hidden — we
      // never surface an error toast for these as they're informational.
      api.get(`/repairs/by-goldsmith/${id}/summary`, { params: { company_id: companyId } })
        .then((r) => setRepairSummary(r.data || null))
        .catch(() => setRepairSummary(null));
      api.get(`/goldsmith-labour-ledger/${id}/labour-ledger`, { params: { company_id: companyId, limit: 500 } })
        .then((r) => setLabourSummary(r.data || null))
        .catch(() => setLabourSummary(null));
    } catch (e) { toast.error(extractError(e, 'Failed to load goldsmith data')); }
  };
  // Wave 3.98 — Reload when Stage / Search filters change (server-side
  // filtering keeps the badge count + PDF export in sync with the
  // on-screen grid).
  useEffect(() => {
    if (id && companyId) load();
    /* eslint-disable-next-line */
  }, [id, companyId, filter.status, ordersStage, ordersSearchDebounced]);

  // Compute totals across all materials currently with this goldsmith.
  // IMPORTANT: useMemo must be called before any early return to respect Rules of Hooks.
  const hasAnyBalance = useMemo(() => {
    if (!balances) return false;
    return (balances.by_material || []).some((m) => {
      if (m.unattributed) return true;
      if ((m.orders || []).length > 0) return true;
      return false;
    });
  }, [balances]);

  // Wave 3.98 — Pending Orders section derived state.
  // ``visibleOrders`` is essentially ``allOrders`` (server-side already
  // filters by stage + search) but during the debounce window we
  // re-apply the same client-side match on ``ordersSearch`` so typing
  // feels instantaneous.
  const visibleOrders = useMemo(() => {
    const q = (ordersSearch || '').trim().toLowerCase();
    const refSet = new Set(ordersReferenceNos || []);
    const custSet = new Set(customerFilter || []);
    return (allOrders || []).filter((o) => {
      if (q) {
        const hay = [
          o.order_no || '',
          o.title || '',
          o.customer_name || '',
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (refSet.size > 0 && !refSet.has(o.reference_no)) return false;
      if (custSet.size > 0 && !custSet.has(o.customer_id)) return false;
      return true;
    });
  }, [allOrders, ordersSearch, ordersReferenceNos, customerFilter]);

  // Distinct customers present in the loaded orders — powers the multi-select.
  const customerOptions = useMemo(() => {
    const seen = new Map();
    for (const o of (allOrders || [])) {
      if (o && o.customer_id && !seen.has(o.customer_id)) {
        seen.set(o.customer_id, o.customer_name || 'Unnamed');
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [allOrders]);
  const customerNameById = useMemo(() => {
    const m = {};
    for (const c of customerOptions) m[c.id] = c.name;
    return m;
  }, [customerOptions]);

  // Wave 4.x — Pending Orders paginated in fixed 15-row ranges with tabs
  // (1-15, 16-30, 31-45, …). Filters/search still run over the FULL
  // ``visibleOrders`` set; only the active range renders.
  const PENDING_PAGE_SIZE = 15;
  const [pendingPage, setPendingPage] = useState(0); // 0-indexed range
  useEffect(() => { setPendingPage(0); }, [visibleOrders]);
  const pendingTotal = visibleOrders.length;
  const pendingTotalPages = Math.max(1, Math.ceil(pendingTotal / PENDING_PAGE_SIZE));
  const pendingCurrent = Math.min(pendingPage, pendingTotalPages - 1);
  const pendingStart = pendingCurrent * PENDING_PAGE_SIZE;
  const pagedPendingOrders = visibleOrders.slice(pendingStart, pendingStart + PENDING_PAGE_SIZE);
  // Wave 4.x — Total shown in the badge / count line. While the full set is
  // still streaming we prefer the cheap server count so operators see the
  // real total immediately (the loaded rows may only be the first 15).
  const ordersLoading = !ordersFullyLoaded;
  const pendingCountTotal = (ordersLoading && typeof ordersServerCount === 'number')
    ? ordersServerCount
    : allOrders.length;
  const ordersReferenceOptions = useMemo(() => {
    if (!refEnabled) return [];
    const set = new Set();
    for (const o of allOrders) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return Array.from(set);
  }, [refEnabled, allOrders]);

  // Wave 3.98 — Prefer the raw ``images[0]`` data URI (sharp) over the
  // tiny ``images_thumb[0]`` for the on-card thumbnail. Falls back to
  // the thumb for PDF-only orders (thumb is the rendered first page).
  const pickOrderThumb = (o) => {
    const imgs = Array.isArray(o?.images) ? o.images : [];
    for (const u of imgs) if (typeof u === 'string' && u.startsWith('data:image/')) return u;
    const thumbs = Array.isArray(o?.images_thumb) ? o.images_thumb : [];
    for (const t of thumbs) if (typeof t === 'string' && t.startsWith('data:image/')) return t;
    return null;
  };

  // Wave 3.98 — Section-badge derived numbers (shown in the ``right``
  // slot of each SectionCollapsible header so operators can triage
  // without expanding).
  const repairItemsCount = useMemo(() => {
    if (!repairSummary) return 0;
    const ex = repairSummary?.external?.totals?.count || 0;
    const ins = repairSummary?.internal?.totals?.count || 0;
    return Number(ex) + Number(ins);
  }, [repairSummary]);
  const unpaidLabourInr = useMemo(() => {
    if (!labourSummary) return 0;
    // Prefer aggregated outstanding_amount across labour_credit rows —
    // matches the "Unpaid" pill inside the Labour Ledger section.
    const rows = Array.isArray(labourSummary?.rows) ? labourSummary.rows : [];
    let outstanding = 0;
    for (const r of rows) {
      if ((r?.entry_type || '') === 'labour_credit') {
        outstanding += Number(r?.outstanding_amount || 0);
      }
    }
    return Math.max(0, outstanding);
  }, [labourSummary]);

  if (!goldsmith) return <div className="text-slate-500 py-8">Loading…</div>;

  const openAttribute = (issueId, lineId, lineMeta, issueMemoNo) => {
    setAttrCtx({ issueId, lineId, lineMeta, issueMemoNo });
  };
  // Pooled attribution — ``pool`` describes a whole classification's
  // consolidated unattributed balance (see AttributeDialog ``pool`` prop).
  const openAttributePool = (pool) => {
    setAttrCtx({ pool });
  };
  const openReturn = (extraFilter) => {
    setReturnCtx({ filter: extraFilter || null });
    setReturnOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Wave 3.98 — Goldsmith identity + primary action strip (always
          visible). Contact info + specialization + Status filter +
          Return + Issue buttons all live here so operators can act on
          the goldsmith without expanding a section. */}
      <section id="gs-overview" className={sectionMat('teal')}>
      <Card className="p-5" data-testid="goldsmith-identity-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Hammer size={18} className="text-[var(--goldsmith-icon)]" />
              <div className="text-xl font-medium text-[var(--ink-navy)]" data-testid="goldsmith-detail-name">{goldsmith.name}</div>
              {goldsmith.specialization && <Badge variant="secondary" className="text-[10px]">{goldsmith.specialization}</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {goldsmith.phone && <span>{goldsmith.phone} · </span>}
              {goldsmith.email && <span>{goldsmith.email} · </span>}
              {goldsmith.address}
            </div>
          </div>
          <div className="flex items-end gap-3 flex-wrap" data-testid="goldsmith-action-buttons">
            <div>
              <Label className="text-[11px] text-slate-500">Order Status</Label>
              <Select value={filter.status} onValueChange={(v) => setFilter({ status: v })}>
                <SelectTrigger className="w-40 h-9" data-testid="goldsmith-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="open">All Open</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              onClick={() => openReturn(null)}
              disabled={!hasAnyBalance}
              className="h-9 border-amber-200 text-amber-700 hover:bg-amber-50"
              data-testid="goldsmith-return-button"
            >
              <ArrowDownToLine size={14} className="mr-1.5" /> Return
            </Button>
            <Button onClick={() => setIssueOpen(true)} className="luxury-button h-9" data-testid="goldsmith-issue-button">
              <Send size={14} className="mr-1.5" /> Issue
            </Button>
          </div>
        </div>
      </Card>
      </section>

      {/* Wave 3.99k — Sticky section-nav chip bar.
          Wave 3.99m — Chips now surface small badge counts for
          sections whose data is loaded at this level (Pending Orders,
          Repair Items, Recent Issues). Sections powered by
          self-loading sub-components (Requirement List, Materials,
          Labour Ledger) are left without a badge so we don't render a
          stale zero before their internal fetch completes. */}
      <StickySectionNav
        sections={[
          { id: 'gs-overview', label: 'Overview', tint: 'teal' },
          {
            id: 'gs-pending-orders',
            label: 'Pending Orders',
            tint: 'amber',
            badge: pendingCountTotal || undefined,
          },
          {
            id: 'gs-repair-items',
            label: 'Repair Items',
            tint: 'rose',
            badge: repairItemsCount || undefined,
          },
          { id: 'gs-portal-repair', label: 'Repair Portal Jewellery', tint: 'amber' },
          { id: 'gs-requirement-list', label: 'Requirement List', tint: 'violet' },
          { id: 'gs-materials', label: 'Material', tint: 'emerald' },
          { id: 'gs-labour-ledger', label: 'Labour Ledger', tint: 'indigo' },
          {
            id: 'gs-recent-issues',
            label: 'Recent Issues',
            tint: 'purple',
            badge: recentIssues.length || undefined,
          },
        ]}
        topOffset={56}
      />

      {/* 1. Pending Orders with this Goldsmith Section */}
      <section id="gs-pending-orders" className={sectionMat('amber')}>
      <SectionCollapsible
        title="Pending Orders with this Goldsmith"
        description={`${pendingCountTotal} order(s) matching ${({active:'Active', on_hold:'On Hold', completed:'Completed', all:'All', open:'Open'}[filter.status] || filter.status)} status.`}
        icon={FileText}
        testId="section-pending-orders"
        right={
          <SectionBadge
            tone="navy"
            count={pendingCountTotal}
            label={`${pendingCountTotal} order${pendingCountTotal === 1 ? '' : 's'}${refEnabled ? ` in ${ordersReferenceOptions.length} ${refLabel.replace(/\.$/, '')}${ordersReferenceOptions.length === 1 ? '' : 's'}` : ''}`}
            testId="section-pending-orders-badge"
          />
        }
      >
        <div className="p-5">
          {/* Wave 3.98 — Universal search + Stage filter + Image List PDF.
              Wave 3.99j — Sticky within the collapsible so it stays
              reachable while scrolling a long order grid. */}
          <div className="sticky-filter-bar flex flex-wrap items-end gap-2 mb-3 rounded-lg px-3" data-testid="goldsmith-orders-filter-bar">
            <div className="relative flex-1 min-w-[220px]">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                aria-hidden
              />
              <Input
                type="search"
                placeholder="Search by Order No, Title or Customer…"
                value={ordersSearch}
                onChange={(e) => setOrdersSearch(e.target.value)}
                className="h-9 pl-9"
                data-testid="goldsmith-orders-search"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Stage</Label>
              {/* Wave 3.98 — Matches the Orders page Stage filter exactly
                  (legacy stages + Production Checklist step keys). The
                  backend endpoint now applies the same logic so all
                  three views — Order Forms page, Production Timeline,
                  and Goldsmith Detail — share one vocabulary. */}
              <Select value={ordersStage} onValueChange={setOrdersStage}>
                <SelectTrigger className="w-56 h-9" data-testid="goldsmith-orders-stage-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  <SelectItem value="__legacy_header__" disabled>─ Order Stage ─</SelectItem>
                  <SelectItem value="Issued">Issued</SelectItem>
                  <SelectItem value="In Production">In Production</SelectItem>
                  <SelectItem value="Quality Check">Quality Check</SelectItem>
                  <SelectItem value="Received Back">Received Back</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                  <SelectItem value="__checklist_header__" disabled>─ Production Checklist Step ─</SelectItem>
                  <SelectItem value="cad">CAD Active</SelectItem>
                  <SelectItem value="cam">CAM Active</SelectItem>
                  <SelectItem value="chamka">Chamka Active</SelectItem>
                  <SelectItem value="ghat_pending_gold_order">Ghat - Pending Gold Order</SelectItem>
                  <SelectItem value="ghat_waiting">Ghat - Waiting For Gold</SelectItem>
                  <SelectItem value="ghat">Ghat Active</SelectItem>
                  <SelectItem value="raw_material">Raw Material Active</SelectItem>
                  <SelectItem value="setting">Setting Active</SelectItem>
                  <SelectItem value="qc">Quality Check Active</SelectItem>
                  <SelectItem value="delivery">Delivery Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {refEnabled && (
              <div>
                <Label className="text-[11px] text-slate-500">{refLabel}</Label>
                <div>
                  <ReferenceFilter
                    options={ordersReferenceOptions}
                    value={ordersReferenceNos}
                    onChange={setOrdersReferenceNos}
                    label={refLabel}
                    testIdPrefix="goldsmith-orders-reference-filter"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-[11px] text-slate-500">Customer</Label>
              <div>
                <CustomerFilter
                  options={customerOptions}
                  value={customerFilter}
                  onChange={setCustomerFilter}
                  testIdPrefix="goldsmith-orders-customer-filter"
                />
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-9 shrink-0"
              disabled={allOrders.length === 0}
              onClick={() => setImageListPdfOpen(true)}
              data-testid="goldsmith-orders-image-list-button"
            >
              <Printer size={14} className="mr-1.5" /> Image List PDF
            </Button>
          </div>
          {((refEnabled && (ordersReferenceNos || []).length > 0) || (customerFilter || []).length > 0) && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="goldsmith-orders-filter-chips">
              <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Filters applied</span>
              {refEnabled && (
                <ReferenceFilterChips
                  value={ordersReferenceNos}
                  onChange={setOrdersReferenceNos}
                  label={refLabel}
                  testIdPrefix="goldsmith-orders-reference-chip"
                />
              )}
              <CustomerFilterChips
                value={customerFilter}
                nameById={customerNameById}
                onChange={setCustomerFilter}
                testIdPrefix="goldsmith-orders-customer-chip"
              />
            </div>
          )}
          <div className="text-[11px] text-slate-400 mb-3" data-testid="goldsmith-orders-count-line">
            Showing <span className="font-mono tabular-nums text-slate-600">{visibleOrders.length}</span> of{' '}
            <span className="font-mono tabular-nums text-slate-600">{pendingCountTotal}</span> order{pendingCountTotal === 1 ? '' : 's'}. Click any card to drill in.
            {ordersLoading && (
              <span className="ml-2 inline-flex items-center gap-1 text-slate-400" data-testid="goldsmith-orders-loading-hint">
                <Loader2 size={11} className="animate-spin" /> loading all orders…
              </span>
            )}
          </div>
          {allOrders.length === 0 ? (
            ordersLoading ? (
              <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md inline-flex items-center justify-center gap-2 w-full" data-testid="goldsmith-orders-loading">
                <Loader2 size={14} className="animate-spin" /> Loading orders…
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md" data-testid="goldsmith-orders-empty">No matching orders.</div>
            )
          ) : visibleOrders.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md" data-testid="goldsmith-orders-no-results">
              No orders match the search or filter.
            </div>
          ) : (
            <div>
              {pendingTotalPages > 1 && (
                <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="goldsmith-orders-range-tabs">
                  {Array.from({ length: pendingTotalPages }, (_, i) => {
                    const from = i * PENDING_PAGE_SIZE + 1;
                    const to = Math.min((i + 1) * PENDING_PAGE_SIZE, pendingTotal);
                    const active = i === pendingCurrent;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPendingPage(i)}
                        aria-pressed={active}
                        data-testid={`goldsmith-orders-range-${from}-${to}`}
                        className={`rounded-md border px-2.5 py-1 text-[11px] font-mono tabular-nums transition-colors ${active ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white' : 'border-[var(--divider)] bg-white text-slate-600 hover:border-[var(--ink-navy)]/40'}`}
                      >
                        {from}-{to}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="goldsmith-orders-list">
                {pagedPendingOrders.map((o) => {
                  const thumb = pickOrderThumb(o);
                  return (
                    <PendingOrderCard key={o.id} order={o} thumb={thumb} pickOrderThumb={pickOrderThumb} refEnabled={refEnabled} refLabelShort={refLabelShort} />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SectionCollapsible>
      </section>

      {/* 2. Repair Items Section */}
      <section id="gs-repair-items" className={sectionMat('rose')}>
      <SectionCollapsible
        title="Repair Items"
        description="Customer + internal repair items currently with this goldsmith."
        icon={Receipt}
        testId="section-repair-items"
        right={
          <SectionBadge
            tone="amber"
            count={repairItemsCount}
            label={`${repairItemsCount} item${repairItemsCount === 1 ? '' : 's'}`}
            testId="section-repair-items-badge"
          />
        }
      >
        <div className="p-0">
          <RepairItemsSection
            goldsmith={goldsmith}
            companyId={companyId}
            onChanged={load}
          />
        </div>
      </SectionCollapsible>
      </section>

      {/* 2b. Repair Portal Jewellery Section */}
      <section id="gs-portal-repair" className={sectionMat('amber')}>
      <SectionCollapsible
        title="Repair Portal Jewellery"
        description="Send an existing received piece (by Jewellery No) back to this goldsmith for repair."
        icon={Hammer}
        testId="section-portal-repair"
      >
        <PortalRepairSection
          goldsmith={goldsmith}
          companyId={companyId}
          onChanged={load}
        />
      </SectionCollapsible>
      </section>


      {/* 3. Requirement List Section */}
      <section id="gs-requirement-list" className={sectionMat('violet')}>
      <SectionCollapsible
        title="Requirement List"
        description="Orders with this goldsmith that have requirements — filter and print the job sheet."
        icon={FileSpreadsheet}
        testId="section-requirement-list"
      >
        <div className="p-0">
          <RequirementListPanel goldsmithId={id} goldsmithName={goldsmith.name} companyId={companyId} refEnabled={refEnabled} refLabel={refLabel} refLabelShort={refLabelShort} />
        </div>
      </SectionCollapsible>
      </section>

      {/* 4. Material with Goldsmith Section */}
      <section id="gs-materials" className={sectionMat('emerald')}>
      <SectionCollapsible
        title="Material with Goldsmith"
        description="Balances of gold, diamonds, colour stones and pearls currently with this goldsmith."
        icon={Gem}
        testId="section-material-with-goldsmith"
      >
        <div className="p-0">
          <MaterialsWithGoldsmithSection
            balances={balances}
            goldsmith={goldsmith}
            onAttribute={openAttribute}
            onAttributePool={openAttributePool}
            onReturn={openReturn}
            ledgerView={ledgerView}
          />
        </div>
      </SectionCollapsible>
      </section>

      {/* 5. Labour Ledger Section */}
      <section id="gs-labour-ledger" className={sectionMat('indigo')}>
      <SectionCollapsible
        title="Labour Ledger"
        description="Auto-credits from received jewellery and manual payments to this goldsmith."
        icon={Coins}
        testId="section-labour-ledger"
        right={
          unpaidLabourInr > 0 ? (
            <SectionBadge
              tone="rose"
              count={1}
              label={`${formatINR(unpaidLabourInr)} unpaid`}
              testId="section-labour-ledger-badge"
            />
          ) : null
        }
      >
        <div className="p-0">
          <LabourLedgerSection goldsmith={goldsmith} ledgerView={ledgerView} />
        </div>
      </SectionCollapsible>
      </section>

      {/* 6. Recent Issues Section */}
      <section id="gs-recent-issues" className={sectionMat('purple')}>
      <SectionCollapsible
        title="Recent Issues"
        description="Memos sent to this goldsmith from any source (Goldsmith page, Order page, or Packet/Gold issue)."
        icon={Send}
        testId="section-recent-issues"
      >
        <div className="p-5">
          {recentIssues.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md">No issues yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2">Memo No</th>
                    <th className="text-left px-3 py-2">Date (IST)</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-left px-3 py-2">Lines</th>
                    {/* Wave 3.41 — Total Value column hidden when !canViewValues */}
                    {canViewValues && <th className="text-right px-3 py-2">Total Value</th>}
                  </tr>
                </thead>
                <tbody data-testid="goldsmith-issues-table">
                  {recentIssues.map((gi) => (
                    <tr key={gi.id} className="border-t border-[var(--divider)] hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <MemoLink id={gi.id} memoNo={gi.issue_memo_no || gi.issue_no} testId={`goldsmith-issue-memo-${gi.id}`} />
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(gi.created_at)}</td>
                      <td className="px-3 py-2 text-xs">
                        <Badge variant="secondary" className="text-[10px] capitalize">{(gi.source || 'goldsmith_page').replace('_', ' ')}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {(gi.lines || []).map((l, i) => {
                          const mt = l.material_type;
                          const isCarat = mt === 'diamond' || mt === 'color_stone' || mt === 'pearl';
                          const Icon = mt === 'color_stone' ? Sparkles : mt === 'pearl' ? Circle : mt === 'gold' ? Coins : Gem;
                          const iconColor = mt === 'color_stone' ? 'text-[var(--colorstone-icon,#C84A6F)]'
                                          : mt === 'pearl' ? 'text-[var(--pearl-icon,#6B7280)]'
                                          : mt === 'gold' ? 'text-[var(--gold-icon)]'
                                          : 'text-[var(--diamond-icon)]';
                          return (
                            <span key={i} className="inline-flex items-center mr-2">
                              <Icon size={10} className={`mr-0.5 ${iconColor}`} />
                              {isCarat ? `${formatCarats(l.carats)}ct/${l.pieces}pcs` : `${formatGrams(l.grams)}g ${l.purity_name}`}
                              {l.order_no && <Link target="_blank" rel="noopener noreferrer" to={`/orders/${l.order_id}`} className="ml-0.5 text-[10px] text-[var(--ink-navy)] hover:underline">{l.order_no}</Link>}
                              {i < gi.lines.length - 1 && <span className="mx-1 text-slate-300">|</span>}
                            </span>
                          );
                        })}
                      </td>
                      {canViewValues && <td className="px-3 py-2 text-right font-mono">{formatINR(gi.total_value)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SectionCollapsible>
      </section>

      {issueOpen && (
        <GoldsmithIssueDialog
          open={issueOpen}
          onOpenChange={setIssueOpen}
          companyId={companyId}
          goldsmith={goldsmith}
          onSuccess={() => { setIssueOpen(false); load(); }}
        />
      )}

      {attrCtx && (
        <AttributeDialog
          open={!!attrCtx}
          onOpenChange={(v) => { if (!v) setAttrCtx(null); }}
          companyId={companyId}
          goldsmithId={id}
          issueId={attrCtx.issueId}
          lineId={attrCtx.lineId}
          lineMeta={attrCtx.lineMeta}
          issueMemoNo={attrCtx.issueMemoNo}
          pool={attrCtx.pool}
          onSuccess={() => { setAttrCtx(null); load(); }}
        />
      )}

      {returnOpen && (
        <ReturnDialog
          open={returnOpen}
          onOpenChange={(v) => { setReturnOpen(v); if (!v) setReturnCtx(null); }}
          companyId={companyId}
          goldsmithId={id}
          goldsmithName={goldsmith.name}
          filter={returnCtx?.filter || null}
          onSuccess={() => { setReturnOpen(false); setReturnCtx(null); load(); }}
        />
      )}
      {/* Wave 3.37 — Image List PDF download dialog (with branding toggle). */}
      <PdfDownloadDialog
        open={imageListPdfOpen}
        onOpenChange={setImageListPdfOpen}
        title={`Order Forms — Image List`}
        description={
          `A4 portrait, 4-column grid. Applied filters: ` +
          `${({active:'Active', on_hold:'On Hold', completed:'Completed', all:'All', open:'Open'}[filter.status] || filter.status)}` +
          `${ordersStage && ordersStage !== 'all' ? ` · Stage: ${ordersStage}` : ''}` +
          `${ordersSearch ? ` · Search: "${ordersSearch}"` : ''}` +
          ` (${visibleOrders.length} of ${allOrders.length} orders)`
        }
        companyId={companyId}
        endpoint={`/orders/by-goldsmith/${id}/orders-image-list/pdf`}
        filename={(() => {
          const slug = (s) => (s || '').toString().trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'all';
          const parts = [
            'Orders_Image_List',
            (goldsmith?.name || 'goldsmith').replace(/\s+/g, '_').replace(/\//g, '-'),
            filter.status || 'all',
          ];
          if (ordersStage && ordersStage !== 'all') parts.push(slug(ordersStage));
          if (ordersSearch) parts.push('q_' + slug(ordersSearch).slice(0, 24));
          return parts.join('_') + '.pdf';
        })()}
        // Wave 3.98 — Send both the server-side ``status`` filter and
        // the local ``stage`` / ``search`` filters so the PDF exactly
        // mirrors what the operator sees in the on-screen grid.
        extraParams={{
          ...(filter.status === 'all' ? {} : { status: filter.status }),
          ...(ordersStage && ordersStage !== 'all' ? { stage: ordersStage } : {}),
          ...(ordersSearch ? { search: ordersSearch } : {}),
        }}
        mode="download"
      />
    </div>
  );
}

// ============================================================================
// Phase 7 — Materials With Goldsmith section
// ============================================================================
// ============================================================================
// Phase 9 hotfix Round 3 — Requirement List panel
//
// Lists every order for this goldsmith that has at least one requirement,
// with a status filter (All / Pending / Completed). User can download a
// landscape PDF job-sheet showing the jewellery reference image and the
// pending/completed requirement breakdown grouped by Gold / Diamonds /
// Colour Stones / Pearls. Each order is a bordered card with a page break
// in the PDF so the goldsmith can be handed individual order sheets.
// ============================================================================
const REQ_GROUPS = [
  { key: 'gold', label: 'Gold', icon: Coins, badgeCls: 'bg-[var(--gold-bg)] text-[var(--gold-icon)]' },
  { key: 'diamond', label: 'Diamonds', icon: Gem, badgeCls: 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)]' },
  { key: 'color_stone', label: 'Colour Stones', icon: Sparkles, badgeCls: 'bg-pink-50 text-pink-700' },
  { key: 'pearl', label: 'Pearls', icon: Sparkles, badgeCls: 'bg-slate-100 text-slate-600' },
];

// Wave 3.98 — Legacy stages + Production Checklist step keys used by the
// Requirement List Stage filter. Kept in sync with the Order Forms page
// dropdown so operators see the same vocabulary everywhere.
const REQ_STAGE_OPTIONS = [
  { group: 'Order Stage', items: [
    { value: 'Issued', label: 'Issued' },
    { value: 'In Production', label: 'In Production' },
    { value: 'Quality Check', label: 'Quality Check' },
    { value: 'Received Back', label: 'Received Back' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ]},
  { group: 'Production Checklist Step', items: [
    { value: 'cad', label: 'CAD Active' },
    { value: 'cam', label: 'CAM Active' },
    { value: 'chamka', label: 'Chamka Active' },
    { value: 'ghat_pending_gold_order', label: 'Ghat - Pending Gold Order' },
    { value: 'ghat_waiting', label: 'Ghat - Waiting For Gold' },
    { value: 'ghat', label: 'Ghat Active' },
    { value: 'raw_material', label: 'Raw Material Active' },
    { value: 'setting', label: 'Setting Active' },
    { value: 'qc', label: 'Quality Check Active' },
    { value: 'delivery', label: 'Delivery Active' },
  ]},
];
const REQ_STAGE_LABEL = (() => {
  const m = { all: 'All stages' };
  for (const g of REQ_STAGE_OPTIONS) for (const it of g.items) m[it.value] = it.label;
  return m;
})();

// Wave 3.98 — Reusable multi-select popover for the Requirement List
// section. Renders a checkbox list of ``options`` with a compact search
// input; the trigger shows how many are selected. Selection is
// controlled via ``selectedIds`` (Set) + ``onChange``.
//
// Wave 3.99 — Cascade-aware counts. ``options`` may be a NARROWED
// (cascaded) subset of the true selection universe. So all badges,
// "select all" and toggles operate on the VISIBLE options only —
// ``selectedIds`` may contain IDs outside ``options`` (residual
// selection preserved across filter changes) and those are ignored
// for display purposes.
function MultiSelectPopover({ label, testId, options, selectedIds, onChange, placeholder }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const filtered = React.useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || '').toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q));
  }, [options, query]);
  // Count of VISIBLE (cascaded) options currently selected — never
  // exceeds ``options.length``.
  const visibleSelectedCount = React.useMemo(
    () => options.reduce((n, o) => n + (selectedIds.has(o.id) ? 1 : 0), 0),
    [options, selectedIds],
  );
  const allSelected = options.length > 0 && visibleSelectedCount === options.length;
  const toggleAll = () => {
    if (allSelected) {
      // Remove ONLY the visible options from the selection so any
      // residual (out-of-cascade) selection is preserved.
      const s = new Set(selectedIds);
      for (const o of options) s.delete(o.id);
      onChange(s);
    } else {
      // Add every visible option (union with residual selection).
      const s = new Set(selectedIds);
      for (const o of options) s.add(o.id);
      onChange(s);
    }
  };
  const toggle = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    onChange(s);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 justify-between min-w-[180px] max-w-[240px]"
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate text-left">
            {allSelected
              ? `${label}: All (${options.length})`
              : visibleSelectedCount === 0
                ? `${label}: None`
                : `${label}: ${visibleSelectedCount} of ${options.length}`}
          </span>
          <ChevronDown size={12} className="ml-2 opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid={`${testId}-content`}>
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder || 'Search…'}
              className="h-8 pl-8 text-[12px]"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px]">
            <button
              type="button"
              onClick={toggleAll}
              className="text-[var(--ink-navy)] hover:underline"
              data-testid={`${testId}-toggle-all`}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
            <span className="text-slate-400">
              {visibleSelectedCount} of {options.length} selected
            </span>
          </div>
        </div>
        <div className="max-h-64 overflow-auto py-1" data-testid={`${testId}-options`}>
          {filtered.length === 0 && (
            <div className="text-[11px] text-slate-400 text-center py-4">No matches.</div>
          )}
          {filtered.map((opt) => (
            <label
              key={opt.id}
              className="flex items-start gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer"
              data-testid={`${testId}-option-${opt.id}`}
            >
              <Checkbox
                checked={selectedIds.has(opt.id)}
                onCheckedChange={() => toggle(opt.id)}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] text-slate-800 truncate">{opt.label}</div>
                {opt.sub && <div className="text-[10px] text-slate-500 truncate">{opt.sub}</div>}
              </div>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Wave 3.98 — Extract the sorted list of customer options from a
// requirement-list payload. Orders lacking a customer_id snapshot are
// bucketed under a synthetic ``__none__`` entry so operators can still
// filter to "unassigned" orders.
function collectCustomerOptions(data) {
  const out = new Map();
  let hasNone = false;
  for (const o of (data?.orders || [])) {
    const cid = o.customer_id;
    if (cid) {
      out.set(cid, {
        id: cid,
        label: o.customer_name || 'Unnamed customer',
        sub: '',
      });
    } else {
      hasNone = true;
    }
  }
  const list = Array.from(out.values()).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  if (hasNone) list.push({ id: '__none__', label: '(No customer)', sub: '' });
  return list;
}

function RequirementListPanel({ goldsmithId, goldsmithName, companyId, refEnabled = false, refLabel = 'Page No.', refLabelShort = 'Page' }) {
  const [status, setStatus] = useState('pending');
  // Wave 3.81 — Grouping (by_order | by_material) & view mode
  // (expanded | collapsed) toggles.
  const [grouping, setGrouping] = useState('by_order');
  const [viewMode, setViewMode] = useState('expanded');
  // Requirement List sort toggle — order by Order No. Newest first by
  // default; remembered across sessions. Drives the on-screen list AND
  // the PDF / Print / Excel exports (all read ``sortDir``).
  const [sortDir, setSortDir] = useState(() => {
    try { return localStorage.getItem('reqListSort') === 'oldest' ? 'oldest' : 'newest'; } catch { return 'newest'; }
  });
  useEffect(() => {
    try { localStorage.setItem('reqListSort', sortDir); } catch { /* noop */ }
  }, [sortDir]);
  // "New page per Page No" PDF export option (mirrors the Dashboard).
  // Remembered across sessions; only meaningful when the company uses
  // reference/page numbers and the export spans more than one Page No.
  const [pageBreakPref, setPageBreakPref] = useState(() => {
    try { return localStorage.getItem('reqListPageBreak') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('reqListPageBreak', pageBreakPref ? '1' : '0'); } catch { /* noop */ }
  }, [pageBreakPref]);
  // Wave 3.82 — Column visibility (Remarks / Comments / Age).
  const [columns, setColumns] = useState(() => {
    try {
      const raw = localStorage.getItem('reqListColumns');
      if (raw) {
        const p = JSON.parse(raw);
        return {
          remarks: p.remarks !== false,
          comments: p.comments !== false,
          age: p.age !== false,
        };
      }
    } catch { /* noop */ }
    return { remarks: true, comments: true, age: true };
  });
  useEffect(() => {
    try { localStorage.setItem('reqListColumns', JSON.stringify(columns)); } catch { /* noop */ }
  }, [columns]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfDialog, setPdfDialog] = useState({ open: false, mode: 'download' });
  // Requirement Cards PDF (image-card grid grouped Customer -> Page -> Order).
  const [cardsPdfOpen, setCardsPdfOpen] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);

  // Wave 3.98 — Section filters (universal search, stage, customer multi,
  // order multi). The customer/order lists are populated from the
  // "master" fetch (no filters applied) so operators can always see
  // every choice; the actual data render uses a second fetch with the
  // filters applied. This keeps checkbox lists stable while the user
  // dials filters.
  const [reqSearch, setReqSearch] = useState('');
  const [reqSearchDebounced, setReqSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setReqSearchDebounced(reqSearch), 300);
    return () => clearTimeout(t);
  }, [reqSearch]);
  const [reqStage, setReqStage] = useState('all');
  const [reqCustomerIds, setReqCustomerIds] = useState(new Set());
  const [reqOrderIds, setReqOrderIds] = useState(new Set());
  // Wave 3.99e — Raw Material multi-select filter. Same "None means
  // no narrowing" convention as reqCustomerIds — an empty Set + a
  // Set that matches every visible material both mean "All". Bootstrap
  // the state to the full master universe on first fetch so the
  // trigger renders "All (N)" instead of "None".
  const ALL_MATERIALS = React.useMemo(
    () => [
      { id: 'gold',        label: 'Gold' },
      { id: 'diamond',     label: 'Diamonds' },
      { id: 'color_stone', label: 'Color Stones' },
      { id: 'pearl',       label: 'Pearls' },
    ],
    [],
  );
  const [reqMaterialIds, setReqMaterialIds] = useState(new Set());
  // Reference-Number system — multi-select reference filter (array of ints).
  const [reqReferenceNos, setReqReferenceNos] = useState([]);
  // "Print header" — which active filters the operator wants stamped at the
  // top of exports. We track the EXCLUDED keys so any newly-added filter is
  // included (ticked) by default; unticks persist as filters change.
  const [excludedHeaderKeys, setExcludedHeaderKeys] = useState(() => new Set());
  // Master option list — always the un-filtered universe of customers
  // and orders for the current ``status`` filter. Populated on first
  // load & whenever ``status`` changes.
  const [masterData, setMasterData] = useState(null);

  // Fetch the un-filtered "master" list once per status change — used
  // exclusively to populate the customer + order multi-select options.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/orders/by-goldsmith/${goldsmithId}/requirement-list`, {
          params: { status },
        });
        if (!cancelled) {
          setMasterData(d);
          // Wave 3.98 — By default every order is selected (as per user spec).
          setReqOrderIds(new Set((d?.orders || []).map((o) => o.id)));
          // Wave 3.99e — By default every material bucket that appears
          // in the master universe is selected (same "All" semantics
          // as orders). Missing materials stay out so the dropdown's
          // "All (N)" count reflects what actually exists here.
          const presentMaterials = new Set();
          for (const o of (d?.orders || [])) {
            for (const k of ['gold', 'diamond', 'color_stone', 'pearl']) {
              if ((o?.groups?.[k] || []).length > 0) presentMaterials.add(k);
            }
          }
          setReqMaterialIds(presentMaterials);
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldsmithId, status]);

  // Wave 3.99 — Cascade filtering across Customer / Order / Stage
  // dropdowns. Each dropdown's options are derived from the master
  // list, filtered by the OTHER active filters (excluding its own).
  // This prevents zero-result states where the user has picked
  // combinations that intersect to nothing.
  const LEGACY_STAGES = React.useMemo(
    () => new Set(['Issued', 'In Production', 'Quality Check', 'Received Back', 'Completed', 'Cancelled']),
    [],
  );
  // Predicate: does an order pass the currently active filters?
  // ``exclude`` names which filter to skip (used when computing that
  // dropdown's cascaded options).
  const orderMatchesFilters = React.useCallback((o, exclude = {}) => {
    // Search
    if (!exclude.search) {
      const s = (reqSearchDebounced || '').trim().toLowerCase();
      if (s) {
        const hay = `${o.order_no || ''} ${o.title || ''} ${o.customer_name || ''}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
    }
    // Stage
    if (!exclude.stage) {
      if (reqStage && reqStage !== 'all') {
        if (LEGACY_STAGES.has(reqStage)) {
          if ((o.stage || '') !== reqStage) return false;
        } else {
          if (!activeStepsMatch(o.active_steps, reqStage)) return false;
        }
      }
    }
    // Customer
    if (!exclude.customer) {
      const allMasterCustomers = collectCustomerOptions(masterData || { orders: [] }).length;
      const isNarrowed = reqCustomerIds.size > 0 && reqCustomerIds.size < allMasterCustomers;
      if (isNarrowed) {
        const cid = o.customer_id || '__none__';
        if (!reqCustomerIds.has(cid)) return false;
      }
    }
    // Order
    if (!exclude.order) {
      const allMasterOrders = (masterData?.orders || []).length;
      const orderNarrowed = reqOrderIds.size > 0 && reqOrderIds.size < allMasterOrders;
      const orderEmpty = reqOrderIds.size === 0 && allMasterOrders > 0;
      if (orderNarrowed) {
        if (!reqOrderIds.has(o.id)) return false;
      } else if (orderEmpty) {
        return false;
      }
    }
    // Wave 3.99e — Raw Material
    if (!exclude.material) {
      // Compute the "all present materials" size against the master
      // universe so cascade narrowing does not falsely mark the filter
      // as narrowed.
      const presentInMaster = new Set();
      for (const mo of (masterData?.orders || [])) {
        for (const k of ['gold', 'diamond', 'color_stone', 'pearl']) {
          if ((mo?.groups?.[k] || []).length > 0) presentInMaster.add(k);
        }
      }
      const isMatNarrowed = reqMaterialIds.size > 0 && reqMaterialIds.size < presentInMaster.size;
      const isMatEmpty    = reqMaterialIds.size === 0 && presentInMaster.size > 0;
      if (isMatEmpty) return false;
      if (isMatNarrowed) {
        const hasMatch = ['gold', 'diamond', 'color_stone', 'pearl'].some(
          (k) => reqMaterialIds.has(k) && (o?.groups?.[k] || []).length > 0,
        );
        if (!hasMatch) return false;
      }
    }
    return true;
  }, [reqSearchDebounced, reqStage, reqCustomerIds, reqOrderIds, reqMaterialIds, masterData, LEGACY_STAGES]);

  // Cascaded option lists — each excludes its own filter so that
  // narrowing one filter does not empty its own dropdown.
  const cascadedCustomerOptions = React.useMemo(() => {
    if (!masterData) return [];
    const orders = (masterData.orders || []).filter((o) => orderMatchesFilters(o, { customer: true }));
    return collectCustomerOptions({ orders });
  }, [masterData, orderMatchesFilters]);

  const cascadedOrderOptions = React.useMemo(() => {
    if (!masterData) return [];
    return (masterData.orders || [])
      .filter((o) => orderMatchesFilters(o, { order: true }))
      .map((o) => ({
        id: o.id,
        label: `${o.order_no}${o.title ? ` — ${o.title}` : ''}`,
        sub: o.customer_name || 'No customer',
      }));
  }, [masterData, orderMatchesFilters]);

  // Set of stage values (legacy + step keys) that would produce at
  // least one visible order given the OTHER active filters.
  const cascadedStageValues = React.useMemo(() => {
    const set = new Set();
    if (!masterData) return set;
    const orders = (masterData.orders || []).filter((o) => orderMatchesFilters(o, { stage: true }));
    for (const o of orders) {
      if (o.stage) set.add(o.stage);
      for (const k of activeStepKeys(o.active_steps)) set.add(k);
    }
    return set;
  }, [masterData, orderMatchesFilters]);

  // Wave 3.99e — Materials present in the master universe (used as
  // the "all" size) AND materials that would still be viable given the
  // OTHER active filters (cascade options for the dropdown). Only
  // materials with at least one non-empty group survive the cascade.
  const presentMaterialsInMaster = React.useMemo(() => {
    const set = new Set();
    if (!masterData) return set;
    for (const o of (masterData.orders || [])) {
      for (const k of ['gold', 'diamond', 'color_stone', 'pearl']) {
        if ((o?.groups?.[k] || []).length > 0) set.add(k);
      }
    }
    return set;
  }, [masterData]);
  const cascadedMaterialOptions = React.useMemo(() => {
    if (!masterData) return [];
    const surviving = new Set();
    const orders = (masterData.orders || []).filter((o) => orderMatchesFilters(o, { material: true }));
    for (const o of orders) {
      for (const k of ['gold', 'diamond', 'color_stone', 'pearl']) {
        if ((o?.groups?.[k] || []).length > 0) surviving.add(k);
      }
    }
    return ALL_MATERIALS.filter(
      (m) => surviving.has(m.id) || reqMaterialIds.has(m.id),
    );
  }, [masterData, orderMatchesFilters, ALL_MATERIALS, reqMaterialIds]);

  // Wave 3.99 — Server params derived from the current filter state.
  // For customer_ids / order_ids we send the INTERSECTION of the
  // user's selection with the cascaded (currently-visible) options.
  // This keeps the wire minimal and matches what the user sees.
  const serverParams = React.useMemo(() => {
    const p = { status };
    p.sort = sortDir;
    if (reqStage && reqStage !== 'all') p.stage = reqStage;
    if (reqSearchDebounced && reqSearchDebounced.trim()) p.search = reqSearchDebounced.trim();
    if (masterData) {
      const totalMasterCustomers = collectCustomerOptions(masterData).length;
      if (reqCustomerIds.size > 0 && reqCustomerIds.size < totalMasterCustomers) {
        p.customer_ids = Array.from(reqCustomerIds).join(',');
      }
      const totalMasterOrders = (masterData.orders || []).length;
      if (reqOrderIds.size > 0 && reqOrderIds.size < totalMasterOrders) {
        p.order_ids = Array.from(reqOrderIds).join(',');
      } else if (reqOrderIds.size === 0 && totalMasterOrders > 0) {
        p.order_ids = '__none__';
      }
      // Wave 3.99e — Material filter. Only send when narrowed so the
      // wire stays clean when the operator has not touched it. An
      // explicitly-empty selection sends a sentinel so the server
      // returns zero orders (mirrors the on-screen empty state).
      if (
        reqMaterialIds.size > 0
        && reqMaterialIds.size < presentMaterialsInMaster.size
      ) {
        p.material_types = Array.from(reqMaterialIds).join(',');
      } else if (reqMaterialIds.size === 0 && presentMaterialsInMaster.size > 0) {
        p.material_types = '__none__';
      }
    }
    return p;
  }, [status, sortDir, reqStage, reqSearchDebounced, reqCustomerIds, reqOrderIds, reqMaterialIds, masterData, presentMaterialsInMaster]);

  // ---- "Print header" — active filter segments for the export header ------
  // Mirrors the on-screen filter chips (Status, Stage, Search, Customers,
  // Order Forms, Raw Material, Page No). Operators tick which of these get
  // stamped on the exported PDF / Cards PDF / Excel.
  const headerFilterSegments = React.useMemo(() => {
    const segs = [];
    const statusLabelMap = { pending: 'Pending', completed: 'Completed', all: 'All' };
    segs.push({ key: 'status', label: 'Status', value: statusLabelMap[status] || status });
    if (reqStage && reqStage !== 'all') {
      segs.push({ key: 'stage', label: 'Stage', value: REQ_STAGE_LABEL[reqStage] || reqStage });
    }
    if (reqSearchDebounced && reqSearchDebounced.trim()) {
      segs.push({ key: 'search', label: 'Search', value: reqSearchDebounced.trim() });
    }
    if (masterData) {
      const masterCustomerList = collectCustomerOptions(masterData);
      const totalMasterCustomers = masterCustomerList.length;
      if (reqCustomerIds.size > 0 && reqCustomerIds.size < totalMasterCustomers) {
        const first = masterCustomerList.find((o) => reqCustomerIds.has(o.id));
        const value = reqCustomerIds.size === 1
          ? (first?.label || '—')
          : `${reqCustomerIds.size} selected`;
        segs.push({ key: 'customers', label: 'Customers', value });
      }
      const totalMasterOrders = (masterData.orders || []).length;
      if (totalMasterOrders > 0 && reqOrderIds.size < totalMasterOrders) {
        const value = reqOrderIds.size === 0
          ? 'None selected'
          : `${reqOrderIds.size} of ${totalMasterOrders} selected`;
        segs.push({ key: 'orders', label: 'Order Forms', value });
      }
      const totalMasterMaterials = presentMaterialsInMaster.size;
      if (totalMasterMaterials > 0 && reqMaterialIds.size < totalMasterMaterials) {
        const matLabel = (id) => (ALL_MATERIALS.find((m) => m.id === id) || {}).label || id;
        const value = reqMaterialIds.size === 0
          ? 'None selected'
          : Array.from(reqMaterialIds).map(matLabel).join(', ');
        segs.push({ key: 'materials', label: 'Raw Material', value });
      }
    }
    if (refEnabled && (reqReferenceNos || []).length > 0) {
      segs.push({ key: 'pageNo', label: refLabel || 'Page No.', value: reqReferenceNos.join(', ') });
    }
    return segs;
  }, [status, reqStage, reqSearchDebounced, reqCustomerIds, reqOrderIds, reqMaterialIds, masterData, presentMaterialsInMaster, refEnabled, reqReferenceNos, refLabel, ALL_MATERIALS]);

  const includedHeaderSegments = React.useMemo(
    () => headerFilterSegments.filter((s) => !excludedHeaderKeys.has(s.key)),
    [headerFilterSegments, excludedHeaderKeys],
  );
  // Always a JSON string (even "[]") so the server renders exactly what the
  // operator chose rather than its own auto summary.
  const headerFiltersParam = React.useMemo(
    () => JSON.stringify(includedHeaderSegments.map((s) => [s.label, s.value])),
    [includedHeaderSegments],
  );

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/orders/by-goldsmith/${goldsmithId}/requirement-list`, {
        params: serverParams,
      });
      // Reference-Number system — client-side narrowing on the fetched
      // orders (the endpoint doesn't filter by reference). Recompute the
      // order/pending counts so the header + Excel gating stay in sync.
      let out = d;
      if (refEnabled && (reqReferenceNos || []).length > 0 && d && Array.isArray(d.orders)) {
        const refSet = new Set(reqReferenceNos);
        const orders = d.orders.filter((o) => refSet.has(o.reference_no));
        out = {
          ...d,
          orders,
          order_count: orders.length,
          total_pending: orders.reduce((s, o) => s + (o.pending_count || 0), 0),
        };
      }
      setData(out);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load requirement list'));
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (goldsmithId) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldsmithId, serverParams, reqReferenceNos, refEnabled]);

  // Reference-Number system — distinct references present in the master
  // universe (for this section's reference filter options).
  const reqReferenceOptions = React.useMemo(() => {
    if (!refEnabled || !masterData) return [];
    const set = new Set();
    for (const o of (masterData.orders || [])) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return Array.from(set);
  }, [refEnabled, masterData]);

  // Distinct running-numbers among the currently listed requirement orders
  // (respects the active filters) — powers the header "in N pages" suffix.
  const reqVisibleReferenceCount = React.useMemo(() => {
    if (!refEnabled || !data?.orders) return 0;
    const set = new Set();
    for (const o of data.orders) {
      if (o.reference_no != null) set.add(o.reference_no);
    }
    return set.size;
  }, [refEnabled, data]);

  // Alias — legacy names kept so downstream JSX doesn't need to change.
  const customerOptions = cascadedCustomerOptions;
  const orderOptions = cascadedOrderOptions;

  // Wave 3.98 — Group-by-customer flag: only when the user has picked
  // 2+ customers via the multi-select. Otherwise the standard order
  // list renders untouched.
  const groupByCustomer = reqCustomerIds.size >= 2;

  const safeGsName = (goldsmithName || 'goldsmith').replace(/\s+/g, '_').replace(/\//g, '-');

  const handleExcelDownload = async () => {
    if (!data || (data.order_count || 0) === 0) return;
    setExcelBusy(true);
    try {
      const resp = await api.get(`/orders/by-goldsmith/${goldsmithId}/requirement-list/excel`, {
        params: {
          ...serverParams,
          grouping,
          view_mode: viewMode,
          // Bug fix — the on-screen Page No filter is applied client-side, so
          // send it explicitly or the export would include every page.
          reference_nos: (refEnabled && (reqReferenceNos || []).length > 0)
            ? reqReferenceNos.join(',')
            : undefined,
          // Operator-curated header segments (Print-header popover).
          header_filters: headerFiltersParam,
        },
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `RequirementList_${safeGsName}_${status}_${grouping}_${viewMode}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success('Excel downloaded');
    } catch (e) {
      toast.error(extractError(e, 'Failed to export Excel'));
    } finally { setExcelBusy(false); }
  };

  // Wave 3.98 — Filter chip helpers. Every non-default filter renders
  // as a removable chip so the user can drop one filter at a time.
  const clearAllFilters = () => {
    setReqSearch('');
    setReqStage('all');
    setReqCustomerIds(new Set());
    if (masterData) setReqOrderIds(new Set((masterData.orders || []).map((o) => o.id)));
    else setReqOrderIds(new Set());
    // Wave 3.99e — Reset materials to the full master universe so the
    // trigger snaps back to "All (N)" instead of "None".
    setReqMaterialIds(new Set(presentMaterialsInMaster));
    setReqReferenceNos([]);
  };

  return (
    <Card className="p-5" data-testid="requirement-list-panel">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="text-[18px] font-semibold tracking-[-0.01em] text-[var(--ink-navy)]">Requirement List</div>
            <span
              className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-white text-slate-600 border border-[var(--divider)] tabular-nums"
              data-testid="requirement-list-order-count"
            >
              {`${data?.order_count ?? 0} ${(data?.order_count ?? 0) === 1 ? 'order' : 'orders'}${refEnabled ? ` in ${reqVisibleReferenceCount} ${refLabel.replace(/\.$/, '')}${reqVisibleReferenceCount === 1 ? '' : 's'}` : ''}`}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Orders with this goldsmith that have requirements. Filter, then print the job sheet to hand over.
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Show</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40 h-9" data-testid="requirement-list-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Wave 3.82 — Column visibility popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9" data-testid="requirement-list-columns-trigger">
                <Columns size={14} className="mr-1.5" />
                Columns
                <ChevronDown size={12} className="ml-1.5 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-2">Show columns</div>
              {[
                { key: 'remarks',  label: 'Remarks' },
                { key: 'comments', label: 'Comments' },
                { key: 'age',      label: 'Age' },
              ].map((opt) => (
                <label key={opt.key} className="flex items-center gap-2 py-1.5 cursor-pointer text-sm">
                  <Checkbox
                    checked={!!columns[opt.key]}
                    onCheckedChange={(v) => setColumns((prev) => ({ ...prev, [opt.key]: !!v }))}
                    data-testid={`requirement-list-columns-${opt.key}`}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
              <div className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
                Excel / PDF exports always include every column.
              </div>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-9"
                disabled={!data || (data.order_count || 0) === 0}
                data-testid="requirement-list-print-header-trigger"
                title="Choose which filters print at the top of exports"
              >
                <PanelTop size={14} className="mr-1.5" /> Print header
                <ChevronDown size={12} className="ml-1.5 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end" data-testid="requirement-list-print-header-popover">
              <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">Print in export header</div>
              <div className="text-[11px] text-slate-400 mb-2 leading-snug">
                Tick the active filters to stamp at the top of the PDF, Cards PDF &amp; Excel.
              </div>
              {headerFilterSegments.length === 0 ? (
                <div className="text-xs text-slate-500 py-2" data-testid="requirement-list-print-header-empty">
                  No active filters to print.
                </div>
              ) : (
                <div className="space-y-0.5">
                  {headerFilterSegments.map((s) => (
                    <label
                      key={s.key}
                      className="flex items-start gap-2 py-1.5 cursor-pointer text-sm hover:bg-slate-50 rounded px-1"
                      data-testid={`requirement-list-print-header-${s.key}-label`}
                    >
                      <Checkbox
                        checked={!excludedHeaderKeys.has(s.key)}
                        onCheckedChange={(v) => setExcludedHeaderKeys((prev) => {
                          const next = new Set(prev);
                          if (v) next.delete(s.key); else next.add(s.key);
                          return next;
                        })}
                        data-testid={`requirement-list-print-header-${s.key}`}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="font-medium text-[var(--ink-navy)]">{s.label}</span>
                        <span className="text-slate-500">: {s.value}</span>
                      </span>
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={() => setExcludedHeaderKeys(new Set())}
                    className="text-[11px] text-slate-500 hover:text-[var(--ink-navy)] hover:underline mt-1 ml-1"
                    data-testid="requirement-list-print-header-reset"
                  >
                    Show all
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            className="h-9"
            disabled={!data || (data.order_count || 0) === 0}
            onClick={() => setPdfDialog({ open: true, mode: 'view' })}
            data-testid="requirement-list-view-pdf"
          >
            <FileText size={14} className="mr-1.5" /> View PDF
          </Button>
          <Button
            variant="outline"
            className="h-9"
            disabled={!data || (data.order_count || 0) === 0}
            onClick={() => setCardsPdfOpen(true)}
            data-testid="requirement-list-cards-pdf"
          >
            <LayoutGrid size={14} className="mr-1.5" /> Cards PDF
          </Button>
          <Button
            variant="outline"
            className="h-9"
            disabled={!data || (data.order_count || 0) === 0}
            onClick={() => setPdfDialog({ open: true, mode: 'print' })}
            data-testid="requirement-list-print"
          >
            <Printer size={14} className="mr-1.5" /> Print
          </Button>
          <Button
            variant="outline"
            className="h-9"
            disabled={!data || (data.order_count || 0) === 0 || excelBusy}
            onClick={handleExcelDownload}
            data-testid="requirement-list-download-excel"
          >
            <FileSpreadsheet size={14} className="mr-1.5" /> {excelBusy ? 'Exporting…' : 'Excel'}
          </Button>
          <Button
            className="h-9"
            disabled={!data || (data.order_count || 0) === 0}
            onClick={() => setPdfDialog({ open: true, mode: 'download' })}
            data-testid="requirement-list-download"
          >
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* Wave 4.0 — Filter toolbar redesign (mirrors the Orders page).
          A single grouped, bordered surface: a dominant Search followed by
          the workflow filters (Stage, Customers, Orders, Raw Material,
          Reference No.). The per-control stacked labels were removed — each
          control is self-describing (Select value / "Customers: None" etc.).
          Stays sticky within the collapsible section as long lists scroll. */}
      <div
        className="sticky-filter-bar mb-2 rounded-lg border border-[var(--divider)] bg-white/90 px-2.5 py-2 shadow-sm"
        data-testid="requirement-list-filter-bar"
      >
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          {/* Search — dominant control. */}
          <div className="relative w-full lg:w-64 lg:flex-shrink-0">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              aria-hidden
            />
            <Input
              type="search"
              value={reqSearch}
              onChange={(e) => setReqSearch(e.target.value)}
              placeholder="Search Order No, Title or Customer…"
              className="w-full h-9 pl-9"
              data-testid="requirement-list-search"
            />
          </div>

          {/* Divider: Search | Filters (desktop only). */}
          <div className="hidden lg:block h-7 w-px bg-[var(--divider)]" aria-hidden="true" />

          {/* Filter controls. */}
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <div className="w-full sm:w-[210px]">
              <Select value={reqStage} onValueChange={setReqStage}>
                <SelectTrigger className="w-full h-9" data-testid="requirement-list-stage-filter">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stages</SelectItem>
                  {/* Wave 3.99 — Cascade: only render stage rows whose value
                      appears on at least one currently-visible order. The
                      currently-selected value is always kept visible so the
                      user can un-select it. Group headers hide when their
                      entire group is empty. */}
                  {REQ_STAGE_OPTIONS.map((grp, gi) => {
                    const visibleItems = grp.items.filter(
                      (it) => cascadedStageValues.has(it.value) || it.value === reqStage,
                    );
                    if (visibleItems.length === 0) return null;
                    return (
                      <React.Fragment key={grp.group}>
                        <SelectItem value={`__hdr_${gi}__`} disabled>─ {grp.group} ─</SelectItem>
                        {visibleItems.map((it) => (
                          <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <MultiSelectPopover
              label="Customers"
              testId="requirement-list-customer-filter"
              options={customerOptions}
              selectedIds={reqCustomerIds}
              onChange={setReqCustomerIds}
              placeholder="Search customers…"
            />
            <MultiSelectPopover
              label="Orders"
              testId="requirement-list-order-filter"
              options={orderOptions}
              selectedIds={reqOrderIds}
              onChange={setReqOrderIds}
              placeholder="Search order no or title…"
            />
            {/* Wave 3.99e — Raw Material multi-select. Same cascade semantics
                as the Customer + Order pickers. */}
            <MultiSelectPopover
              label="Raw Material"
              testId="requirement-list-material-filter"
              options={cascadedMaterialOptions}
              selectedIds={reqMaterialIds}
              onChange={setReqMaterialIds}
              placeholder="Search material…"
            />
            {refEnabled && (
              <ReferenceFilter
                options={reqReferenceOptions}
                value={reqReferenceNos}
                onChange={setReqReferenceNos}
                label={refLabel}
                testIdPrefix="requirement-list-reference-filter"
              />
            )}
          </div>
        </div>
      </div>

      {/* Wave 3.98 — Filter chip row. Each active filter renders a chip
          with an inline ✕ so the operator can drop that one filter
          without touching the others. Wave 3.99 — comparisons use the
          MASTER universe totals so cascade narrowing does not falsely
          hide chips. */}
      {(() => {
        const chips = [];
        if (reqSearchDebounced && reqSearchDebounced.trim()) {
          chips.push({
            key: 'search',
            label: `Search: "${reqSearchDebounced.trim()}"`,
            onRemove: () => setReqSearch(''),
          });
        }
        if (reqStage && reqStage !== 'all') {
          chips.push({
            key: 'stage',
            label: `Stage: ${REQ_STAGE_LABEL[reqStage] || reqStage}`,
            onRemove: () => setReqStage('all'),
          });
        }
        if (masterData) {
          const masterCustomerList = collectCustomerOptions(masterData);
          const totalMasterCustomers = masterCustomerList.length;
          if (reqCustomerIds.size > 0 && reqCustomerIds.size < totalMasterCustomers) {
            const first = masterCustomerList.find((o) => reqCustomerIds.has(o.id));
            const label = reqCustomerIds.size === 1
              ? `Customer: ${first?.label || '—'}`
              : `${reqCustomerIds.size} customers`;
            chips.push({
              key: 'customers',
              label,
              onRemove: () => setReqCustomerIds(new Set()),
            });
          }
          const totalMasterOrders = (masterData.orders || []).length;
          if (totalMasterOrders > 0 && reqOrderIds.size < totalMasterOrders) {
            chips.push({
              key: 'orders',
              label: reqOrderIds.size === 0
                ? 'No orders selected'
                : `${reqOrderIds.size} of ${totalMasterOrders} orders`,
              onRemove: () => setReqOrderIds(new Set((masterData.orders || []).map((o) => o.id))),
            });
          }
          // Wave 3.99e — Raw Material chip. Shows when the operator
          // has narrowed away from "All materials present in master".
          const totalMasterMaterials = presentMaterialsInMaster.size;
          if (
            totalMasterMaterials > 0
            && reqMaterialIds.size < totalMasterMaterials
          ) {
            const matLabel = (id) => (ALL_MATERIALS.find((m) => m.id === id) || {}).label || id;
            const label = reqMaterialIds.size === 0
              ? 'No materials selected'
              : reqMaterialIds.size === 1
                ? `Material: ${matLabel(Array.from(reqMaterialIds)[0])}`
                : `${reqMaterialIds.size} materials`;
            chips.push({
              key: 'materials',
              label,
              onRemove: () => setReqMaterialIds(new Set(presentMaterialsInMaster)),
            });
          }
        }
        // Reference-Number system — one removable chip per selected reference.
        if (refEnabled && (reqReferenceNos || []).length > 0) {
          reqReferenceNos.forEach((ref) => {
            chips.push({
              key: `reference-${ref}`,
              label: `${refLabelShort}: ${ref}`,
              onRemove: () => setReqReferenceNos((prev) => prev.filter((r) => r !== ref)),
            });
          });
        }
        if (chips.length === 0) return null;
        return (
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="requirement-list-filter-chips">
            <span className="text-xs font-medium text-slate-500 mr-1">Filters applied</span>
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={c.onRemove}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[var(--ink-navy)]/20 bg-[var(--jewellery-bg)] text-[var(--ink-navy)] hover:bg-[var(--jewellery-bg)]/80 transition-colors"
                data-testid={`requirement-list-filter-chip-${c.key}`}
                title="Remove this filter"
              >
                {c.label}
                <span aria-hidden className="text-slate-500">✕</span>
              </button>
            ))}
            <button
              type="button"
              onClick={clearAllFilters}
              className="text-[11px] text-slate-500 hover:text-[var(--ink-navy)] hover:underline ml-1"
              data-testid="requirement-list-filter-clear-all"
            >
              Clear all
            </button>
          </div>
        );
      })()}

      {/* Wave 3.81 — Grouping + view-mode toggle bar. Both apply to the
          on-screen list AND propagate to the PDF endpoint. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5" data-testid="requirement-list-grouping-toggle">
          {[
            { key: 'by_order', label: 'By Order Form' },
            { key: 'by_material', label: 'By Material' },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setGrouping(opt.key)}
              className={`px-3 h-7 text-[12px] rounded transition-colors ${
                grouping === opt.key
                  ? 'bg-white text-[var(--ink-navy)] shadow-sm font-medium'
                  : 'text-slate-600 hover:text-[var(--ink-navy)]'
              }`}
              data-testid={`requirement-list-grouping-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5" data-testid="requirement-list-viewmode-toggle">
          {[
            { key: 'expanded', label: 'Expanded' },
            { key: 'collapsed', label: 'Collapsed' },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setViewMode(opt.key)}
              className={`px-3 h-7 text-[12px] rounded transition-colors ${
                viewMode === opt.key
                  ? 'bg-white text-[var(--ink-navy)] shadow-sm font-medium'
                  : 'text-slate-600 hover:text-[var(--ink-navy)]'
              }`}
              data-testid={`requirement-list-viewmode-${opt.key}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* Sort toggle — order by Order No (Newest / Oldest). Applies to the
            on-screen list AND the PDF / Print / Excel exports. */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5" data-testid="requirement-list-sort-toggle">
          {[
            { key: 'newest', label: 'Newest' },
            { key: 'oldest', label: 'Oldest' },
          ].map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setSortDir(opt.key)}
              className={`px-3 h-7 text-[12px] rounded transition-colors ${
                sortDir === opt.key
                  ? 'bg-white text-[var(--ink-navy)] shadow-sm font-medium'
                  : 'text-slate-600 hover:text-[var(--ink-navy)]'
              }`}
              data-testid={`requirement-list-sort-${opt.key}`}
              title={opt.key === 'newest' ? 'Highest Order No first' : 'Lowest Order No first'}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* "New page per Page No" — PDF export option (mirrors the Dashboard).
            Only meaningful when the company uses Page Nos and the current
            list spans more than one. */}
        {refEnabled && reqVisibleReferenceCount > 1 && (
          <label
            className="ml-auto inline-flex items-center gap-1.5 text-[12px] text-slate-600 hover:text-[var(--ink-navy)] cursor-pointer select-none"
            data-testid="requirement-list-pagebreak-toggle"
            title={`Start each ${refLabel.replace(/\.$/, '')} on a new page in the PDF`}
          >
            <Checkbox
              checked={pageBreakPref}
              onCheckedChange={(v) => setPageBreakPref(!!v)}
              data-testid="requirement-list-pagebreak-checkbox"
            />
            New page per {refLabel.replace(/\.$/, '')}
          </label>
        )}
      </div>

      {loading && (
        <div className="text-sm text-slate-500 py-6" data-testid="requirement-list-loading">Loading…</div>
      )}
      {!loading && data && data.order_count === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md" data-testid="requirement-list-empty">
          No orders with <span className="font-medium">{status}</span> requirements.
        </div>
      )}
      {!loading && data && data.order_count > 0 && (
        grouping === 'by_order' ? (
          <RequirementListByOrder
            data={data}
            statusFilter={status}
            viewMode={viewMode}
            columns={columns}
            groupByCustomer={groupByCustomer}
            refEnabled={refEnabled}
            refLabel={refLabel}
            refLabelShort={refLabelShort}
          />
        ) : (
          <RequirementListByMaterial data={data} viewMode={viewMode} columns={columns} />
        )
      )}

      <PdfDownloadDialog
        open={pdfDialog.open}
        onOpenChange={(o) => setPdfDialog({ ...pdfDialog, open: o })}
        title={pdfDialog.mode === 'view' ? 'View Requirement List PDF' : pdfDialog.mode === 'print' ? 'Print Requirement List' : 'Download Requirement List PDF'}
        description="Choose whether to print your company logo and name on the requirement list."
        companyId={data?.goldsmith?.company_id}
        endpoint={`/orders/by-goldsmith/${goldsmithId}/requirement-list/pdf`}
        filename={`RequirementList_${safeGsName}_${status}_${grouping}_${viewMode}.pdf`}
        // Wave 3.98 — Bring EVERY active filter along to the PDF export.
        extraParams={{
          ...serverParams,
          grouping,
          view_mode: viewMode,
          new_page_per_ref: (pageBreakPref && refEnabled && reqVisibleReferenceCount > 1),
          // Bug fix — the on-screen Page No filter is applied client-side
          // (see fetchList), so it must be sent explicitly as reference_nos
          // or the PDF would include every page instead of the selected ones.
          reference_nos: (refEnabled && (reqReferenceNos || []).length > 0)
            ? reqReferenceNos.join(',')
            : undefined,
          // Operator-curated header segments (Print-header popover).
          header_filters: headerFiltersParam,
        }}
        mode={pdfDialog.mode}
      />

      {/* Requirement Cards PDF — landscape image-card grid (5/row) grouped
          Customer -> Page No -> Order Form with per-Page + per-Customer
          remaining-material subtotals. Opens in a new tab (mode="view"). */}
      <PdfDownloadDialog
        open={cardsPdfOpen}
        onOpenChange={setCardsPdfOpen}
        title="View Requirement Cards PDF"
        description="A landscape image-card sheet of pending requirements, grouped by Customer and Page No with remaining-material subtotals."
        companyId={companyId || data?.goldsmith?.company_id}
        endpoint={`/orders/by-goldsmith/${goldsmithId}/requirement-list/cards-pdf`}
        filename={`RequirementCards_${safeGsName}_${status}.pdf`}
        // Mirror EVERY active filter, including the client-side Page/Reference
        // filter (sent as reference_nos so the server-rendered PDF matches
        // the on-screen result set exactly).
        extraParams={{
          ...serverParams,
          reference_nos: (refEnabled && (reqReferenceNos || []).length > 0)
            ? reqReferenceNos.join(',')
            : undefined,
          // Operator-curated header segments (Print-header popover).
          header_filters: headerFiltersParam,
        }}
        mode="view"
      />
    </Card>
  );
}


// ============================================================================
// Wave 3.81 — Requirement List views (By Order Form / By Material)
// ----------------------------------------------------------------------------
// Both views live inline in GoldsmithDetail.jsx so they share the ``data``
// payload returned by ``GET /orders/by-goldsmith/{id}/requirement-list``
// (which already carries a ``groups`` breakdown per order).
// ============================================================================

// Wave 3.81 — Helper: aggregate one order's requirements into a compact
// summary used by both the collapsed by_order view and the summary row
// inside the expanded by_order view.
function _aggregateOrder(order) {
  const agg = {
    req_ct: 0, iss_ct: 0, bal_ct: 0,
    req_pcs: 0, iss_pcs: 0, bal_pcs: 0,
    req_g: 0, iss_g: 0, bal_g: 0,
    pending: 0, partial: 0, complete: 0,
  };
  const bumpStatus = (r) => {
    const s = (r.status || 'pending').toLowerCase();
    if (s === 'complete' || s === 'completed' || s === 'done') agg.complete += 1;
    else if (s === 'partial') agg.partial += 1;
    else agg.pending += 1;
  };
  for (const mt of ['diamond', 'color_stone', 'pearl']) {
    for (const r of (order.groups?.[mt] || [])) {
      const req = Number(r.required_carats || 0);
      const fil = Number(r.fulfilled_carats || 0);
      agg.req_ct += req; agg.iss_ct += fil; agg.bal_ct += Math.max(0, req - fil);
      const rp = Number(r.required_pieces || 0);
      const fp = Number(r.fulfilled_pieces || 0);
      agg.req_pcs += rp; agg.iss_pcs += fp; agg.bal_pcs += Math.max(0, rp - fp);
      bumpStatus(r);
    }
  }
  for (const r of (order.groups?.gold || [])) {
    const req = Number(r.required_grams || 0);
    const fil = Number(r.fulfilled_grams || 0);
    agg.req_g += req; agg.iss_g += fil; agg.bal_g += Math.max(0, req - fil);
    bumpStatus(r);
  }
  return agg;
}

// Wave 3.81 — Shared shape extractor. The requirement's
// ``classification_label`` is always ``"<schema_code> — <shape_name>"``
// when a classification is linked, so we split on the em-dash to
// surface the shape independent of the code prefix.
function _splitLabel(label) {
  if (!label) return { code: '', shape: '' };
  if (label.includes(' — ')) {
    const [code, ...rest] = label.split(' — ');
    return { code: code.trim(), shape: rest.join(' — ').trim() };
  }
  return { code: '', shape: label.trim() };
}

const _MATERIAL_LABEL = { diamond: 'Diamond', color_stone: 'Colour Stone', pearl: 'Pearl', gold: 'Gold' };
const _MATERIAL_RANK  = { diamond: 0, color_stone: 1, pearl: 2, gold: 3 };

function StatusPill({ status }) {
  const s = (status || 'pending').toLowerCase();
  if (s === 'complete' || s === 'completed' || s === 'done') {
    return <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-50 text-emerald-700">COMPLETE</span>;
  }
  const partial = s === 'partial';
  const cls = partial
    ? 'bg-amber-50 text-amber-800'
    : 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${cls}`}>
      {partial ? 'PARTIAL' : 'PENDING'}
    </span>
  );
}

function AgeCell({ status, days }) {
  // Wave 3.82 — Standalone Age cell used when the "Age" column is
  // toggled on. Only shows a value for still-pending / partial
  // requirements; completed rows collapse to a subtle em-dash so the
  // column doesn't turn into visual noise.
  const s = (status || 'pending').toLowerCase();
  if (s === 'complete' || s === 'completed' || s === 'done') {
    return <span className="text-slate-300">—</span>;
  }
  const d = Number(days || 0);
  const label = d <= 0 ? 'Today' : d === 1 ? '1 Day' : `${d} Days`;
  const cls = d >= 15
    ? 'text-rose-700 font-medium'
    : d >= 7
    ? 'text-amber-700'
    : 'text-slate-600';
  return <span className={`text-[11px] ${cls}`}>{label}</span>;
}

function CommentsCell({ count }) {
  const n = Number(count || 0);
  if (n === 0) return <span className="text-slate-300">—</span>;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-700 font-medium">
      <MessageSquare size={11} /> {n}
    </span>
  );
}

/**
 * Wave 3.81 — "By Order Form" list.
 *
 * ``viewMode='expanded'``: One card per order with a full
 * per-order requirement table (Material · Shape · Size · Sieves ·
 * Req/Iss/Bal Pcs · Req/Iss/Bal Ct · Remarks · Status).
 *
 * ``viewMode='collapsed'``: A single flat table with one row per
 * order (order · title · stage · pending/partial/complete counts
 * · aggregate carats + pieces + grams).
 */
function RequirementListByOrder({ data, statusFilter, viewMode, columns, groupByCustomer, refEnabled = false, refLabel = 'Page No.', refLabelShort = 'Page' }) {
  const cols = columns || { remarks: true, comments: true, age: true };
  const refTerm = (refLabel || 'Page No.').replace(/\.$/, '');
  // Wave 4.06 — When the company uses running (Page) numbers, the
  // "By Order Form · Expanded" view groups orders by their Page No with a
  // banner that mirrors the Dashboard exactly. ``data.orders`` already
  // arrives server-sorted by Page No (primary) then Order No (secondary,
  // direction per the sort toggle), so grouping in first-seen order keeps
  // that direction — no re-sort needed. Orders without a Page No trail in a
  // final "No <term>" group.
  const referenceGroups = React.useMemo(() => {
    if (!refEnabled) return null;
    const map = new Map();
    const keyOrder = [];
    for (const o of (data.orders || [])) {
      const key = o.reference_no != null ? o.reference_no : '__none__';
      if (!map.has(key)) { map.set(key, []); keyOrder.push(key); }
      map.get(key).push(o);
    }
    return keyOrder.map((key) => ({
      ref: key === '__none__' ? null : key,
      label: key === '__none__' ? `No ${refTerm}` : `${refTerm} ${key}`,
      orders: map.get(key),
    }));
  }, [data.orders, refEnabled, refTerm]);
  // Wave 3.98 — Group orders by customer when the user has picked
  // multiple customers. Falls back to a single flat group otherwise.
  const groupedOrders = React.useMemo(() => {
    if (!groupByCustomer) return null;
    const buckets = new Map();
    for (const o of (data.orders || [])) {
      const key = o.customer_id || '__none__';
      const label = o.customer_name || '(No customer)';
      if (!buckets.has(key)) buckets.set(key, { key, label, orders: [] });
      buckets.get(key).orders.push(o);
    }
    return Array.from(buckets.values()).sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [data.orders, groupByCustomer]);

  if (viewMode === 'collapsed') {
    const grand = { req_ct: 0, iss_ct: 0, bal_ct: 0, req_pcs: 0, iss_pcs: 0, bal_pcs: 0, req_g: 0, iss_g: 0, bal_g: 0, pending: 0, partial: 0, complete: 0 };
    for (const o of data.orders) {
      const a = _aggregateOrder(o);
      Object.keys(grand).forEach((k) => { grand[k] += a[k]; });
    }
    // One data row per order (shared by the flat + Page-No-grouped layouts).
    const renderCollapsedRow = (o) => {
      const a = _aggregateOrder(o);
      return (
        <tr key={o.id} data-testid={`requirement-list-by-order-collapsed-${o.order_no}`}>
          <td className="px-2 py-1.5">
            {/* Wave 3.98 — Inline thumbnail beside the order number. */}
            <div className="w-8 h-8 rounded-sm overflow-hidden border border-[var(--divider)] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
              {o.image ? (
                <img src={o.image} alt={o.title || o.order_no} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <ImageOff size={12} className="text-slate-300" aria-hidden />
              )}
            </div>
          </td>
          <td className="px-3 py-2">
            <div className="flex items-center gap-1.5">
              <Link target="_blank" rel="noopener noreferrer" to={`/orders/${o.id}`} className="font-mono text-[11px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline">
                {o.order_no}
              </Link>
              {refEnabled && o.reference_no != null && (
                <InlineReference
                  referenceNo={o.reference_no}
                  labelShort={refLabelShort}
                  testId={`requirement-order-${o.order_no}-page-no`}
                />
              )}
            </div>
          </td>
          <td className="px-3 py-2 max-w-[160px] truncate text-slate-600" title={o.customer_name || ''}>
            {o.customer_name || <span className="italic text-slate-400">No customer</span>}
          </td>
          <td className="px-3 py-2 max-w-[220px] truncate" title={o.title || ''}>{o.title || <span className="italic text-slate-400">Untitled</span>}</td>
          <td className="px-3 py-2">
            {/* Wave 3.98 — Multi-pill StageBadge (matches Order Forms). */}
            <StageBadge stage={o.stage} substage={o.substage} holdStatus={o.hold_status} activeSteps={o.active_steps} />
          </td>
          <td className="px-3 py-2 text-right tabular-nums">{a.pending}</td>
          <td className="px-3 py-2 text-right tabular-nums">{a.partial}</td>
          <td className="px-3 py-2 text-right tabular-nums border-r border-slate-300">{a.complete}</td>
          <td className="px-3 py-2 text-right tabular-nums">{a.req_pcs}</td>
          <td className="px-3 py-2 text-right tabular-nums req-issued">{a.iss_pcs}</td>
          <td className="px-3 py-2 text-right tabular-nums req-balance border-r border-slate-300">{a.bal_pcs}</td>
          <td className="px-3 py-2 text-right tabular-nums">{formatCarats(a.req_ct)}</td>
          <td className="px-3 py-2 text-right tabular-nums req-issued">{formatCarats(a.iss_ct)}</td>
          <td className="px-3 py-2 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(a.bal_ct)}</td>
          <td className="px-3 py-2 text-right tabular-nums">{formatGrams(a.req_g)}</td>
          <td className="px-3 py-2 text-right tabular-nums req-issued">{formatGrams(a.iss_g)}</td>
          <td className="px-3 py-2 text-right tabular-nums req-balance">{formatGrams(a.bal_g)}</td>
        </tr>
      );
    };
    // Full-width blue Page-No bar row (matches the expanded-view banner).
    const renderCollapsedBanner = (g) => (
      <tr key={`ref-${g.ref ?? '__none__'}`} data-testid={`requirement-list-collapsed-ref-group-${g.ref ?? 'none'}`}>
        <td colSpan={17} className="p-0">
          <div className="flex items-center gap-2 border-l-4 border-l-indigo-400 pl-2.5 py-1.5 bg-indigo-50/60">
            <span className="inline-flex items-center font-mono text-[11px] text-indigo-700 bg-white border border-indigo-200 px-2 py-0.5 rounded">{g.label}</span>
            <span className="text-[11px] text-slate-500">{g.orders.length} order{g.orders.length === 1 ? '' : 's'}</span>
          </div>
        </td>
      </tr>
    );
    return (
      <div className="overflow-x-auto ledger-table" data-testid="requirement-list-by-order-collapsed">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-[var(--ink-navy)] text-white">
              <th className="text-left px-3 py-2 font-semibold w-8"></th>{/* thumb column */}
              <th className="text-left px-3 py-2 font-semibold">Order</th>
              <th className="text-left px-3 py-2 font-semibold">Customer</th>
              <th className="text-left px-3 py-2 font-semibold">Title</th>
              <th className="text-left px-3 py-2 font-semibold">Stage</th>
              <th className="text-right px-3 py-2 font-semibold">Pending</th>
              <th className="text-right px-3 py-2 font-semibold">Partial</th>
              <th className="text-right px-3 py-2 font-semibold border-r border-slate-300">Complete</th>
              <th className="text-right px-3 py-2 font-semibold">Req Pcs</th>
              <th className="text-right px-3 py-2 font-semibold">Iss Pcs</th>
              <th className="text-right px-3 py-2 font-semibold border-r border-slate-300">Bal Pcs</th>
              <th className="text-right px-3 py-2 font-semibold">Req Ct</th>
              <th className="text-right px-3 py-2 font-semibold">Iss Ct</th>
              <th className="text-right px-3 py-2 font-semibold border-r border-slate-300">Bal Ct</th>
              <th className="text-right px-3 py-2 font-semibold">Gold Req (g)</th>
              <th className="text-right px-3 py-2 font-semibold">Gold Iss (g)</th>
              <th className="text-right px-3 py-2 font-semibold">Gold Bal (g)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--divider)]">
            {refEnabled && referenceGroups ? (
              referenceGroups.map((g) => (
                <React.Fragment key={g.ref ?? '__none__'}>
                  {renderCollapsedBanner(g)}
                  {g.orders.map((o) => renderCollapsedRow(o))}
                </React.Fragment>
              ))
            ) : (
              data.orders.map((o) => renderCollapsedRow(o))
            )}
            <tr className="bg-slate-100 font-medium" data-testid="requirement-list-by-order-collapsed-total">
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2">TOTAL</td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-right tabular-nums">{grand.pending}</td>
              <td className="px-3 py-2 text-right tabular-nums">{grand.partial}</td>
              <td className="px-3 py-2 text-right tabular-nums border-r border-slate-300">{grand.complete}</td>
              <td className="px-3 py-2 text-right tabular-nums">{grand.req_pcs}</td>
              <td className="px-3 py-2 text-right tabular-nums req-issued">{grand.iss_pcs}</td>
              <td className="px-3 py-2 text-right tabular-nums req-balance border-r border-slate-300">{grand.bal_pcs}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCarats(grand.req_ct)}</td>
              <td className="px-3 py-2 text-right tabular-nums req-issued">{formatCarats(grand.iss_ct)}</td>
              <td className="px-3 py-2 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(grand.bal_ct)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatGrams(grand.req_g)}</td>
              <td className="px-3 py-2 text-right tabular-nums req-issued">{formatGrams(grand.iss_g)}</td>
              <td className="px-3 py-2 text-right tabular-nums req-balance">{formatGrams(grand.bal_g)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Expanded — one table per order.
  // Wave 4.06 — When the company uses running (Page) numbers, group the
  // expanded cards by Page No with a banner that mirrors the Dashboard.
  // Takes precedence over the customer grouping (the customer name still
  // shows on each card). ``ByOrderExpandedCard`` already renders the Order
  // No + reference inline, so the banner adds the grouping context.
  if (refEnabled && referenceGroups) {
    return (
      <div className="space-y-6" data-testid="requirement-list-by-order-expanded">
        {referenceGroups.map((g) => (
          <div
            key={g.ref ?? '__none__'}
            className="space-y-2"
            data-testid={`requirement-list-ref-group-${g.ref ?? 'none'}`}
          >
            <div className="flex items-center gap-2 border-l-4 border-l-indigo-400 pl-2.5 py-1 bg-indigo-50/50 rounded-r-md">
              <span className="inline-flex items-center font-mono text-[11px] text-indigo-700 bg-white border border-indigo-200 px-2 py-0.5 rounded">{g.label}</span>
              <span className="text-[11px] text-slate-500">{g.orders.length} order{g.orders.length === 1 ? '' : 's'}</span>
            </div>
            <div className="space-y-4">
              {g.orders.map((o) => (
                <ByOrderExpandedCard key={o.id} order={o} statusFilter={statusFilter} columns={cols} refEnabled={refEnabled} refLabelShort={refLabelShort} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Group by customer when requested (and references are not driving the
  // grouping above).
  if (groupByCustomer && groupedOrders) {
    return (
      <div className="space-y-6" data-testid="requirement-list-by-order-expanded">
        {groupedOrders.map((bucket) => (
          <div key={bucket.key} className="space-y-2" data-testid={`requirement-list-customer-group-${bucket.key}`}>
            <div className="flex items-center gap-2 border-l-4 border-[var(--gold-accent)] pl-3 py-1 bg-slate-50/60 rounded-r-md">
              <User size={14} className="text-[var(--goldsmith-icon)]" />
              <div className="text-sm font-semibold text-[var(--ink-navy)]">{bucket.label}</div>
              <span className="text-[11px] text-slate-500">· {bucket.orders.length} order{bucket.orders.length === 1 ? '' : 's'}</span>
            </div>
            <div className="space-y-4">
              {bucket.orders.map((o) => (
                <ByOrderExpandedCard key={o.id} order={o} statusFilter={statusFilter} columns={cols} refEnabled={refEnabled} refLabelShort={refLabelShort} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="requirement-list-by-order-expanded">
      {data.orders.map((o) => (
        <ByOrderExpandedCard key={o.id} order={o} statusFilter={statusFilter} columns={cols} refEnabled={refEnabled} refLabelShort={refLabelShort} />
      ))}
    </div>
  );
}

function ByOrderExpandedCard({ order, statusFilter, columns, refEnabled = false, refLabelShort = 'Page' }) {
  const cols = columns || { remarks: true, comments: true, age: true };
  const stones = [];
  for (const mt of ['diamond', 'color_stone', 'pearl']) {
    for (const r of (order.groups?.[mt] || [])) {
      stones.push({ ...r, __mt: mt });
    }
  }
  // Sort stones with Wave 3.80 diamond hierarchy (via classification_label).
  stones.sort((a, b) => {
    const ra = _MATERIAL_RANK[a.__mt] ?? 99;
    const rb = _MATERIAL_RANK[b.__mt] ?? 99;
    if (ra !== rb) return ra - rb;
    if (a.__mt === 'diamond') {
      const [dra] = diamondHierarchyRank(a.classification_label || '');
      const [drb] = diamondHierarchyRank(b.classification_label || '');
      if (dra !== drb) return dra - drb;
    }
    const sa = _splitLabel(a.classification_label).shape.toLowerCase();
    const sb = _splitLabel(b.classification_label).shape.toLowerCase();
    if (sa !== sb) return sa < sb ? -1 : 1;
    return (a.size || '').localeCompare(b.size || '');
  });
  const golds = order.groups?.gold || [];
  return (
    <Card className="p-4" data-testid={`requirement-list-by-order-expanded-card-${order.order_no}`}>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          {/* Wave 3.98 — Order thumbnail next to the Order No. */}
          <div
            className="shrink-0 w-11 h-11 rounded-md overflow-hidden border border-[var(--divider)] bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center"
            data-testid={`requirement-order-thumb-${order.order_no}`}
          >
            {order.image ? (
              <img src={order.image} alt={order.title || order.order_no} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <ImageOff size={16} className="text-slate-300" aria-hidden />
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link target="_blank" rel="noopener noreferrer" to={`/orders/${order.id}`} className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline">
                {order.order_no}
              </Link>
              {refEnabled && order.reference_no != null && (
                <InlineReference
                  referenceNo={order.reference_no}
                  labelShort={refLabelShort}
                  testId={`requirement-order-${order.order_no}-page-no`}
                  valueSize="text-[11px]"
                />
              )}
              {/* Wave 3.98 — Multi-pill StageBadge (matches Order Forms). */}
              <StageBadge
                stage={order.stage}
                substage={order.substage}
                holdStatus={order.hold_status}
                activeSteps={order.active_steps}
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="text-sm font-medium text-[var(--ink-navy)] truncate max-w-[380px]" title={order.title || ''}>
                {order.title || <span className="italic text-slate-400">Untitled</span>}
              </span>
              {/* Wave 3.98 — Customer name next to title. */}
              <span
                className="inline-flex items-center gap-1 text-[11px] text-slate-500 truncate max-w-[220px]"
                title={order.customer_name || ''}
                data-testid={`requirement-order-${order.order_no}-customer`}
              >
                <User size={10} className="shrink-0 text-slate-400" aria-hidden />
                {order.customer_name || <span className="italic text-slate-400">No customer</span>}
              </span>
            </div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 flex gap-2">
          {order.requirements_pending > 0 && <span className="text-rose-700">{order.requirements_pending} pending</span>}
          {order.requirements_completed > 0 && <span className="text-emerald-700">{order.requirements_completed} completed</span>}
        </div>
      </div>
      {stones.length > 0 && (
        <div className="overflow-x-auto ledger-table mb-2">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[var(--ink-navy)] text-white">
                <th className="text-left px-2 py-1.5 font-semibold">Material</th>
                <th className="text-left px-2 py-1.5 font-semibold">Shape</th>
                <th className="text-left px-2 py-1.5 font-semibold">Size</th>
                <th className="text-left px-2 py-1.5 font-semibold border-r border-slate-300">Sieves</th>
                <th className="text-right px-2 py-1.5 font-semibold">Req Pcs</th>
                <th className="text-right px-2 py-1.5 font-semibold">Iss Pcs</th>
                <th className="text-right px-2 py-1.5 font-semibold border-r border-slate-300">Bal Pcs</th>
                <th className="text-right px-2 py-1.5 font-semibold">Req Ct</th>
                <th className="text-right px-2 py-1.5 font-semibold">Iss Ct</th>
                <th className="text-right px-2 py-1.5 font-semibold border-r border-slate-300">Bal Ct</th>
                {cols.remarks && <th className="text-left px-2 py-1.5 font-semibold">Remarks</th>}
                <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                {cols.age && <th className="text-left px-2 py-1.5 font-semibold">Age</th>}
                {cols.comments && <th className="text-center px-2 py-1.5 font-semibold">Cmts</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {stones.map((r) => {
                const { code, shape } = _splitLabel(r.classification_label);
                const rc = Number(r.required_carats || 0);
                const fc = Number(r.fulfilled_carats || 0);
                const bc = Math.max(0, rc - fc);
                const rp = Number(r.required_pieces || 0);
                const fp = Number(r.fulfilled_pieces || 0);
                const bp = Math.max(0, rp - fp);
                return (
                  <tr key={r.id}>
                    <td className="px-2 py-1.5 font-medium">{_MATERIAL_LABEL[r.__mt] || r.__mt}</td>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{shape || '—'}</div>
                      {code && <div className="text-[10px] text-slate-500">{code}</div>}
                    </td>
                    <td className="px-2 py-1.5">{r.size || '—'}</td>
                    <td className="px-2 py-1.5 border-r border-slate-300">{r.sieves || '—'}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{rp}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-issued">{fp}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{bp}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatCarats(rc)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatCarats(fc)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(bc)}</td>
                    {cols.remarks && <td className="px-2 py-1.5 text-slate-600 text-[11px]">{r.description || r.note || '—'}</td>}
                    <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                    {cols.age && <td className="px-2 py-1.5"><AgeCell status={r.status} days={r.days_since_requested} /></td>}
                    {cols.comments && <td className="px-2 py-1.5 text-center"><CommentsCell count={r.comments_count} /></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {golds.length > 0 && (
        <div className="overflow-x-auto ledger-table">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[var(--ink-navy)] text-white">
                <th className="text-left px-2 py-1.5 font-semibold">Purity</th>
                <th className="text-right px-2 py-1.5 font-semibold">Grams Req</th>
                <th className="text-right px-2 py-1.5 font-semibold">Metal Issued</th>
                <th className="text-right px-2 py-1.5 font-semibold">Balance (g)</th>
                <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                {cols.age && <th className="text-left px-2 py-1.5 font-semibold">Age</th>}
                {cols.comments && <th className="text-center px-2 py-1.5 font-semibold">Cmts</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--divider)]">
              {golds.map((r) => {
                const rg = Number(r.required_grams || 0);
                const fg = Number(r.fulfilled_grams || 0);
                const bg = Math.max(0, rg - fg);
                return (
                  <tr key={r.id}>
                    <td className="px-2 py-1.5 font-medium">{r.classification_label || (r.required_purity ? `${r.required_purity}%` : 'Gold')}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatGrams(rg)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatGrams(fg)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-balance">{formatGrams(bg)}</td>
                    <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                    {cols.age && <td className="px-2 py-1.5"><AgeCell status={r.status} days={r.days_since_requested} /></td>}
                    {cols.comments && <td className="px-2 py-1.5 text-center"><CommentsCell count={r.comments_count} /></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {stones.length === 0 && golds.length === 0 && (
        <div className="text-slate-500 text-sm italic py-4 text-center">No {statusFilter} requirements on this order.</div>
      )}
    </Card>
  );
}

/**
 * Wave 3.81 — "By Material" consolidated list. Combines every
 * requirement across every order into a single Stones table + a
 * single Gold table, sorted by ``material_rank → diamond_hierarchy →
 * shape → size → sieves``.
 *
 * ``viewMode='expanded'``: One row per underlying requirement. An
 * "Order" column links back to the source order.
 *
 * ``viewMode='collapsed'``: Rows sharing ``(material, shape, size,
 * sieves)`` are merged; Req Pcs / Iss Pcs / Bal Pcs / Req Ct / Iss
 * Ct / Bal Ct are summed. Gold rows are similarly consolidated by
 * purity label.
 */
function RequirementListByMaterial({ data, viewMode, columns }) {
  const cols = columns || { remarks: true, comments: true, age: true };
  // Wave 3.82 — Drill-down: in collapsed mode, clicking a merged
  // Stones row expands it inline to show the individual per-order
  // sub-rows that were rolled up. We key expansion by the bucket key
  // string so it survives re-renders across viewMode / columns
  // changes.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const toggleExpansion = (k) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  // Flatten & sort stones.
  const stones = [];
  for (const o of data.orders) {
    for (const mt of ['diamond', 'color_stone', 'pearl']) {
      for (const r of (o.groups?.[mt] || [])) {
        stones.push({ ...r, __mt: mt, __order_no: o.order_no, __order_id: o.id });
      }
    }
  }
  stones.sort((a, b) => {
    const ra = _MATERIAL_RANK[a.__mt] ?? 99;
    const rb = _MATERIAL_RANK[b.__mt] ?? 99;
    if (ra !== rb) return ra - rb;
    if (a.__mt === 'diamond') {
      const [dra] = diamondHierarchyRank(a.classification_label || '');
      const [drb] = diamondHierarchyRank(b.classification_label || '');
      if (dra !== drb) return dra - drb;
    }
    const sa = _splitLabel(a.classification_label).shape.toLowerCase();
    const sb = _splitLabel(b.classification_label).shape.toLowerCase();
    if (sa !== sb) return sa < sb ? -1 : 1;
    if ((a.size || '') !== (b.size || '')) return (a.size || '').localeCompare(b.size || '');
    if ((a.sieves || '') !== (b.sieves || '')) return (a.sieves || '').localeCompare(b.sieves || '');
    return (a.__order_no || '').localeCompare(b.__order_no || '');
  });
  // Flatten gold rows.
  const golds = [];
  for (const o of data.orders) {
    for (const r of (o.groups?.gold || [])) {
      golds.push({ ...r, __order_no: o.order_no, __order_id: o.id });
    }
  }
  golds.sort((a, b) => (a.classification_label || '').localeCompare(b.classification_label || ''));

  if (viewMode === 'collapsed') {
    // Consolidate stones by (material, shape, size, sieves).
    const bucketMap = new Map();
    for (const r of stones) {
      const { shape, code } = _splitLabel(r.classification_label);
      // Wave 4.x — Diamond sub-category (Polki → Diamonds → Rose Cuts →
      // Brown → Chakri → Other) is baked into the bucket key so the
      // collapsed view honours the canonical hierarchy exactly like the
      // expanded view. Without this, a Polki row that happens to share
      // shape/size/sieves with a Diamond row would merge into one bucket
      // and get mis-ranked (shown under Diamonds). Rank is derived from
      // the SAME input the expanded sort uses — the full classification
      // label — so the two views can never diverge.
      const dRank = r.__mt === 'diamond' ? diamondHierarchyRank(r.classification_label || '')[0] : 0;
      const key = [r.__mt, dRank, (shape || '').trim(), (r.size || '').trim(), (r.sieves || '').trim()].join('|');
      const b = bucketMap.get(key) || {
        _key: key,
        __mt: r.__mt, __d_rank: dRank, shape, size: r.size || '', sieves: r.sieves || '',
        req_ct: 0, iss_ct: 0, bal_ct: 0,
        req_pcs: 0, iss_pcs: 0, bal_pcs: 0,
        orders: new Set(), codes: new Set(),
        // Wave 3.82 — Keep the underlying rows so we can render a
        // per-order drill-down sub-table when the operator expands
        // this bucket.
        sources: [],
      };
      b.req_ct += Number(r.required_carats || 0);
      b.iss_ct += Number(r.fulfilled_carats || 0);
      b.bal_ct += Math.max(0, Number(r.required_carats || 0) - Number(r.fulfilled_carats || 0));
      b.req_pcs += Number(r.required_pieces || 0);
      b.iss_pcs += Number(r.fulfilled_pieces || 0);
      b.bal_pcs += Math.max(0, Number(r.required_pieces || 0) - Number(r.fulfilled_pieces || 0));
      if (r.__order_no) b.orders.add(r.__order_no);
      if (code) b.codes.add(code);
      b.sources.push(r);
      bucketMap.set(key, b);
    }
    const stoneBuckets = Array.from(bucketMap.values()).sort((a, b) => {
      const ra = _MATERIAL_RANK[a.__mt] ?? 99;
      const rb = _MATERIAL_RANK[b.__mt] ?? 99;
      if (ra !== rb) return ra - rb;
      // Diamond sub-category hierarchy — uses the rank stamped on the
      // bucket (derived from the source classification label) so the
      // order matches the expanded view exactly.
      if (a.__mt === 'diamond') {
        const dra = a.__d_rank ?? 99;
        const drb = b.__d_rank ?? 99;
        if (dra !== drb) return dra - drb;
      }
      if (a.shape !== b.shape) return (a.shape || '').localeCompare(b.shape || '');
      if (a.size !== b.size) return (a.size || '').localeCompare(b.size || '');
      return (a.sieves || '').localeCompare(b.sieves || '');
    });

    const goldBucketMap = new Map();
    for (const r of golds) {
      const key = (r.classification_label || 'Gold').trim();
      const b = goldBucketMap.get(key) || { purity_label: key, req_g: 0, iss_g: 0, bal_g: 0, orders: new Set() };
      b.req_g += Number(r.required_grams || 0);
      b.iss_g += Number(r.fulfilled_grams || 0);
      b.bal_g += Math.max(0, Number(r.required_grams || 0) - Number(r.fulfilled_grams || 0));
      if (r.__order_no) b.orders.add(r.__order_no);
      goldBucketMap.set(key, b);
    }
    const goldBuckets = Array.from(goldBucketMap.values()).sort((a, b) => a.purity_label.localeCompare(b.purity_label));

    const stoneTotals = stoneBuckets.reduce((acc, b) => ({
      req_ct: acc.req_ct + b.req_ct, iss_ct: acc.iss_ct + b.iss_ct, bal_ct: acc.bal_ct + b.bal_ct,
      req_pcs: acc.req_pcs + b.req_pcs, iss_pcs: acc.iss_pcs + b.iss_pcs, bal_pcs: acc.bal_pcs + b.bal_pcs,
    }), { req_ct: 0, iss_ct: 0, bal_ct: 0, req_pcs: 0, iss_pcs: 0, bal_pcs: 0 });
    const goldTotals = goldBuckets.reduce((acc, b) => ({
      req_g: acc.req_g + b.req_g, iss_g: acc.iss_g + b.iss_g, bal_g: acc.bal_g + b.bal_g,
    }), { req_g: 0, iss_g: 0, bal_g: 0 });

    return (
      <div className="space-y-4" data-testid="requirement-list-by-material-collapsed">
        {stoneBuckets.length > 0 && (
          <Card className="p-4">
            <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Stone Requirements <span className="text-slate-500 font-normal">(Consolidated · Summed)</span></div>
            <div className="overflow-x-auto ledger-table">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-[var(--ink-navy)] text-white">
                    <th className="text-left px-2 py-1.5 font-semibold w-6"></th>
                    <th className="text-left px-2 py-1.5 font-semibold">Material</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Shape</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Size</th>
                    <th className="text-left px-2 py-1.5 font-semibold border-r border-slate-300">Sieves</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Req Pcs</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Iss Pcs</th>
                    <th className="text-right px-2 py-1.5 font-semibold border-r border-slate-300">Bal Pcs</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Req Ct</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Iss Ct</th>
                    <th className="text-right px-2 py-1.5 font-semibold border-r border-slate-300">Bal Ct</th>
                    <th className="text-right px-2 py-1.5 font-semibold">#Orders</th>
                    {cols.comments && <th className="text-center px-2 py-1.5 font-semibold">Cmts</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--divider)]">
                  {stoneBuckets.map((b, idx) => {
                    const isExpanded = expandedKeys.has(b._key);
                    const drillCommentsTotal = (b.sources || []).reduce((s, r) => s + Number(r.comments_count || 0), 0);
                    return (
                      <React.Fragment key={idx}>
                        <tr
                          className={`cursor-pointer hover:bg-slate-50 ${isExpanded ? 'bg-slate-50' : ''}`}
                          onClick={() => toggleExpansion(b._key)}
                          data-testid={`requirement-list-by-material-collapsed-row-${b._key}`}
                        >
                          <td className="px-2 py-1.5 text-slate-500">
                            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </td>
                          <td className="px-2 py-1.5 font-medium">{_MATERIAL_LABEL[b.__mt] || b.__mt}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{b.shape || '—'}</div>
                            {b.codes.size > 0 && <div className="text-[10px] text-slate-500">{Array.from(b.codes).sort().join(' · ')}</div>}
                          </td>
                          <td className="px-2 py-1.5">{b.size || '—'}</td>
                          <td className="px-2 py-1.5 border-r border-slate-300">{b.sieves || '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{b.req_pcs}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums req-issued">{b.iss_pcs}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{b.bal_pcs}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{formatCarats(b.req_ct)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatCarats(b.iss_ct)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(b.bal_ct)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{b.orders.size}</td>
                          {cols.comments && <td className="px-2 py-1.5 text-center"><CommentsCell count={drillCommentsTotal} /></td>}
                        </tr>
                        {isExpanded && (b.sources || []).map((sr) => {
                          const rp = Number(sr.required_pieces || 0);
                          const fp = Number(sr.fulfilled_pieces || 0);
                          const bp = Math.max(0, rp - fp);
                          const rc = Number(sr.required_carats || 0);
                          const fc = Number(sr.fulfilled_carats || 0);
                          const bc = Math.max(0, rc - fc);
                          return (
                            <tr key={`${b._key}_${sr.__order_id}_${sr.id}`} className="bg-slate-50/60">
                              <td className="px-2 py-1.5"></td>
                              <td className="px-2 py-1.5"></td>
                              <td className="px-2 py-1.5 pl-6 text-slate-500 text-[11px] italic">↳ from</td>
                              <td className="px-2 py-1.5 text-slate-500 border-r border-slate-300" colSpan={2}>
                                <Link target="_blank" rel="noopener noreferrer" to={`/orders/${sr.__order_id}`} className="font-mono text-[11px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-1.5 py-0.5 rounded hover:underline">
                                  {sr.__order_no}
                                </Link>
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{rp}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums req-issued">{fp}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{bp}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{formatCarats(rc)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatCarats(fc)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(bc)}</td>
                              <td className="px-2 py-1.5"></td>
                              {cols.comments && <td className="px-2 py-1.5 text-center"><CommentsCell count={sr.comments_count} /></td>}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  <tr className="bg-slate-100 font-medium">
                    <td className="px-2 py-1.5"></td>
                    <td className="px-2 py-1.5">TOTAL</td>
                    <td className="px-2 py-1.5 border-r border-slate-300" colSpan={3}></td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{stoneTotals.req_pcs}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-issued">{stoneTotals.iss_pcs}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{stoneTotals.bal_pcs}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatCarats(stoneTotals.req_ct)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatCarats(stoneTotals.iss_ct)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(stoneTotals.bal_ct)}</td>
                    <td className="px-2 py-1.5"></td>
                    {cols.comments && <td className="px-2 py-1.5"></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        )}
        {goldBuckets.length > 0 && (
          <Card className="p-4">
            <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Gold Requirements <span className="text-slate-500 font-normal">(Consolidated · Summed)</span></div>
            <div className="overflow-x-auto ledger-table">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-[var(--ink-navy)] text-white">
                    <th className="text-left px-2 py-1.5 font-semibold">Purity</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Grams Req</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Metal Issued</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Balance (g)</th>
                    <th className="text-right px-2 py-1.5 font-semibold">#Orders</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--divider)]">
                  {goldBuckets.map((b, idx) => (
                    <tr key={idx}>
                      <td className="px-2 py-1.5 font-medium">{b.purity_label}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatGrams(b.req_g)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatGrams(b.iss_g)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-balance">{formatGrams(b.bal_g)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{b.orders.size}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100 font-medium">
                    <td className="px-2 py-1.5">TOTAL</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{formatGrams(goldTotals.req_g)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatGrams(goldTotals.iss_g)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums req-balance">{formatGrams(goldTotals.bal_g)}</td>
                    <td className="px-2 py-1.5"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        )}
        {stoneBuckets.length === 0 && goldBuckets.length === 0 && (
          <div className="text-slate-500 text-sm italic py-6 text-center">No requirements to display.</div>
        )}
      </div>
    );
  }

  // Expanded — one row per requirement, with Order column.
  return (
    <div className="space-y-4" data-testid="requirement-list-by-material-expanded">
      {stones.length > 0 && (
        <Card className="p-4">
          <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Stone Requirements <span className="text-slate-500 font-normal">(Consolidated)</span></div>
          <div className="overflow-x-auto ledger-table">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-[var(--ink-navy)] text-white">
                  <th className="text-left px-2 py-1.5 font-semibold">Material</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Shape</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Size</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Sieves</th>
                  <th className="text-left px-2 py-1.5 font-semibold border-r border-slate-300">Order</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Req Pcs</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Iss Pcs</th>
                  <th className="text-right px-2 py-1.5 font-semibold border-r border-slate-300">Bal Pcs</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Req Ct</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Iss Ct</th>
                  <th className="text-right px-2 py-1.5 font-semibold border-r border-slate-300">Bal Ct</th>
                  {cols.remarks && <th className="text-left px-2 py-1.5 font-semibold">Remarks</th>}
                  <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                  {cols.age && <th className="text-left px-2 py-1.5 font-semibold">Age</th>}
                  {cols.comments && <th className="text-center px-2 py-1.5 font-semibold">Cmts</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--divider)]">
                {stones.map((r) => {
                  const { code, shape } = _splitLabel(r.classification_label);
                  const rc = Number(r.required_carats || 0);
                  const fc = Number(r.fulfilled_carats || 0);
                  const bc = Math.max(0, rc - fc);
                  const rp = Number(r.required_pieces || 0);
                  const fp = Number(r.fulfilled_pieces || 0);
                  const bp = Math.max(0, rp - fp);
                  return (
                    <tr key={`${r.__order_id}_${r.id}`}>
                      <td className="px-2 py-1.5 font-medium">{_MATERIAL_LABEL[r.__mt] || r.__mt}</td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{shape || '—'}</div>
                        {code && <div className="text-[10px] text-slate-500">{code}</div>}
                      </td>
                      <td className="px-2 py-1.5">{r.size || '—'}</td>
                      <td className="px-2 py-1.5">{r.sieves || '—'}</td>
                      <td className="px-2 py-1.5 border-r border-slate-300">
                        <Link target="_blank" rel="noopener noreferrer" to={`/orders/${r.__order_id}`} className="font-mono text-[11px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-1.5 py-0.5 rounded hover:underline">
                          {r.__order_no}
                        </Link>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{rp}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-issued">{fp}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{bp}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatCarats(rc)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatCarats(fc)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-balance border-r border-slate-300">{formatCarats(bc)}</td>
                      {cols.remarks && <td className="px-2 py-1.5 text-slate-600 text-[11px]">{r.description || r.note || '—'}</td>}
                      <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                      {cols.age && <td className="px-2 py-1.5"><AgeCell status={r.status} days={r.days_since_requested} /></td>}
                      {cols.comments && <td className="px-2 py-1.5 text-center"><CommentsCell count={r.comments_count} /></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {golds.length > 0 && (
        <Card className="p-4">
          <div className="text-sm font-medium text-[var(--ink-navy)] mb-2">Gold Requirements <span className="text-slate-500 font-normal">(Consolidated)</span></div>
          <div className="overflow-x-auto ledger-table">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-[var(--ink-navy)] text-white">
                  <th className="text-left px-2 py-1.5 font-semibold">Purity</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Order</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Grams Req</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Metal Issued</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Balance (g)</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                  {cols.age && <th className="text-left px-2 py-1.5 font-semibold">Age</th>}
                  {cols.comments && <th className="text-center px-2 py-1.5 font-semibold">Cmts</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--divider)]">
                {golds.map((r) => {
                  const rg = Number(r.required_grams || 0);
                  const fg = Number(r.fulfilled_grams || 0);
                  const bg = Math.max(0, rg - fg);
                  return (
                    <tr key={`${r.__order_id}_${r.id}`}>
                      <td className="px-2 py-1.5 font-medium">{r.classification_label || (r.required_purity ? `${r.required_purity}%` : 'Gold')}</td>
                      <td className="px-2 py-1.5">
                        <Link target="_blank" rel="noopener noreferrer" to={`/orders/${r.__order_id}`} className="font-mono text-[11px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-1.5 py-0.5 rounded hover:underline">
                          {r.__order_no}
                        </Link>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatGrams(rg)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-issued">{formatGrams(fg)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums req-balance">{formatGrams(bg)}</td>
                      <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                      {cols.age && <td className="px-2 py-1.5"><AgeCell status={r.status} days={r.days_since_requested} /></td>}
                      {cols.comments && <td className="px-2 py-1.5 text-center"><CommentsCell count={r.comments_count} /></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {stones.length === 0 && golds.length === 0 && (
        <div className="text-slate-500 text-sm italic py-6 text-center">No requirements to display.</div>
      )}
    </div>
  );
}

function RequirementOrderRow({ order, statusFilter }) {
  const [expanded, setExpanded] = useState(false);
  const firstImage = (order.images || []).find((x) => typeof x === 'string' && x.startsWith('data:image')) || null;
  return (
    <div className="border border-[var(--divider)] rounded-md bg-white overflow-hidden" data-testid={`requirement-order-${order.order_no}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
        data-testid={`requirement-order-${order.order_no}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {firstImage ? (
            <img
              src={firstImage}
              alt={order.title || order.order_no}
              className="h-10 w-10 rounded object-cover border border-[var(--divider)]"
              data-testid={`requirement-order-${order.order_no}-thumb`}
            />
          ) : (
            <div className="h-10 w-10 rounded bg-slate-100 grid place-items-center text-slate-400">
              <Gem size={14} />
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/orders/${order.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline"
                data-testid={`requirement-order-${order.order_no}-link`}
              >
                {order.order_no}
              </Link>
              <span className="font-medium text-[var(--ink-navy)] truncate" title={order.title || ''}>
                {order.title || <span className="italic text-slate-400">Untitled jewellery</span>}
              </span>
              <StageBadge stage={order.stage} substage={order.substage} holdStatus={order.hold_status} />
              {order.jewellery_purity_name && (
                <Badge className="bg-[var(--gold-bg)] text-[var(--gold-icon)] text-[10px]">
                  <Coins size={9} className="mr-0.5" /> {order.jewellery_purity_name}
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {order.requirements_pending > 0 && (
                <span className="text-rose-700 mr-2" data-testid={`requirement-order-${order.order_no}-pending`}>
                  {order.requirements_pending} pending
                </span>
              )}
              {order.requirements_completed > 0 && (
                <span className="text-emerald-700" data-testid={`requirement-order-${order.order_no}-completed`}>
                  {order.requirements_completed} completed
                </span>
              )}
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronRight size={16} className="text-slate-500" />}
      </button>
      {expanded && (
        <div className="border-t border-[var(--divider)] bg-slate-50/40 px-3 py-2.5" data-testid={`requirement-order-${order.order_no}-details`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {REQ_GROUPS.map(({ key, label, icon: Icon, badgeCls }) => {
              const rows = order.groups?.[key] || [];
              if (rows.length === 0) return null;
              return (
                <div key={key} className="bg-white rounded border border-[var(--divider)] p-2.5" data-testid={`requirement-order-${order.order_no}-group-${key}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Badge className={`text-[10px] ${badgeCls}`}>
                      <Icon size={9} className="mr-0.5" /> {label}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{rows.length} item{rows.length === 1 ? '' : 's'}</span>
                  </div>
                  <ul className="space-y-1 text-xs">
                    {rows.map((r) => {
                      const isComplete = (r.status || '').toLowerCase() === 'complete' || (r.status || '').toLowerCase() === 'completed';
                      return (
                        <li key={r.id} className="flex items-start gap-2" data-testid={`requirement-order-${order.order_no}-${key}-${r.id}`}>
                          <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${isComplete ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-slate-700">
                              {r.classification_label}
                              {r.required_carats > 0 && <span className="text-slate-500"> · {formatCarats(r.required_carats)} ct</span>}
                              {r.required_pieces > 0 && <span className="text-slate-500"> · {r.required_pieces} pcs</span>}
                              {r.required_grams > 0 && (
                                <span className="text-slate-500"> · {formatGrams(r.required_grams)} g{r.required_purity ? ` (${r.required_purity})` : ''}</span>
                              )}
                            </div>
                            {!isComplete && (statusFilter === 'pending' || statusFilter === 'all') && (
                              <div className="text-[10px] text-rose-700">
                                Pending:
                                {r.remaining_carats > 0 && <span> {formatCarats(r.remaining_carats)} ct</span>}
                                {r.remaining_pieces > 0 && <span> {r.remaining_pieces} pcs</span>}
                                {r.remaining_grams > 0 && <span> {formatGrams(r.remaining_grams)} g</span>}
                              </div>
                            )}
                            {isComplete && (
                              <div className="text-[10px] text-emerald-700">Completed</div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function MaterialsWithGoldsmithSection({ balances, goldsmith, onAttribute, onAttributePool, onReturn, ledgerView }) {
  // Wave 3.41 — read the master Value Visibility permission.
  const { canViewValues, hasPermission } = useAuth();
  // Goldsmith Gold Ledger is RBAC-gated: only Super Admin / Company Admin, or a
  // user explicitly granted 'goldsmiths.gold_ledger.view', may open it.
  const canViewGoldLedger = hasPermission('goldsmiths.gold_ledger.view');
  // Wave 3.98 — Default to the "By Order" tab (operator preference —
  // most day-to-day reconciliation happens order-by-order).
  const [tab, setTab] = useState('order'); // 'material' | 'order'
  // Wave 3.19 — Print "Materials With Goldsmith" PDF (Material-wise OR Order-wise)
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfView, setPdfView] = useState('order'); // 'material' | 'order'
  const [pdfLoading, setPdfLoading] = useState(false);
  // Wave 3.21 — Toggle to include/exclude Rate & Amount columns in the PDF.
  // Default OFF so the printout is safe to share with the goldsmith without
  // disclosing valuations.
  const [includeRate, setIncludeRate] = useState(false);
  // Wave 3.38b — Display mode: 'expanded' = only per-memo individual rows,
  // 'collapsed' = only grouped/subtotal rows. Removes the legacy confusion
  // of mixing both in a single output.
  const [pdfDisplay, setPdfDisplay] = useState('expanded');

  // Wave 3.22 — Gold Ledger dialog (chronological view + branding-aware PDF
  // download). Opens via the new Gold Ledger button next to Generate PDF.
  const [goldLedgerOpen, setGoldLedgerOpen] = useState(false);

  // Gold purity view (999 ↔ 995) shared across the Gold Ledger popup, the
  // Raw Material (Unattributed) ledger gold section, and the summary pill so
  // the whole page reads in one consistent unit. Persisted to localStorage
  // (remembered across visits). See ``convertG999`` for the conversion rule.
  const [goldPurity, setGoldPurity] = useState(readGoldPurity);
  const changeGoldPurity = (v) => {
    const next = v === '995' ? '995' : '999';
    setGoldPurity(next);
    try { localStorage.setItem(GOLD_PURITY_KEY, next); } catch { /* noop */ }
  };

  // ── Filters for the "Materials With Goldsmith" section ──────────────────
  // Raw Material applies to every tab; Order No / Page No / Customer apply to
  // the By Material + By Order tabs only (the Unattributed Ledger has no order
  // attribution, so those three are hidden while it is active — per spec).
  const [rawMatFilter, setRawMatFilter] = useState([]);       // composite keys "${kind}:${id}"
  const [orderFilter, setOrderFilter] = useState([]);         // order_ids
  const [pageFilter, setPageFilter] = useState([]);           // reference_nos (numbers)
  const [customerFilter, setCustomerFilter] = useState([]);   // customer_ids

  const downloadMaterialsPdf = async () => {
    if (!goldsmith?.id) return;
    setPdfLoading(true);
    try {
      // Req — mirror the on-screen filters into the printout.
      const filterParams = {};
      if ((rawMatFilter || []).length) filterParams.raw_materials = rawMatFilter.join(',');
      if ((orderFilter || []).length) filterParams.order_ids = orderFilter.join(',');
      if ((pageFilter || []).length) filterParams.reference_nos = pageFilter.join(',');
      if ((customerFilter || []).length) filterParams.customer_ids = customerFilter.join(',');
      const filtered = Object.keys(filterParams).length > 0;
      const res = await api.get(
        `/goldsmith-issues/balance/by-goldsmith/${goldsmith.id}/pdf`,
        // Wave 3.41 — Force include_rate=false for users without values.view.
        // The backend also enforces this, but we redundantly enforce on the
        // client to avoid a wasted round-trip.
        { params: { view: pdfView, include_rate: canViewValues ? includeRate : false, display: pdfDisplay, ...filterParams }, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = pdfView === 'material' ? 'raw_material_wise' : 'order_form_wise';
      const rateSuffix = includeRate ? '' : '_no-rate';
      const displaySuffix = pdfDisplay === 'collapsed' ? '_collapsed' : '';
      const filteredSuffix = filtered ? '_filtered' : '';
      a.download = `materials_with_${(goldsmith.name || 'goldsmith').replace(/\s+/g, '_')}_${suffix}${rateSuffix}${displaySuffix}${filteredSuffix}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
      setPdfOpen(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate PDF'));
    } finally {
      setPdfLoading(false);
    }
  };

  if (!balances) {
    return (
      <Card className="p-5" data-testid="materials-section-loading">
        <div className="text-base font-medium text-[var(--ink-navy)] mb-1">Materials With Goldsmith</div>
        <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
      </Card>
    );
  }
  // Task B — Reference / Page-No display (company-gated).
  const refEnabled = !!balances?.uses_reference_numbers;
  const refLabel = balances?.reference_label || 'Page No.';
  // Phase 15.6 — summary value pills (provided by backend; safe fallback to 0).
  const unattrVal = Number(balances?.total_unattributed_value || 0);
  const attrVal = Number(balances?.total_attributed_value || 0);
  const totalVal = Number(balances?.total_value || (unattrVal + attrVal));

  // ── Build filter option lists from the current balance trees ────────────
  // (plain computations — this runs after the early return so no hooks here.)
  const rawMatOptions = [];
  const rawMatSeen = new Set();
  const rawMatLabelByKey = {};
  const addRawMat = (kind, id, label) => {
    if (id == null || !kind) return;
    const key = `${kind}:${id}`;
    if (rawMatSeen.has(key)) return;
    rawMatSeen.add(key);
    rawMatOptions.push({ key, kind, label: label || 'Material' });
    rawMatLabelByKey[key] = label || 'Material';
  };
  (balances.by_material || []).forEach((m) => addRawMat(m.kind, m.id, m.label));
  (balances.by_order || []).forEach((o) => (o.lines || []).forEach((l) => addRawMat(l.kind, l.id, l.label)));

  const orderOptions = [];
  const orderSeen = new Set();
  const orderLabelById = {};
  (balances.by_order || []).forEach((o) => {
    if (!o.order_id || orderSeen.has(o.order_id)) return;
    orderSeen.add(o.order_id);
    orderOptions.push({ id: o.order_id, order_no: o.order_no || 'Order', title: o.title || '' });
    orderLabelById[o.order_id] = o.order_no || 'Order';
  });

  const customerOptions = [];
  const customerSeen = new Set();
  const customerNameById = {};
  (balances.by_order || []).forEach((o) => {
    if (!o.customer_id || customerSeen.has(o.customer_id)) return;
    customerSeen.add(o.customer_id);
    customerOptions.push({ id: o.customer_id, name: o.customer_name || 'Unnamed' });
    customerNameById[o.customer_id] = o.customer_name || 'Unnamed';
  });

  const pageOptions = [];
  if (refEnabled) {
    const pageSeen = new Set();
    (balances.by_order || []).forEach((o) => {
      if (o.reference_no == null || pageSeen.has(o.reference_no)) return;
      pageSeen.add(o.reference_no);
      pageOptions.push(o.reference_no);
    });
  }

  // ── Active-filter matchers ──────────────────────────────────────────────
  const rawMatSet = new Set(rawMatFilter);
  const orderSet = new Set(orderFilter);
  const pageSet = new Set(pageFilter);
  const customerSet = new Set(customerFilter);
  const rawMatActive = rawMatSet.size > 0;
  const attrFilterActive = orderSet.size > 0 || pageSet.size > 0 || customerSet.size > 0;
  const anyFilterActive = rawMatActive || attrFilterActive;
  const showAttrControls = tab !== 'unattributed_ledger';

  const lineMatchesRawMat = (l) => !rawMatActive || rawMatSet.has(`${l.kind}:${l.id}`);
  // An order sub-bucket / bucket matches when it satisfies every active
  // order / page / customer selection.
  const orderMatches = (o) => {
    if (orderSet.size && !orderSet.has(o.order_id)) return false;
    if (customerSet.size && !customerSet.has(o.customer_id)) return false;
    if (pageSet.size && !pageSet.has(o.reference_no)) return false;
    return true;
  };

  // ── Live faceted counts ────────────────────────────────────────────────
  // Build a flat list of every balance line entry (attributed + unattributed)
  // and tally per option. Each dimension's count respects the OTHER active
  // filters (standard faceted-search behaviour) so the numbers stay "live".
  const filterEntries = [];
  (balances.by_order || []).forEach((o) => (o.lines || []).forEach((l) => filterEntries.push({
    matKey: `${l.kind}:${l.id}`,
    order_id: o.order_id || null,
    customer_id: o.customer_id || null,
    reference_no: o.reference_no,
  })));
  const matchExcept = (e, exclude) => {
    if (exclude !== 'raw' && rawMatSet.size && !rawMatSet.has(e.matKey)) return false;
    if (exclude !== 'order' && orderSet.size && !orderSet.has(e.order_id)) return false;
    if (exclude !== 'customer' && customerSet.size && !customerSet.has(e.customer_id)) return false;
    if (exclude !== 'page' && pageSet.size && !pageSet.has(e.reference_no)) return false;
    return true;
  };
  const rawMatCounts = new Map();
  const orderCounts = new Map();
  const customerCounts = new Map();
  const pageCounts = new Map();
  const bump = (map, key) => { if (key != null) map.set(key, (map.get(key) || 0) + 1); };
  filterEntries.forEach((e) => {
    if (matchExcept(e, 'raw')) bump(rawMatCounts, e.matKey);
    if (e.order_id && matchExcept(e, 'order')) bump(orderCounts, e.order_id);
    if (e.customer_id && matchExcept(e, 'customer')) bump(customerCounts, e.customer_id);
    if (e.reference_no != null && matchExcept(e, 'page')) bump(pageCounts, e.reference_no);
  });

  // ── By Order — bucket-level order/page/customer + line-level raw material ─
  const filteredByOrder = (balances.by_order || []).map((bucket) => {
    if (!bucket.order_id) {
      // The Unattributed bucket has no order/customer/page — hide it whenever
      // any of those three filters is active.
      if (attrFilterActive) return null;
      const lines = (bucket.lines || []).filter(lineMatchesRawMat);
      return lines.length ? { ...bucket, lines } : null;
    }
    if (!orderMatches(bucket)) return null;
    const lines = (bucket.lines || []).filter(lineMatchesRawMat);
    return lines.length ? { ...bucket, lines } : null;
  }).filter(Boolean);

  // ── By Material — raw material narrows the list; order/page/customer
  //    cascade into the order sub-buckets; unattributed pool is dropped when
  //    an order/page/customer filter is active; materials left with nothing
  //    are hidden entirely.
  const filteredByMaterial = (balances.by_material || []).map((mat) => {
    if (rawMatActive && !rawMatSet.has(`${mat.kind}:${mat.id}`)) return null;
    const orders = (mat.orders || []).filter((ord) => orderMatches(ord));
    const unattributed = attrFilterActive ? null : mat.unattributed;
    if (!unattributed && orders.length === 0) return null;
    return { ...mat, orders, unattributed };
  }).filter(Boolean);

  const hasMaterialFiltered = filteredByMaterial.length > 0;
  const hasOrderFiltered = filteredByOrder.some((o) => (o.lines || []).length > 0);

  const clearAllFilters = () => {
    setRawMatFilter([]); setOrderFilter([]); setPageFilter([]); setCustomerFilter([]);
  };

  return (
    <Card className="p-5" data-testid="materials-section">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Materials With Goldsmith</div>
          <div className="text-xs text-slate-500">Live balance trees — Unattributed vs Order-attributed material currently with {goldsmith?.name || 'this goldsmith'}.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Phase 15.6 — summary value pills (Unattributed + Attributed).
              Wave 3.41 — hidden entirely for users without ``values.view``. */}
          {canViewValues && (
            <>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-[11px] font-mono tabular-nums"
                title="Total value of material currently with this goldsmith but not yet attributed to a specific order"
                data-testid="materials-pill-unattributed-value"
              >
                <span className="text-[10px] uppercase tracking-wide font-sans">Unattributed</span>
                <span className="font-medium" data-testid="materials-pill-unattributed-amount">{formatINR(unattrVal)}</span>
              </span>
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px] font-mono tabular-nums"
                title="Total value of material attributed to Active or On-Hold orders (excludes Completed)"
                data-testid="materials-pill-attributed-value"
              >
                <span className="text-[10px] uppercase tracking-wide font-sans">Attributed</span>
                <span className="font-medium" data-testid="materials-pill-attributed-amount">{formatINR(attrVal)}</span>
              </span>
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-[11px] font-mono tabular-nums"
                title="Total value of all material currently with this goldsmith"
                data-testid="materials-pill-total-value"
              >
                <span className="text-[10px] uppercase tracking-wide font-sans">Total</span>
                <span className="font-medium" data-testid="materials-pill-total-amount">{formatINR(totalVal)}</span>
              </span>
            </>
          )}
          <div className="inline-flex rounded-md border border-[var(--divider)] overflow-hidden text-xs">
            <button
              onClick={() => setTab('material')}
              className={`px-3 py-1.5 ${tab === 'material' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="materials-tab-material"
            >By Material</button>
            <button
              onClick={() => setTab('order')}
              className={`px-3 py-1.5 ${tab === 'order' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="materials-tab-order"
            >By Order</button>
            <button
              onClick={() => setTab('unattributed_ledger')}
              className={`px-3 py-1.5 ${tab === 'unattributed_ledger' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="materials-tab-unattributed-ledger"
            >Unattributed Ledger</button>
          </div>
          {/* Wave 3.19 — Generate PDF (Materials With Goldsmith) */}
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => { setPdfView(tab === 'order' ? 'order' : 'material'); setPdfOpen(true); }}
            data-testid="materials-generate-pdf-button"
          >
            <Printer size={14} className="mr-1.5" /> Generate PDF
          </Button>
          {/* Wave 3.22 — Gold Ledger: chronological view of every gold
              movement with this goldsmith (issued, returned, consumed in
              jewellery, overflow→unattributed). Mirrors the printable PDF.
              RBAC-gated via goldsmiths.gold_ledger.view. */}
          {canViewGoldLedger && (
            <Button
              variant="outline"
              className="h-8 text-xs"
              onClick={() => setGoldLedgerOpen(true)}
              data-testid="gold-ledger-open-button"
            >
              <Coins size={14} className="mr-1.5" /> Gold Ledger
            </Button>
          )}
        </div>
      </div>

      {/* ── Filter toolbar ─────────────────────────────────────────────────
          Raw Material applies to every tab. Order No / Page No / Customer
          apply to By Material + By Order only (hidden on Unattributed Ledger
          which has no order attribution). */}
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="materials-filter-toolbar">
        <RawMaterialFilter options={rawMatOptions} value={rawMatFilter} onChange={setRawMatFilter} counts={rawMatCounts} />
        {showAttrControls && (
          <>
            <OrderFilter options={orderOptions} value={orderFilter} onChange={setOrderFilter} counts={orderCounts} />
            {refEnabled && (
              <ReferenceFilter options={pageOptions} value={pageFilter} onChange={setPageFilter} label={refLabel} counts={pageCounts} />
            )}
            <CustomerFilter options={customerOptions} value={customerFilter} onChange={setCustomerFilter} counts={customerCounts} />
          </>
        )}
        {anyFilterActive && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-[12px] text-slate-500 hover:text-slate-800 underline ml-1"
            data-testid="materials-filter-clear-all"
          >
            Clear all
          </button>
        )}
      </div>
      {(rawMatActive || (showAttrControls && attrFilterActive)) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="materials-filter-chips">
          <RawMaterialFilterChips value={rawMatFilter} labelByKey={rawMatLabelByKey} onChange={setRawMatFilter} />
          {showAttrControls && (
            <>
              <OrderFilterChips value={orderFilter} labelById={orderLabelById} onChange={setOrderFilter} />
              {refEnabled && (
                <ReferenceFilterChips value={pageFilter} onChange={setPageFilter} label={refLabel} />
              )}
              <CustomerFilterChips value={customerFilter} nameById={customerNameById} onChange={setCustomerFilter} />
            </>
          )}
        </div>
      )}

      {tab === 'material' && (
        <div className="mt-4 space-y-3" data-testid="by-material-tree">
          {!hasMaterialFiltered && (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md" data-testid="by-material-empty">
              {anyFilterActive ? 'No materials match the selected filters.' : 'No materials currently with this goldsmith.'}
            </div>
          )}
          {filteredByMaterial.map((m) => (
            <MaterialNode key={`${m.kind}-${m.id}`} mat={m} onAttribute={onAttribute} onAttributePool={onAttributePool} onReturn={onReturn} refEnabled={refEnabled} refLabel={refLabel} />
          ))}
        </div>
      )}
      {tab === 'order' && (
        <div className="mt-4 space-y-3" data-testid="by-order-tree">
          {!hasOrderFiltered && (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md" data-testid="by-order-empty">
              {anyFilterActive ? 'No orders match the selected filters.' : 'No order-attributed material currently with this goldsmith.'}
            </div>
          )}
          {filteredByOrder.map((o) => {
            if (!(o.lines || []).length) return null;
            return <OrderBucketNode key={o.order_id || 'unattributed'} bucket={o} onAttribute={onAttribute} onAttributePool={onAttributePool} onReturn={onReturn} refEnabled={refEnabled} refLabel={refLabel} />;
          })}
        </div>
      )}
      {tab === 'unattributed_ledger' && (
        <UnattributedLedgerTab
          goldsmithId={goldsmith?.id}
          goldsmithName={goldsmith?.name}
          purity={goldPurity}
          onPurityChange={changeGoldPurity}
          rawMatFilter={rawMatFilter}
        />
      )}

      {/* Wave 3.19 — PDF chooser dialog: "By Raw Material" or "By Order Form" */}
      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="max-w-lg" data-testid="materials-pdf-dialog">
          <DialogHeader>
            <DialogTitle>Generate PDF — Materials With Goldsmith</DialogTitle>
            <DialogDescription>
              Print a tally-friendly report of every raw-material balance currently sitting with {goldsmith?.name || 'this goldsmith'}, attributed or unattributed. Choose how the report is grouped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setPdfView('material')}
              className={`w-full text-left p-3 rounded-md border transition ${pdfView === 'material' ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)]/5' : 'border-[var(--divider)] hover:bg-slate-50'}`}
              data-testid="materials-pdf-view-material"
            >
              <div className="flex items-start gap-2">
                <Gem size={16} className="mt-0.5 text-[var(--ink-navy)]" />
                <div>
                  <div className="text-sm font-medium text-[var(--ink-navy)]">Raw Material wise</div>
                  <div className="text-xs text-slate-500">Group rows by packet / purity. Each material shows its Unattributed pool plus every Order Form to which a balance is attributed.</div>
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setPdfView('order')}
              className={`w-full text-left p-3 rounded-md border transition ${pdfView === 'order' ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)]/5' : 'border-[var(--divider)] hover:bg-slate-50'}`}
              data-testid="materials-pdf-view-order"
            >
              <div className="flex items-start gap-2">
                <FileText size={16} className="mt-0.5 text-[var(--ink-navy)]" />
                <div>
                  <div className="text-sm font-medium text-[var(--ink-navy)]">Order Form wise</div>
                  <div className="text-xs text-slate-500">Group rows by order. A leading <b>Unattributed Pool</b> section captures all uncovered material. Useful for goldsmith-side stock tally.</div>
                </div>
              </div>
            </button>
            <div className="text-[11px] text-slate-500 px-1 pt-1">
              Both views print the grand total of goods value with this goldsmith at the bottom.
            </div>
            {/* Wave 3.38b — Display mode toggle. The legacy output mixed
                grouped/subtotal rows with per-memo individual rows which
                was confusing. Now the user picks one or the other. */}
            <div
              className="mt-2 px-3 py-2.5 rounded-md border border-[var(--divider)] bg-slate-50/60"
              data-testid="materials-pdf-display-row"
            >
              <Label className="text-sm font-medium text-[var(--ink-navy)]">Display</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setPdfDisplay('expanded')}
                  className={`text-left p-2.5 rounded-md border transition ${pdfDisplay === 'expanded' ? 'border-emerald-500 bg-emerald-50' : 'border-[var(--divider)] hover:bg-white'}`}
                  data-testid="materials-pdf-display-expanded"
                >
                  <div className="text-sm font-medium text-[var(--ink-navy)]">Expanded</div>
                  <div className="text-[11px] text-slate-500">Show <b>only</b> the per-memo individual rows. Best for goldsmith-side tally against each issue memo.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setPdfDisplay('collapsed')}
                  className={`text-left p-2.5 rounded-md border transition ${pdfDisplay === 'collapsed' ? 'border-emerald-500 bg-emerald-50' : 'border-[var(--divider)] hover:bg-white'}`}
                  data-testid="materials-pdf-display-collapsed"
                >
                  <div className="text-sm font-medium text-[var(--ink-navy)]">Collapsed</div>
                  <div className="text-[11px] text-slate-500">Show <b>only</b> the grouped totals (per order or per packet). Best for a quick summary view.</div>
                </button>
              </div>
            </div>
            {/* Wave 3.21 — Include Rate & Amount toggle. Default OFF so the
                printout is safe to hand to the goldsmith without disclosing
                internal valuations. Toggle ON when you need a full financial
                tally for internal records.
                Wave 3.41 — Entirely hidden for users without ``values.view``
                (they can't see values anyway and the backend force-strips
                them from the PDF). */}
            {canViewValues && (
              <div
                className="mt-2 px-3 py-2.5 rounded-md border border-[var(--divider)] bg-slate-50/60 flex items-start gap-3"
                data-testid="materials-pdf-include-rate-row"
              >
                <Switch
                  id="materials-pdf-include-rate"
                  checked={includeRate}
                  onCheckedChange={setIncludeRate}
                  data-testid="materials-pdf-include-rate-switch"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <Label
                    htmlFor="materials-pdf-include-rate"
                    className="text-sm font-medium text-[var(--ink-navy)] cursor-pointer"
                  >
                    Include Rate & Amount
                  </Label>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {includeRate
                      ? 'Rate, Value columns, summary value pills, and the grand-total band will be printed.'
                      : 'Rate, Value columns, summary value pills, and the grand-total band will be hidden. Safe to share with the goldsmith.'}
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOpen(false)} disabled={pdfLoading}>Cancel</Button>
            <Button onClick={downloadMaterialsPdf} disabled={pdfLoading} className="luxury-button" data-testid="materials-pdf-download-button">
              {pdfLoading ? 'Preparing…' : (<><Download size={14} className="mr-1.5" /> Download PDF</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wave 3.22 — Gold Ledger dialog (RBAC-gated) */}
      {canViewGoldLedger && (
        <GoldLedgerDialog
          open={goldLedgerOpen}
          onOpenChange={setGoldLedgerOpen}
          goldsmithId={goldsmith?.id}
          goldsmithName={goldsmith?.name}
          purity={goldPurity}
          onPurityChange={changeGoldPurity}
          ledgerView={ledgerView}
        />
      )}
    </Card>
  );
}

function MaterialNode({ mat, onAttribute, onAttributePool, onReturn, refEnabled, refLabel }) {
  const [expanded, setExpanded] = useState(true);
  const isGold = mat.kind === 'gold';
  const isCarat = mat.kind === 'diamond' || mat.kind === 'color_stone' || mat.kind === 'pearl';
  const Icon = mat.kind === 'color_stone' ? Sparkles : mat.kind === 'pearl' ? Circle : isGold ? Coins : Gem;
  const iconColor = mat.kind === 'color_stone' ? 'text-[var(--colorstone-icon,#C84A6F)]'
                  : mat.kind === 'pearl' ? 'text-[var(--pearl-icon,#6B7280)]'
                  : isGold ? 'text-[var(--gold-icon)]'
                  : 'text-[var(--diamond-icon)]';
  const pillCls = mat.kind === 'color_stone' ? 'bg-[var(--colorstone-bg,#FCE7F3)] text-[var(--colorstone-icon,#C84A6F)]'
                : mat.kind === 'pearl' ? 'bg-slate-100 text-slate-700'
                : isGold ? 'bg-[var(--gold-bg)] text-[var(--gold-icon)]'
                : 'bg-[var(--diamond-bg)] text-[var(--diamond-icon)]';
  const totalLabel = (() => {
    if (isGold) {
      const g = (mat.unattributed?.grams || 0) + (mat.orders || []).reduce((s, o) => s + (o.lines || []).reduce((ss, l) => ss + (l.grams || 0), 0), 0);
      return `${formatGrams(g)} g`;
    }
    const ct = (mat.unattributed?.carats || 0) + (mat.orders || []).reduce((s, o) => s + (o.lines || []).reduce((ss, l) => ss + (l.carats || 0), 0), 0);
    const pcs = (mat.unattributed?.pieces || 0) + (mat.orders || []).reduce((s, o) => s + (o.lines || []).reduce((ss, l) => ss + (l.pieces || 0), 0), 0);
    return `${formatCarats(ct)} ct · ${pcs} pcs`;
  })();

  return (
    <div className="border border-[var(--divider)] rounded-md bg-white" data-testid={`material-node-${mat.kind}-${mat.id}`}>
      <div className="flex items-center px-3 py-2 gap-2">
        <button onClick={() => setExpanded((x) => !x)} className="text-slate-500 hover:text-slate-900" data-testid={`material-toggle-${mat.kind}-${mat.id}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <Icon size={14} className={iconColor} />
        <span className={`font-mono text-[12px] px-2 py-0.5 rounded ${pillCls}`}>{mat.label}</span>
        {mat.classification_schema && <span className="text-xs text-slate-500">{mat.classification_schema}</span>}
        <div className="flex-1" />
        <span className="text-xs font-mono text-slate-700">{totalLabel}</span>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {/* Unattributed pool — consolidated into a single row per
              classification (purity/packet). Provenance per source memo
              stays available in the Unattributed Ledger tab. */}
          {mat.unattributed && (
            <BucketBox
              title="Unattributed Pool"
              tone="slate"
              data-testid={`unattr-${mat.kind}-${mat.id}`}
              actions={null}
            >
              <UnattributedPoolRow
                kind={mat.kind}
                id={mat.id}
                label={mat.label}
                classificationSchema={mat.classification_schema}
                grams={mat.unattributed.grams}
                carats={mat.unattributed.carats}
                pieces={mat.unattributed.pieces}
                sourceCount={(mat.unattributed.lines || []).length}
                onAttributePool={onAttributePool}
                onReturn={onReturn}
              />
            </BucketBox>
          )}
          {/* Order buckets */}
          {(mat.orders || []).map((ord) => (
            <BucketBox
              key={ord.order_id}
              title={
                <span className="inline-flex items-center gap-2">
                  <Link target="_blank" rel="noopener noreferrer" to={`/orders/${ord.order_id}`} className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline">{ord.order_no || 'Order'}</Link>
                  {refEnabled && ord.reference_no != null && (
                    <ReferenceChip
                      referenceNo={ord.reference_no}
                      className="font-mono text-[11px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded"
                      title={`${refLabel} ${ord.reference_no} — open in Order Forms (new tab)`}
                      testId={`order-bucket-refchip-${mat.kind}-${mat.id}-${ord.order_id}`}
                    >
                      {refLabel} {ord.reference_no}
                    </ReferenceChip>
                  )}
                  <span className="text-xs text-slate-500">attributed</span>
                </span>
              }
              tone="violet"
              data-testid={`order-bucket-${mat.kind}-${mat.id}-${ord.order_id}`}
            >
              <BucketLines
                kind={mat.kind}
                isUnattributed={false}
                materialMeta={mat}
                lines={ord.lines || []}
                orderId={ord.order_id}
                onAttribute={onAttribute}
                onReturn={onReturn}
              />
            </BucketBox>
          ))}
        </div>
      )}
    </div>
  );
}

function BucketBox({ title, tone, children, actions, ...rest }) {
  const toneCls = tone === 'violet' ? 'bg-[var(--jewellery-bg)]/40 border-[var(--jewellery-border)]' : 'bg-slate-50 border-[var(--divider)]';
  return (
    <div className={`border rounded-md ${toneCls}`} {...rest}>
      <div className="flex items-center justify-between px-3 py-2 text-xs">
        <div className="text-slate-700 font-medium">{title}</div>
        {actions}
      </div>
      <div className="px-3 pb-3">{children}</div>
    </div>
  );
}

/**
 * Wave — Consolidated Unattributed Pool row. Renders ONE row for a whole
 * classification (gold purity OR carat packet), summing every underlying
 * source memo line. Attribute/Return act on the pooled total (the backend
 * allocates FIFO across the source memos). Per-source provenance remains
 * available in the Unattributed Ledger tab.
 */
function UnattributedPoolRow({ kind, id, label, classificationSchema, grams, carats, pieces, sourceCount, onAttributePool, onReturn }) {
  const isGold = kind === 'gold';
  const isCarat = kind === 'diamond' || kind === 'color_stone' || kind === 'pearl';
  const Icon = kind === 'color_stone' ? Sparkles : kind === 'pearl' ? Circle : isGold ? Coins : Gem;
  const iconColor = kind === 'color_stone' ? 'text-[var(--colorstone-icon,#C84A6F)]'
                  : kind === 'pearl' ? 'text-[var(--pearl-icon,#6B7280)]'
                  : isGold ? 'text-[var(--gold-icon)]'
                  : 'text-[var(--diamond-icon)]';
  const qty = isGold
    ? `${formatGrams(grams || 0)} g`
    : `${formatCarats(carats || 0)} ct · ${pieces || 0} pcs`;
  const poolMeta = isGold
    ? { material_type: 'gold', purity_id: id, purity_name: label, available: { grams: grams || 0 } }
    : { material_type: kind, packet_id: id, packet_no: label, classification_schema: classificationSchema, available: { carats: carats || 0, pieces: pieces || 0 } };
  const returnFilter = isGold
    ? { material: 'gold', purityId: id, unattributedOnly: true }
    : { material: kind, packetId: id, unattributedOnly: true };
  return (
    <div className="flex items-center gap-2 text-xs bg-white border border-[var(--divider)] rounded px-2 py-1.5" data-testid={`unattr-pool-line-${kind}-${id}`}>
      <Icon size={12} className={iconColor} />
      <span className="font-mono text-[11px]">{label}</span>
      {classificationSchema && <span className="text-slate-400 text-[10px]">{classificationSchema}</span>}
      <span className="text-slate-400">·</span>
      <span className="font-mono text-slate-700" data-testid={`unattr-pool-qty-${kind}-${id}`}>{qty}</span>
      {sourceCount > 1 && (
        <span className="text-slate-400 text-[10px] hidden sm:inline" title="See Unattributed Ledger for the source of each memo">· pooled from {sourceCount} memos</span>
      )}
      <div className="flex-1" />
      <button
        onClick={() => onAttributePool(poolMeta)}
        className="text-[11px] inline-flex items-center px-2 py-0.5 rounded border border-[var(--ink-navy)] text-[var(--ink-navy)] hover:bg-[var(--ink-navy)] hover:text-white transition-colors"
        data-testid={`unattr-pool-attribute-${kind}-${id}`}
      >
        <Link2 size={11} className="mr-1" /> Attribute
      </button>
      <button
        onClick={() => onReturn(returnFilter)}
        className="text-[11px] inline-flex items-center px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
        data-testid={`unattr-pool-return-${kind}-${id}`}
      >
        <ArrowDownToLine size={11} className="mr-1" /> Return
      </button>
    </div>
  );
}

function BucketLines({ kind, isUnattributed, materialMeta, lines, orderId, onAttribute, onReturn }) {
  if (!lines || lines.length === 0) {
    return <div className="text-xs text-slate-500">No lines</div>;
  }
  const isCarat = kind === 'diamond' || kind === 'color_stone' || kind === 'pearl';
  return (
    <div className="space-y-1">
      {lines.map((ln, i) => {
        const qty = isCarat
          ? `${formatCarats(ln.carats || 0)} ct · ${ln.pieces || 0} pcs`
          : `${formatGrams(ln.grams || 0)} g`;
        const lineMeta = isCarat
          ? {
              material_type: kind,
              packet_no: materialMeta.label,
              classification_schema: materialMeta.classification_schema,
              available: { carats: ln.carats || 0, pieces: ln.pieces || 0 },
            }
          : {
              material_type: 'gold',
              purity_name: materialMeta.label,
              purity_id: materialMeta.id,
              available: { grams: ln.grams || 0, grams_999: ln.grams_999 || 0 },
            };
        return (
          <div key={`${ln.line_id}-${i}`} className="flex items-center gap-2 text-xs bg-white border border-[var(--divider)] rounded px-2 py-1.5" data-testid={`bucket-line-${ln.line_id}`}>
            <span className="font-mono text-slate-700">{qty}</span>
            <span className="text-slate-400">·</span>
            <MemoLink id={ln.issue_id} memoNo={ln.issue_memo_no || ln.issue_no} />
            <span className="text-slate-400 text-[10px]">{formatDate(ln.issued_at)}</span>
            {ln.line_note && <span className="text-slate-400 text-[10px] truncate max-w-[200px]" title={ln.line_note}>· {ln.line_note}</span>}
            <div className="flex-1" />
            {isUnattributed && (
              <button
                onClick={() => onAttribute(ln.issue_id, ln.line_id, lineMeta, ln.issue_memo_no)}
                className="text-[11px] inline-flex items-center px-2 py-0.5 rounded border border-[var(--ink-navy)] text-[var(--ink-navy)] hover:bg-[var(--ink-navy)] hover:text-white transition-colors"
                data-testid={`bucket-attribute-${ln.line_id}`}
              >
                <Link2 size={11} className="mr-1" /> Attribute
              </button>
            )}
            <button
              onClick={() => onReturn({
                material: kind,
                packetId: isCarat ? materialMeta.id : undefined,
                purityId: kind === 'gold' ? materialMeta.id : undefined,
                orderId: isUnattributed ? undefined : orderId,
                lineId: ln.line_id,
              })}
              className="text-[11px] inline-flex items-center px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid={`bucket-return-${ln.line_id}`}
            >
              <ArrowDownToLine size={11} className="mr-1" /> Return
            </button>
          </div>
        );
      })}
    </div>
  );
}

function OrderBucketNode({ bucket, onAttribute, onAttributePool, onReturn, refEnabled, refLabel }) {
  const [expanded, setExpanded] = useState(true);
  const isUnattr = !bucket.order_id;
  // Wave 3.22 — Canonical material hierarchy: Gold → Diamonds → Colour Stones → Pearls.
  // The backend ordering of `bucket.lines` mirrors the insertion order of
  // goldsmith-issue lines, which is not predictable; we re-sort here so the
  // user always sees the same top-to-bottom flow.
  const MATERIAL_RANK = { gold: 0, diamond: 1, color_stone: 2, pearl: 3 };
  const sortedLines = useMemo(() => {
    const lines = bucket.lines || [];
    return [...lines].sort((a, b) => {
      const ra = MATERIAL_RANK[a.kind] ?? 99;
      const rb = MATERIAL_RANK[b.kind] ?? 99;
      if (ra !== rb) return ra - rb;
      // Tiebreaker — packet/purity label so the order is deterministic.
      const la = (a.label || '').toString();
      const lb = (b.label || '').toString();
      return la.localeCompare(lb);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket.lines]);
  // Wave — For the Unattributed bucket, club every source line by its
  // classification (kind + purity/packet id) into a single pooled row so
  // the operator acts on one head instead of one-row-per-source-memo.
  const unattrGroups = useMemo(() => {
    if (!isUnattr) return [];
    const map = new Map();
    for (const ln of bucket.lines || []) {
      const key = `${ln.kind}:${ln.id}`;
      if (!map.has(key)) {
        map.set(key, {
          kind: ln.kind, id: ln.id, label: ln.label,
          classification_schema: ln.classification_schema,
          grams: 0, carats: 0, pieces: 0, count: 0,
        });
      }
      const g = map.get(key);
      g.grams += ln.grams || 0;
      g.carats += ln.carats || 0;
      g.pieces += ln.pieces || 0;
      g.count += 1;
    }
    return Array.from(map.values()).sort((a, b) => {
      const ra = MATERIAL_RANK[a.kind] ?? 99;
      const rb = MATERIAL_RANK[b.kind] ?? 99;
      if (ra !== rb) return ra - rb;
      return (a.label || '').toString().localeCompare((b.label || '').toString());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnattr, bucket.lines]);
  return (
    <div className="border border-[var(--divider)] rounded-md bg-white" data-testid={`order-bucket-node-${bucket.order_id || 'unattributed'}`}>
      <div className="flex items-center px-3 py-2 gap-2">
        <button onClick={() => setExpanded((x) => !x)} className="text-slate-500 hover:text-slate-900">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        {/* Wave 3.98 — Order thumbnail next to the order number for
            quick visual identification. Only shown for real orders
            (not the "Unattributed" bucket) and only when the backend
            returned an image for the order. Uses ``object-cover`` at
            40×40 so jewellery photos fill the tile without letterbox
            bars — the operator taps it to open the full order page. */}
        {!isUnattr && bucket.image && (
          <Link
            to={`/orders/${bucket.order_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 block w-10 h-10 rounded-md overflow-hidden border border-[var(--divider)] bg-gradient-to-br from-slate-50 to-slate-100 hover:border-[var(--ink-navy)]/50 hover:shadow-sm transition-all"
            title={`Open ${bucket.order_no}`}
            data-testid={`order-bucket-thumb-${bucket.order_id}`}
          >
            <img
              src={bucket.image}
              alt={bucket.title || bucket.order_no || 'Order thumbnail'}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </Link>
        )}
        {isUnattr
          ? <Badge className="bg-slate-100 text-slate-700">Unattributed</Badge>
          : <Link target="_blank" rel="noopener noreferrer" to={`/orders/${bucket.order_id}`} className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline">{bucket.order_no}</Link>}
        {!isUnattr && refEnabled && bucket.reference_no != null && (
          <ReferenceChip
            referenceNo={bucket.reference_no}
            className="font-mono text-[11px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded"
            title={`${refLabel} ${bucket.reference_no} — open in Order Forms (new tab)`}
            testId={`order-bucket-refchip-${bucket.order_id}`}
          >
            {refLabel} {bucket.reference_no}
          </ReferenceChip>
        )}
        <span className="text-xs text-slate-600 truncate flex-1">{bucket.title}</span>
        <span className="text-[11px] text-slate-500">{isUnattr ? `${unattrGroups.length} pooled` : `${sortedLines.length} line(s)`}</span>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-1">
          {isUnattr ? (
            unattrGroups.map((grp) => (
              <UnattributedPoolRow
                key={`${grp.kind}-${grp.id}`}
                kind={grp.kind}
                id={grp.id}
                label={grp.label}
                classificationSchema={grp.classification_schema}
                grams={grp.grams}
                carats={grp.carats}
                pieces={grp.pieces}
                sourceCount={grp.count}
                onAttributePool={onAttributePool}
                onReturn={onReturn}
              />
            ))
          ) : (
            sortedLines.map((ln, i) => {
            const kind = ln.kind;
            const isGold = kind === 'gold';
            const isCarat = kind === 'diamond' || kind === 'color_stone' || kind === 'pearl';
            const Icon = kind === 'color_stone' ? Sparkles : kind === 'pearl' ? Circle : isGold ? Coins : Gem;
            const iconColor = kind === 'color_stone' ? 'text-[var(--colorstone-icon,#C84A6F)]'
                            : kind === 'pearl' ? 'text-[var(--pearl-icon,#6B7280)]'
                            : isGold ? 'text-[var(--gold-icon)]'
                            : 'text-[var(--diamond-icon)]';
            const qty = isGold ? `${formatGrams(ln.grams || 0)} g` : `${formatCarats(ln.carats || 0)} ct · ${ln.pieces || 0} pcs`;
            const lineMeta = isGold
              ? { material_type: 'gold', purity_name: ln.label, purity_id: ln.id, available: { grams: ln.grams || 0, grams_999: ln.grams_999 || 0 } }
              : { material_type: kind, packet_no: ln.label, classification_schema: ln.classification_schema, available: { carats: ln.carats || 0, pieces: ln.pieces || 0 } };
            return (
              <div key={`${ln.line_id}-${i}`} className="flex items-center gap-2 text-xs bg-white border border-[var(--divider)] rounded px-2 py-1.5">
                <Icon size={12} className={iconColor} />
                <span className="font-mono text-[11px]">{ln.label}</span>
                <span className="text-slate-400">·</span>
                <span className="font-mono text-slate-700">{qty}</span>
                <span className="text-slate-400">·</span>
                <MemoLink id={ln.issue_id} memoNo={ln.issue_memo_no || ln.issue_no} />
                <div className="flex-1" />
                {isUnattr && (
                  <button
                    onClick={() => onAttribute(ln.issue_id, ln.line_id, lineMeta, ln.issue_memo_no)}
                    className="text-[11px] inline-flex items-center px-2 py-0.5 rounded border border-[var(--ink-navy)] text-[var(--ink-navy)] hover:bg-[var(--ink-navy)] hover:text-white transition-colors"
                  >
                    <Link2 size={11} className="mr-1" /> Attribute
                  </button>
                )}
                <button
                  onClick={() => onReturn({
                    material: kind,
                    packetId: isCarat ? ln.id : undefined,
                    purityId: isGold ? ln.id : undefined,
                    orderId: isUnattr ? undefined : bucket.order_id,
                    lineId: ln.line_id,
                  })}
                  className="text-[11px] inline-flex items-center px-2 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <ArrowDownToLine size={11} className="mr-1" /> Return
                </button>
              </div>
            );
            })
          )}
        </div>
      )}
    </div>
  );
}

function OrderTreeNode({ order }) {
  const [expanded, setExpanded] = useState(false);
  const reqs = order.requirements || [];
  const done = reqs.filter((r) => r.status === 'complete').length;
  return (
    <div className="border border-[var(--divider)] rounded-md bg-white" data-testid={`order-tree-${order.id}`}>
      <div className="flex items-center px-3 py-2 gap-2">
        <button onClick={() => setExpanded((x) => !x)} className="text-slate-500 hover:text-slate-900" data-testid={`order-tree-toggle-${order.id}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <Link target="_blank" rel="noopener noreferrer" to={`/orders/${order.id}`} className="font-mono text-[12px] bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)] px-2 py-0.5 rounded hover:underline" data-testid={`order-tree-link-${order.id}`}>{order.order_no}</Link>
        <div className="flex-1 truncate text-sm">{order.title}</div>
        <StageBadge stage={order.stage} substage={order.substage} holdStatus={order.hold_status} />
        <div className="text-[11px] text-slate-500 hidden md:block">{reqs.length > 0 ? `${done}/${reqs.length} done` : '—'}</div>
        <Link target="_blank" rel="noopener noreferrer" to={`/orders/${order.id}`} className="text-xs text-[var(--ink-navy)] hover:underline inline-flex items-center" data-testid={`order-tree-open-${order.id}`}><ExternalLink size={11} className="mr-1" />Open</Link>
      </div>
      {expanded && (
        <div className="px-4 py-3 border-t border-[var(--divider)] bg-slate-50/50">
          {reqs.length === 0 ? (
            <div className="text-xs text-slate-500">No requirements added yet.</div>
          ) : (
            <ul className="space-y-1" data-testid={`order-tree-reqs-${order.id}`}>
              {reqs.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-xs">
                  {r.material_type === 'gold' ? <Coins size={11} className="text-[var(--gold-icon)]" /> : <Gem size={11} className="text-[var(--diamond-icon)]" />}
                  <span className="text-slate-700">{r.classification_label || r.description || 'Requirement'}</span>
                  <span className="text-slate-500">
                    {r.material_type === 'gold'
                      ? `${formatGrams(r.fulfilled_grams || 0)}/${formatGrams(r.required_grams || 0)}g`
                      : `${formatCarats(r.fulfilled_carats || 0)}/${formatCarats(r.required_carats || 0)}ct · ${r.fulfilled_pieces || 0}/${r.required_pieces || 0}pcs`}
                  </span>
                  <Badge className={`text-[9px] ${r.status === 'complete' ? 'bg-emerald-50 text-emerald-700' : r.status === 'partial' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{r.status}</Badge>
                </li>
              ))}
            </ul>
          )}
          {order.hold_status === 'on_hold' && order.hold_reason && (
            <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 px-2 py-1 rounded">On hold: {order.hold_reason}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Goldsmith Issue Dialog (multi-row form)
// ============================================================================
const blankLine = () => ({
  material_type: 'diamond',
  packet_id: '',
  purity_id: '',
  carats: '',
  pieces: '',
  grams: '',
  order_id: '',
  order_no: '',
  requirement_id: '',
  mark_requirement_complete: false,
  note: '',
});

function GoldsmithIssueDialog({ open, onOpenChange, companyId, goldsmith, onSuccess }) {
  // Phase 16 — multi-material packet pools (carat-based materials only)
  const [packetsByMat, setPacketsByMat] = useState({ diamond: [], color_stone: [], pearl: [] });
  const [purities, setPurities] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([blankLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [reqsByOrder, setReqsByOrder] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [d, cs, pr, pur, o] = await Promise.all([
          api.get('/diamonds/packets', { params: { company_id: companyId } }).catch(() => ({ data: [] })),
          api.get('/color-stones/packets', { params: { company_id: companyId } }).catch(() => ({ data: [] })),
          api.get('/pearls/packets', { params: { company_id: companyId } }).catch(() => ({ data: [] })),
          api.get('/gold/purities', { params: { company_id: companyId } }).catch(() => ({ data: [] })),
          api.get(`/orders/by-goldsmith/${goldsmith.id}`, { params: { status: 'active' } }).catch(() => ({ data: [] })),
        ]);
        const hasStock = (x) => (x.balance_carats > 0 || x.balance_pieces > 0);
        setPacketsByMat({
          diamond: (d.data || []).filter(hasStock),
          color_stone: (cs.data || []).filter(hasStock),
          pearl: (pr.data || []).filter(hasStock),
        });
        setPurities(pur.data || []);
        setOrders(o.data || []);
      } catch (e) { toast.error(extractError(e, 'Failed to load metadata')); }
    })();
    // eslint-disable-next-line
  }, [open]);

  const ensureReqsFor = async (oid) => {
    if (!oid || reqsByOrder[oid]) return;
    try {
      const { data } = await api.get(`/orders/${oid}`);
      const openReqs = (data.requirements || []).filter((r) => r.status !== 'complete');
      setReqsByOrder((p) => ({ ...p, [oid]: openReqs }));
    } catch { /* ignore */ }
  };

  const updateLine = (idx, patch) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  // Phase 16 — carat-material check kept inline (avoid extra import).
  const isCaratMat = (mt) => mt === 'diamond' || mt === 'color_stone' || mt === 'pearl';

  const totalEstimated = useMemo(() => {
    let total = 0;
    lines.forEach((l) => {
      if (isCaratMat(l.material_type)) {
        const pool = packetsByMat[l.material_type] || [];
        const p = pool.find((x) => x.id === l.packet_id);
        if (p) total += (Number(l.carats) || 0) * (p.current_rate_per_ct || 0);
      } else if (l.material_type === 'gold') {
        const pur = purities.find((x) => x.id === l.purity_id);
        // approximate value using 999 conversion + last known rate (we'll let server compute final)
        if (pur) {
          const g999 = (Number(l.grams) || 0) * (pur.percent / 100);
          total += g999 * 10000; // placeholder — final value computed server-side
        }
      }
    });
    return total;
  }, [lines, packetsByMat, purities]);

  const submit = async () => {
    // Validate
    const cleanLines = [];
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (isCaratMat(l.material_type)) {
        const pool = packetsByMat[l.material_type] || [];
        if (!l.packet_id) { toast.error(`Row ${i + 1}: select a packet`); return; }
        if (!Number(l.carats) && !Number(l.pieces)) { toast.error(`Row ${i + 1}: enter carats or pieces`); return; }
        const p = pool.find((x) => x.id === l.packet_id);
        if (Number(l.carats) > (p?.balance_carats || 0) + 1e-9) { toast.error(`Row ${i + 1}: exceeds balance carats`); return; }
        if (Number(l.pieces) > (p?.balance_pieces || 0)) { toast.error(`Row ${i + 1}: exceeds balance pieces`); return; }
        cleanLines.push({
          material_type: l.material_type, packet_id: l.packet_id,
          carats: Number(l.carats) || 0, pieces: Number(l.pieces) || 0,
          order_id: l.order_id || null, order_no: l.order_no || null,
          requirement_id: l.requirement_id || null,
          mark_requirement_complete: !!l.mark_requirement_complete,
          note: l.note || '',
        });
      } else if (l.material_type === 'gold') {
        if (!l.purity_id) { toast.error(`Row ${i + 1}: select a purity`); return; }
        if (!Number(l.grams)) { toast.error(`Row ${i + 1}: enter grams`); return; }
        cleanLines.push({
          material_type: 'gold', purity_id: l.purity_id, grams: Number(l.grams),
          order_id: l.order_id || null, order_no: l.order_no || null,
          requirement_id: l.requirement_id || null,
          mark_requirement_complete: !!l.mark_requirement_complete,
          note: l.note || '',
        });
      } else {
        toast.error(`Row ${i + 1}: unsupported material.`);
        return;
      }
    }
    if (cleanLines.length === 0) { toast.error('Add at least one line'); return; }

    setSubmitting(true);
    try {
      const payload = {
        goldsmith_id: goldsmith.id, company_id: companyId,
        reference, note, lines: cleanLines,
      };
      const { data } = await api.post('/goldsmith-issues/', payload);
      toast.success(`Issue ${data.issue_memo_no || data.issue_no} created`);
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(extractError(e, 'Issue failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Issue to {goldsmith.name}</DialogTitle>
          <DialogDescription>Issue Diamonds, Colour Stones, Pearls, or Gold. Each row may link to a separate order and requirement.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Reference (optional)</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Slip / chit no" data-testid="gi-reference-input" />
          </div>
          <div>
            <Label>Header Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Applies to all lines" data-testid="gi-note-input" />
          </div>
        </div>

        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="gi-lines">
          {lines.map((l, idx) => (
            <IssueLineRow
              key={idx} idx={idx} line={l}
              onChange={(patch) => updateLine(idx, patch)}
              onRemove={() => removeLine(idx)}
              canRemove={lines.length > 1}
              packetsByMat={packetsByMat} purities={purities} orders={orders}
              reqsByOrder={reqsByOrder}
              ensureReqsFor={ensureReqsFor}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-3">
          <Button variant="outline" onClick={addLine} data-testid="gi-add-line"><Plus size={14} className="mr-1.5" /> Add Row</Button>
          <div className="text-sm text-slate-600">{lines.length} row(s) · est. {formatINR(totalEstimated)}</div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="luxury-button" data-testid="gi-submit-button">
            {submitting ? 'Submitting…' : 'Submit Issue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueLineRow({ idx, line, onChange, onRemove, canRemove, packetsByMat, purities, orders, reqsByOrder, ensureReqsFor }) {
  useEffect(() => { if (line.order_id) ensureReqsFor(line.order_id); /* eslint-disable-next-line */ }, [line.order_id]);
  const isCaratMat = line.material_type === 'diamond' || line.material_type === 'color_stone' || line.material_type === 'pearl';
  // Requirements: gold ↔ gold; carat-based materials only match their own type (or untyped legacy).
  const filteredReqs = (reqsByOrder[line.order_id] || []).filter((r) => {
    if (line.material_type === 'gold') return r.material_type === 'gold';
    return (r.material_type === line.material_type) || (!r.material_type && line.material_type === 'diamond');
  });
  const currentPool = isCaratMat ? (packetsByMat[line.material_type] || []) : [];
  const sourceLabel = line.material_type === 'color_stone' ? 'Colour Stone Packet'
                    : line.material_type === 'pearl' ? 'Pearl Packet'
                    : 'Diamond Packet';

  return (
    <div className="border border-[var(--divider)] rounded-md p-3 bg-slate-50/40" data-testid={`gi-line-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-slate-500 font-medium">Row {idx + 1}</div>
        {canRemove && (
          <button onClick={onRemove} className="text-xs text-rose-600 hover:underline inline-flex items-center" data-testid={`gi-remove-line-${idx}`}>
            <Trash2 size={11} className="mr-1" /> Remove
          </button>
        )}
      </div>
      <div className="grid grid-cols-12 gap-2">
        {/* Material */}
        <div className="col-span-12 md:col-span-2">
          <Label className="text-[11px]">Material</Label>
          <Select value={line.material_type} onValueChange={(v) => onChange({ material_type: v, packet_id: '', purity_id: '' })}>
            <SelectTrigger className="h-9" data-testid={`gi-line-${idx}-material`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="diamond">Diamond</SelectItem>
              <SelectItem value="color_stone">Colour Stone</SelectItem>
              <SelectItem value="pearl">Pearl</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Source */}
        {isCaratMat && (
          <div className="col-span-12 md:col-span-3">
            <Label className="text-[11px]">{sourceLabel}</Label>
            <SearchableSelect
              items={currentPool}
              value={line.packet_id}
              onValueChange={(v) => onChange({ packet_id: v || '' })}
              itemToLabel={(p) => `${p.packet_no} · ${p.balance_carats}ct/${p.balance_pieces}pcs`}
              itemToSubLabel={(p) => p.classification_shape || p.classification_schema || null}
              searchKeys={['packet_no', 'classification_shape', 'classification_schema', 'description']}
              placeholder={currentPool.length === 0 ? 'No stock' : 'Choose packet'}
              searchPlaceholder="Search packet no / shape…"
              disabled={currentPool.length === 0}
              testId={`gi-line-${idx}-packet`}
            />
          </div>
        )}
        {line.material_type === 'gold' && (
          <div className="col-span-12 md:col-span-3">
            <Label className="text-[11px]">Purity</Label>
            <SearchableSelect
              items={purities}
              value={line.purity_id}
              onValueChange={(v) => onChange({ purity_id: v || '' })}
              itemToLabel={(p) => `${p.name} (${p.percent}%)`}
              searchKeys={['name']}
              placeholder={purities.length === 0 ? 'No purities defined' : 'Choose purity'}
              searchPlaceholder="Search purity…"
              disabled={purities.length === 0}
              testId={`gi-line-${idx}-purity`}
            />
          </div>
        )}

        {/* Quantities */}
        {isCaratMat ? (
          <>
            <div className="col-span-6 md:col-span-2">
              <Label className="text-[11px]">Carats</Label>
              <Input className="h-9" type="number" step="0.01" value={line.carats} onChange={(e) => onChange({ carats: e.target.value })} data-testid={`gi-line-${idx}-carats`} />
            </div>
            <div className="col-span-6 md:col-span-1">
              <Label className="text-[11px]">Pcs</Label>
              <Input className="h-9" type="number" step="1" value={line.pieces} onChange={(e) => onChange({ pieces: e.target.value })} data-testid={`gi-line-${idx}-pieces`} />
            </div>
          </>
        ) : (
          <div className="col-span-12 md:col-span-3">
            <Label className="text-[11px]">Grams</Label>
            <Input className="h-9" type="number" step="0.001" value={line.grams} onChange={(e) => onChange({ grams: e.target.value })} data-testid={`gi-line-${idx}-grams`} />
          </div>
        )}

        {/* Order */}
        <div className="col-span-6 md:col-span-2">
          <Label className="text-[11px]">Order</Label>
          <SearchableSelect
            items={[{ id: 'none', order_no: 'No order' }, ...orders]}
            value={line.order_id || 'none'}
            onValueChange={(v) => {
              const ord = !v || v === 'none' ? null : orders.find((o) => o.id === v);
              onChange({ order_id: ord?.id || '', order_no: ord?.order_no || '', requirement_id: '' });
            }}
            itemToLabel={(o) => o.order_no}
            itemToSubLabel={(o) => (o.id === 'none' ? null : o.title || null)}
            searchKeys={['order_no', 'title']}
            placeholder="No order"
            searchPlaceholder="Search order no…"
            testId={`gi-line-${idx}-order`}
          />
        </div>

        {/* Requirement */}
        <div className="col-span-6 md:col-span-2">
          <Label className="text-[11px]">Requirement</Label>
          {(() => {
            const reqOptions = filteredReqs.map((r) => {
              // Wave 3.68 — For non-gold, use the shared Shape - Size -
              // Sieves formatter.  Gold keeps its grams-aware label.
              const isGold = r.material_type === 'gold';
              if (!isGold) {
                return { id: r.id, label: formatRequirementDropdownLabel(r) };
              }
              const label = r.classification_label || r.description || 'Requirement';
              const remG = Math.max(0, Number(r.required_grams || 0) - Number(r.fulfilled_grams || 0));
              const remBits = [];
              if (remG > 0) remBits.push(`${formatGrams(remG)} g${r.required_purity ? ` (${r.required_purity})` : ''}`);
              const remTxt = remBits.length ? ` · pending ${remBits.join(' / ')}` : ' · pending none';
              return { id: r.id, label: `${label}${remTxt}` };
            });
            const items = [{ id: 'none', label: 'None' }, ...reqOptions];
            return (
              <SearchableSelect
                items={items}
                value={line.requirement_id || 'none'}
                onValueChange={(v) => onChange({ requirement_id: !v || v === 'none' ? '' : v })}
                itemToValue={(it) => it.id}
                itemToLabel={(it) => it.label}
                searchKeys={['label']}
                placeholder={line.order_id ? 'None' : '—'}
                searchPlaceholder="Search requirement…"
                disabled={!line.order_id}
                testId={`gi-line-${idx}-req`}
              />
            );
          })()}
        </div>

        {/* Note */}
        <div className="col-span-12">
          <Label className="text-[11px]">Line Note (optional)</Label>
          <Input className="h-9" value={line.note} onChange={(e) => onChange({ note: e.target.value })} data-testid={`gi-line-${idx}-note`} />
        </div>

        {line.requirement_id && (
          <label className="col-span-12 flex items-center gap-2 text-xs text-slate-700">
            <Checkbox checked={!!line.mark_requirement_complete} onCheckedChange={(v) => onChange({ mark_requirement_complete: !!v })} data-testid={`gi-line-${idx}-mark-complete`} />
            <span>Mark linked requirement as fully met after this issue</span>
          </label>
        )}
      </div>
    </div>
  );
}


// ===========================================================================
// Wave 3.16 Phase B — Unattributed Ledger tab
// ===========================================================================

// Recompute a chronological ledger's running balance after rows have been
// filtered (e.g. by the Raw Material filter). Mirrors the backend
// ``_finalize_rows`` so the Balance column + closing totals stay consistent
// once some rows are hidden. Works in the canonical 999 basis (display code
// converts to the chosen purity separately).
function recomputeUnattrRows(rows) {
  const r3 = (n) => Math.round((n + Number.EPSILON) * 1000) / 1000;
  const ordered = [...(rows || [])].sort((a, b) => {
    const ta = new Date(a.timestamp).getTime() || 0;
    const tb = new Date(b.timestamp).getTime() || 0;
    return ta - tb;
  });
  let g = 0, ct = 0, pcs = 0;
  const out = ordered.map((r) => {
    g = r3(g + (r.in_grams_999 || 0) - (r.out_grams_999 || 0));
    ct = r3(ct + (r.in_carats || 0) - (r.out_carats || 0));
    pcs = Math.max(0, pcs + (r.in_pieces || 0) - (r.out_pieces || 0));
    return { ...r, balance_grams_999: g, balance_carats: ct, balance_pieces: pcs };
  });
  return { rows: out, closing_grams_999: g, closing_carats: ct, closing_pieces: pcs };
}

// Apply the Raw Material filter (composite keys "${kind}:${id}") to the
// unattributed-ledger payload. Each ledger classification key (``cls_key``)
// equals the packet_id / purity_id used by the balance trees, so the same
// selection maps directly. Returns the original data untouched when no filter
// is active.
function filterUnattrData(data, rawMatKeys) {
  if (!data || !Array.isArray(rawMatKeys) || rawMatKeys.length === 0) return data;
  const sel = new Set(rawMatKeys);
  const sections = (data.sections || []).map((sec) => {
    const mt = sec.material_type;
    const keep = (clsKey) => sel.has(`${mt}:${clsKey}`);
    const recomputed = recomputeUnattrRows((sec.rows || []).filter((r) => keep(r.cls_key)));
    const groups = (sec.groups || []).filter((g) => keep(g.cls_key));
    return {
      ...sec,
      rows: recomputed.rows,
      groups,
      closing_grams_999: recomputed.closing_grams_999,
      closing_carats: recomputed.closing_carats,
      closing_pieces: recomputed.closing_pieces,
    };
  });
  return { ...data, sections };
}

function UnattributedLedgerTab({ goldsmithId, goldsmithName, purity = '999', onPurityChange, rawMatFilter = [] }) {
  const { effectiveCompanyId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfOpen, setPdfOpen] = useState(false);
  // Task C — ledger view mode: 'flat' (chronological) or 'grouped'
  // (one sub-ledger per raw-material classification: purity / packet).
  const [groupMode, setGroupMode] = useState('flat');
  const unit = purityUnitLabel(purity);

  useEffect(() => {
    if (!goldsmithId || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/goldsmiths/${goldsmithId}/unattributed-ledger`, {
          params: { company_id: effectiveCompanyId },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        toast.error(extractError(e, 'Failed to load unattributed ledger'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [goldsmithId, effectiveCompanyId]);

  if (loading || !data) {
    return (
      <div className="mt-4 text-sm text-slate-500 py-8 text-center border border-dashed rounded-md" data-testid="unattr-ledger-loading">
        Loading unattributed ledger…
      </div>
    );
  }

  const viewData = filterUnattrData(data, rawMatFilter);
  const filterActive = Array.isArray(rawMatFilter) && rawMatFilter.length > 0;
  const sections = viewData.sections || [];
  const summary = {
    gold: sections.find((s) => s.material_type === 'gold'),
    diamond: sections.find((s) => s.material_type === 'diamond'),
    color_stone: sections.find((s) => s.material_type === 'color_stone'),
    pearl: sections.find((s) => s.material_type === 'pearl'),
  };
  // Wave 3.23 Hot-Fix-3 — Show ALL non-empty material sections stacked
  // vertically (Gold → Diamonds → Colour Stones → Pearls). Empty sections
  // are hidden so the panel stays tidy. The pills above act as a quick
  // jump-to anchor instead of tab switchers.
  const MATERIAL_ORDER = ['gold', 'diamond', 'color_stone', 'pearl'];
  const nonEmptySections = MATERIAL_ORDER
    .map((mt) => sections.find((s) => s.material_type === mt))
    .filter((s) => s && ((s.rows || []).length > 0
      || (s.closing_grams_999 || 0) > 0
      || (s.closing_carats || 0) > 0
      || (s.closing_pieces || 0) > 0));

  return (
    <div className="mt-4 space-y-4" data-testid="unattributed-ledger-tab">
      {/* Summary pills + purity toggle + Print PDF */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <SummaryPill
            label={`Gold ${unit}`}
            icon={<Coins size={12} />}
            value={`${formatGrams(convertG999(summary.gold?.closing_grams_999 || 0, purity))} g`}
            tone="amber"
            active={(summary.gold?.closing_grams_999 || 0) > 0}
            data-testid="unattr-ledger-pill-gold"
          />
          <SummaryPill
            label="Diamonds"
            icon={<Gem size={12} />}
            value={`${formatCarats(summary.diamond?.closing_carats || 0)} ct · ${summary.diamond?.closing_pieces || 0} pcs`}
            tone="sky"
            active={(summary.diamond?.closing_carats || 0) > 0 || (summary.diamond?.closing_pieces || 0) > 0}
            data-testid="unattr-ledger-pill-diamond"
          />
          <SummaryPill
            label="Colour Stones"
            icon={<Sparkles size={12} />}
            value={`${formatCarats(summary.color_stone?.closing_carats || 0)} ct · ${summary.color_stone?.closing_pieces || 0} pcs`}
            tone="rose"
            active={(summary.color_stone?.closing_carats || 0) > 0 || (summary.color_stone?.closing_pieces || 0) > 0}
            data-testid="unattr-ledger-pill-color_stone"
          />
          <SummaryPill
            label="Pearls"
            icon={<Circle size={12} />}
            value={`${formatCarats(summary.pearl?.closing_carats || 0)} ct · ${summary.pearl?.closing_pieces || 0} pcs`}
            tone="slate"
            active={(summary.pearl?.closing_carats || 0) > 0 || (summary.pearl?.closing_pieces || 0) > 0}
            data-testid="unattr-ledger-pill-pearl"
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Task C — view mode: Chronological vs By Classification */}
          <div className="inline-flex rounded-md border border-[var(--divider)] overflow-hidden text-xs">
            <button
              onClick={() => setGroupMode('flat')}
              className={`px-3 py-1.5 ${groupMode === 'flat' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="unattr-ledger-view-chrono"
            >Chronological</button>
            <button
              onClick={() => setGroupMode('grouped')}
              className={`px-3 py-1.5 ${groupMode === 'grouped' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="unattr-ledger-view-grouped"
            >By Classification</button>
          </div>
          {onPurityChange && (
            <GoldPurityToggle purity={purity} onChange={onPurityChange} testId="unattr-ledger-purity-toggle" />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPdfOpen(true)}
            className="h-9"
            data-testid="unattr-ledger-print-button"
          >
            <Printer size={14} className="mr-1.5" /> Print Ledger
          </Button>
        </div>
      </div>

      {/* Stack every non-empty material ledger section vertically so Gold +
          Diamond + CS + Pearl entries are all visible at once. */}
      {nonEmptySections.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed rounded-md" data-testid="unattr-ledger-empty">
          {filterActive ? 'No unattributed material matches the selected raw material filter.' : 'No unattributed material currently with this goldsmith.'}
        </div>
      ) : (
        <div className="space-y-6" data-testid="unattr-ledger-sections">
          {nonEmptySections.map((sec) => (
            <div key={sec.material_type} data-testid={`unattr-ledger-section-${sec.material_type}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-navy)] mb-2 flex items-center gap-2">
                {sec.material_type === 'gold' && <Coins size={14} className="text-amber-600" />}
                {sec.material_type === 'diamond' && <Gem size={14} className="text-sky-600" />}
                {sec.material_type === 'color_stone' && <Sparkles size={14} className="text-rose-600" />}
                {sec.material_type === 'pearl' && <Circle size={14} className="text-slate-600" />}
                {sec.material_type === 'gold' ? `Gold (${unit})` : sec.label}
              </div>
              {groupMode === 'grouped' ? (
                <div className="space-y-3" data-testid={`unattr-ledger-groups-${sec.material_type}`}>
                  {(sec.groups || []).length === 0 ? (
                    <div className="text-sm text-slate-500 py-6 text-center border border-dashed rounded-md">No movements recorded for this material yet.</div>
                  ) : (
                    (sec.groups || []).map((g) => {
                      const gIsGold = sec.material_type === 'gold';
                      const closingLabel = gIsGold
                        ? `${formatGrams(convertG999(g.closing_grams_999 || 0, purity))} g ${unit}`
                        : `${formatCarats(g.closing_carats || 0)} ct · ${g.closing_pieces || 0} pcs`;
                      return (
                        <div key={g.cls_key} data-testid={`unattr-ledger-group-${sec.material_type}-${g.cls_key}`}>
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{g.cls_label}</span>
                            {g.cls_schema && <span className="text-[10px] text-slate-400">{g.cls_schema}</span>}
                            <span className="text-slate-300">·</span>
                            <span className="text-[11px] font-mono text-slate-600">Closing: {closingLabel}</span>
                          </div>
                          <UnattributedLedgerTable
                            section={{
                              material_type: sec.material_type,
                              rows: g.rows,
                              closing_grams_999: g.closing_grams_999,
                              closing_carats: g.closing_carats,
                              closing_pieces: g.closing_pieces,
                            }}
                            purity={purity}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <UnattributedLedgerTable section={sec} purity={purity} />
              )}
            </div>
          ))}
        </div>
      )}

      <PdfDownloadDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title={`Unattributed Ledger — ${goldsmithName}`}
        description={`Landscape PDF with separate sections per material (Receipts/Issued/Balance). Gold shown in ${unit} basis.`}
        companyId={effectiveCompanyId}
        endpoint={`/goldsmiths/${goldsmithId}/unattributed-ledger/pdf`}
        extraParams={{ purity, ...(filterActive ? { raw_materials: (rawMatFilter || []).join(',') } : {}) }}
        filename={`Unattributed_Ledger_${(goldsmithName || 'goldsmith').replace(/\s+/g, '_')}_${unit}${filterActive ? '_filtered' : ''}.pdf`}
        mode="download"
      />
    </div>
  );
}

function SummaryPill({ label, icon, value, tone, active, onClick, ...rest }) {
  const toneCls = {
    amber: active ? 'border-amber-400 bg-amber-100 text-amber-900' : 'border-amber-200 bg-amber-50 text-amber-800',
    sky: active ? 'border-sky-400 bg-sky-100 text-sky-900' : 'border-sky-200 bg-sky-50 text-sky-800',
    rose: active ? 'border-rose-400 bg-rose-100 text-rose-900' : 'border-rose-200 bg-rose-50 text-rose-800',
    slate: active ? 'border-slate-400 bg-slate-100 text-slate-900' : 'border-slate-200 bg-slate-50 text-slate-700',
  }[tone] || '';
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-mono transition-colors hover:opacity-95 ${toneCls}`}
    >
      <span className="inline-flex items-center gap-1 font-sans uppercase tracking-wide text-[10px]">{icon}{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </button>
  );
}

function UnattributedLedgerTable({ section, purity = '999' }) {
  if (!section) return null;
  const isGold = section.material_type === 'gold';
  const rows = section.rows || [];
  const gLabel = `g ${purityUnitLabel(purity)}`;
  if (rows.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-md" data-testid={`unattr-ledger-empty-${section.material_type}`}>
        No movements recorded for this material yet.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-[var(--divider)] rounded-md" data-testid={`unattr-ledger-table-${section.material_type}`}>
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="bg-slate-100 text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 align-bottom" rowSpan={2}>Date</th>
            <th className="bg-slate-100 text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 align-bottom" rowSpan={2}>Particulars</th>
            <th className="bg-slate-100 text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 align-bottom" rowSpan={2}>Reference</th>
            <th className="bg-red-50 text-red-700 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-center border-l border-red-100" colSpan={isGold ? 1 : 2}>Receipts</th>
            <th className="bg-blue-50 text-blue-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-center border-l border-blue-100" colSpan={isGold ? 1 : 2}>Issued</th>
            <th className="bg-amber-50 text-amber-800 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-center border-l border-amber-100" colSpan={isGold ? 1 : 2}>Balance</th>
          </tr>
          <tr>
            {isGold ? (
              <>
                <th className="bg-red-50 text-red-700 px-3 py-1.5 text-[11px] font-semibold text-right border-l border-red-100">{gLabel}</th>
                <th className="bg-blue-50 text-blue-800 px-3 py-1.5 text-[11px] font-semibold text-right border-l border-blue-100">{gLabel}</th>
                <th className="bg-amber-50 text-amber-800 px-3 py-1.5 text-[11px] font-semibold text-right border-l border-amber-100">{gLabel}</th>
              </>
            ) : (
              <>
                <th className="bg-red-50 text-red-700 px-3 py-1.5 text-[11px] font-semibold text-right border-l border-red-100">Carats</th>
                <th className="bg-red-50 text-red-700 px-3 py-1.5 text-[11px] font-semibold text-right">Pcs</th>
                <th className="bg-blue-50 text-blue-800 px-3 py-1.5 text-[11px] font-semibold text-right border-l border-blue-100">Carats</th>
                <th className="bg-blue-50 text-blue-800 px-3 py-1.5 text-[11px] font-semibold text-right">Pcs</th>
                <th className="bg-amber-50 text-amber-800 px-3 py-1.5 text-[11px] font-semibold text-right border-l border-amber-100">Carats</th>
                <th className="bg-amber-50 text-amber-800 px-3 py-1.5 text-[11px] font-semibold text-right">Pcs</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[var(--divider)] hover:bg-slate-50" data-testid={`unattr-ledger-row-${i}`}>
              <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">{formatDate(r.timestamp)}</td>
              <td className="px-3 py-2 text-xs text-slate-800">{r.particulars}</td>
              <td className="px-3 py-2 text-xs text-slate-600 font-mono">{r.reference || '—'}</td>
              {isGold ? (
                <>
                  <td className={`px-3 py-2 text-xs text-right font-mono border-l border-red-100 ${r.in_grams_999 ? 'text-red-700' : 'text-slate-300'}`}>
                    {r.in_grams_999 ? formatGrams(convertG999(r.in_grams_999, purity)) : '—'}
                  </td>
                  <td className={`px-3 py-2 text-xs text-right font-mono border-l border-blue-100 ${r.out_grams_999 ? 'text-blue-700' : 'text-slate-300'}`}>
                    {r.out_grams_999 ? formatGrams(convertG999(r.out_grams_999, purity)) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-right font-mono border-l border-amber-100 text-amber-800 font-medium">
                    {formatGrams(convertG999(r.balance_grams_999, purity))}
                  </td>
                </>
              ) : (
                <>
                  <td className={`px-3 py-2 text-xs text-right font-mono border-l border-red-100 ${r.in_carats ? 'text-red-700' : 'text-slate-300'}`}>
                    {r.in_carats ? formatCarats(r.in_carats) : '—'}
                  </td>
                  <td className={`px-3 py-2 text-xs text-right font-mono ${r.in_pieces ? 'text-red-700' : 'text-slate-300'}`}>
                    {r.in_pieces || '—'}
                  </td>
                  <td className={`px-3 py-2 text-xs text-right font-mono border-l border-blue-100 ${r.out_carats ? 'text-blue-700' : 'text-slate-300'}`}>
                    {r.out_carats ? formatCarats(r.out_carats) : '—'}
                  </td>
                  <td className={`px-3 py-2 text-xs text-right font-mono ${r.out_pieces ? 'text-blue-700' : 'text-slate-300'}`}>
                    {r.out_pieces || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-right font-mono border-l border-amber-100 text-amber-800 font-medium">
                    {formatCarats(r.balance_carats)}
                  </td>
                  <td className="px-3 py-2 text-xs text-right font-mono text-amber-800 font-medium">
                    {r.balance_pieces}
                  </td>
                </>
              )}
            </tr>
          ))}
          {/* Closing balance row */}
          <tr className="bg-amber-50 border-t-2 border-amber-200">
            <td className="px-3 py-2"></td>
            <td className="px-3 py-2 text-xs font-semibold text-amber-900">Closing Balance</td>
            <td className="px-3 py-2"></td>
            {isGold ? (
              <>
                <td></td><td></td>
                <td className="px-3 py-2 text-xs text-right font-mono text-amber-900 font-semibold">{formatGrams(convertG999(section.closing_grams_999, purity))}</td>
              </>
            ) : (
              <>
                <td></td><td></td><td></td><td></td>
                <td className="px-3 py-2 text-xs text-right font-mono text-amber-900 font-semibold">{formatCarats(section.closing_carats)}</td>
                <td className="px-3 py-2 text-xs text-right font-mono text-amber-900 font-semibold">{section.closing_pieces}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Wave 3.22 — Gold Ledger Dialog
// ----------------------------------------------------------------------------
// A self-contained dialog that fetches the chronological gold movements for
// this goldsmith (issued in, returned out, consumed in jewellery, plus
// zero-net overflow→unattributed shifts). Renders the rows in an
// at-a-glance table and exposes a download-PDF flow with the standard
// branding toggle.
// ============================================================================
function GoldLedgerDialog({ open, onOpenChange, goldsmithId, goldsmithName, purity = '999', onPurityChange, ledgerView }) {
  const { effectiveCompanyId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  useEffect(() => {
    if (!open || !goldsmithId || !effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/goldsmiths/${goldsmithId}/gold-ledger`, {
          params: { company_id: effectiveCompanyId },
        });
        if (!cancelled) setData(res.data);
      } catch (e) {
        toast.error(extractError(e, 'Failed to load gold ledger'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, goldsmithId, effectiveCompanyId]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const col = purityColLabel(purity);
  const unit = purityUnitLabel(purity);

  // Apply the shared date-range + sort view. Rows arrive chronologically with
  // a running ``balance_g999``; we filter to the window and flip order for
  // display, surfacing the pre-range balance as an Opening Balance.
  const { display: displayRows, openingBalance, showOpening, hasRange } = applyLedgerView(rows, {
    from: ledgerView?.from,
    to: ledgerView?.to,
    sortDir: ledgerView?.sortDir || 'asc',
    balanceKey: 'balance_g999',
  });
  const openingRow = showOpening ? (
    <tr className="bg-amber-50/60 border-t border-[var(--divider)]" data-testid="gold-ledger-opening-row">
      <td className="px-3 py-2 text-xs text-slate-600 font-mono whitespace-nowrap">{ledgerView?.from ? formatDate(`${ledgerView.from}T00:00:00`) : ''}</td>
      <td className="px-3 py-2" colSpan={6}>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">Opening Balance</span>
        <span className="text-[11px] text-slate-500 ml-2">(carried into {ledgerView?.from ? formatDate(`${ledgerView.from}T00:00:00`) : 'range'})</span>
      </td>
      <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-amber-900 font-semibold" data-testid="gold-ledger-opening-balance">
        {formatGrams(convertG999(openingBalance, purity))}
      </td>
      <td className="px-3 py-2"></td>
    </tr>
  ) : null;

  // Compose the Particulars text for a row honouring the selected purity.
  // For consumption rows that settled a gold loss "in gold", the backend
  // returns a clean ``particulars_base`` plus the structured 999 figures so
  // we can regenerate the "Actual X + Gold loss Y = Total Z (basis)" tail in
  // the chosen unit — keeping it in perfect sync with the Consumed column.
  const displayParticulars = (r) => {
    const base = r.particulars_base || r.particulars || '';
    if (
      r.kind === 'consumption_out' &&
      r.actual_consumption_g999 != null &&
      r.gold_loss_g999 != null &&
      r.consumed_total_g999 != null
    ) {
      const a = convertG999(r.actual_consumption_g999, purity);
      const l = convertG999(r.gold_loss_g999, purity);
      const t = convertG999(r.consumed_total_g999, purity);
      return `${base} · Actual ${a.toFixed(3)} g + Gold loss ${l.toFixed(3)} g = ${t.toFixed(3)} g (${unit})`;
    }
    return r.particulars || base;
  };

  const kindBadge = (kind) => {
    if (kind === 'issue_in') return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Issued</Badge>;
    if (kind === 'return_out') return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Returned</Badge>;
    if (kind === 'consumption_out') return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Consumed</Badge>;
    if (kind === 'overflow_shift') return <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Overflow</Badge>;
    return null;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="!max-w-6xl w-[95vw] max-h-[88vh] overflow-hidden flex flex-col"
          data-testid="gold-ledger-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins size={18} className="text-[var(--gold-icon)]" />
              Gold Ledger — {goldsmithName || 'Goldsmith'}
            </DialogTitle>
            <DialogDescription>
              Chronological log of every gold movement with this goldsmith. Quantities are
              shown in <span className="font-mono">{unit} grams</span> ({purity === '995' ? '99.5%' : '100%'}).
              Overflow rows mark gold that has been reclassified from an order into the goldsmith's
              unattributed pool — they do not change the running balance.
            </DialogDescription>
          </DialogHeader>

          {/* Purity view toggle — switches the whole ledger between Gold 999
              and Gold 995. Choice is shared with the Raw Material ledger and
              remembered across visits. */}
          <div className="flex items-center justify-between gap-3 flex-wrap pb-1">
            <div className="text-[11px] text-slate-500">
              Viewing gold in <span className="font-medium text-amber-700">Gold {unit}</span> basis
            </div>
            {onPurityChange && (
              <GoldPurityToggle purity={purity} onChange={onPurityChange} testId="gold-ledger-purity-toggle" />
            )}
          </div>

          {/* Date range + sort controls (shared with the Labour Ledger). */}
          {ledgerView && (
            <div className="pb-2 border-b border-[var(--divider)]">
              <LedgerViewControls view={ledgerView} testId="gold-ledger-view-controls" />
            </div>
          )}

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2 pb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-800 text-[11px] font-mono tabular-nums">
              <span className="text-[10px] uppercase tracking-wide font-sans">Issued In</span>
              <span className="font-medium" data-testid="gold-ledger-total-issued">
                {formatGrams(convertG999(totals.issued_in_g999 || 0, purity))} g
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-[11px] font-mono tabular-nums">
              <span className="text-[10px] uppercase tracking-wide font-sans">Returned Out</span>
              <span className="font-medium" data-testid="gold-ledger-total-returned">
                {formatGrams(convertG999(totals.returned_out_g999 || 0, purity))} g
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-orange-200 bg-orange-50 text-orange-800 text-[11px] font-mono tabular-nums">
              <span className="text-[10px] uppercase tracking-wide font-sans">Consumed</span>
              <span className="font-medium" data-testid="gold-ledger-total-consumed">
                {formatGrams(convertG999(totals.consumed_out_g999 || 0, purity))} g
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-300 bg-amber-100 text-amber-900 text-[11px] font-mono tabular-nums">
              <span className="text-[10px] uppercase tracking-wide font-sans">Closing Balance</span>
              <span className="font-bold" data-testid="gold-ledger-closing-balance">
                {formatGrams(convertG999(totals.closing_g999 || 0, purity))} g
              </span>
            </span>
            {(totals.overflow_to_unattributed_g999 || 0) > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 text-[11px] font-mono tabular-nums">
                <span className="text-[10px] uppercase tracking-wide font-sans">Overflow → Unattributed</span>
                <span className="font-medium" data-testid="gold-ledger-total-overflow">
                  {formatGrams(convertG999(totals.overflow_to_unattributed_g999 || 0, purity))} g
                </span>
              </span>
            )}
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-auto border border-[var(--divider)] rounded-md">
            {loading ? (
              <div className="text-sm text-slate-500 py-10 text-center" data-testid="gold-ledger-loading">
                Loading gold ledger…
              </div>
            ) : rows.length === 0 ? (
              <div className="text-sm text-slate-500 py-10 text-center" data-testid="gold-ledger-empty">
                No gold movements recorded for this goldsmith yet.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="gold-ledger-table">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr>
                    <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Date</th>
                    <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Type</th>
                    <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Particulars</th>
                    <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Purity</th>
                    <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50">Issued ({col})</th>
                    <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50">Returned ({col})</th>
                    <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-orange-700 bg-orange-50">Consumed ({col})</th>
                    <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50">Balance ({col})</th>
                    <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Attribution</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerView?.sortDir !== 'desc' && openingRow}
                  {displayRows.map((r, idx) => {
                    const isOverflow = r.kind === 'overflow_shift';
                    return (
                      <tr
                        key={idx}
                        className={`border-t border-[var(--divider)] ${isOverflow ? 'bg-slate-50/50' : 'hover:bg-slate-50/40'}`}
                        data-testid={`gold-ledger-row-${idx}`}
                      >
                        <td className="px-3 py-2 text-xs text-slate-700 font-mono whitespace-nowrap">{formatDate(r.timestamp)}</td>
                        <td className="px-3 py-2">{kindBadge(r.kind)}</td>
                        <td className="px-3 py-2 text-xs text-slate-700">{displayParticulars(r)}</td>
                        <td className="px-3 py-2 text-xs text-slate-700 font-mono whitespace-nowrap">
                          {r.purity_name ? `${r.purity_name}${r.purity_percent ? ` (${Number(r.purity_percent).toFixed(2)}%)` : ''}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-blue-700">
                          {r.kind === 'issue_in'
                            ? formatGrams(convertG999(r.in_grams_999 || 0, purity))
                            : (isOverflow && (r.shift_grams_999 || 0) > 0 ? <span className="text-slate-400">+{formatGrams(convertG999(r.shift_grams_999, purity))}*</span> : '')}
                        </td>
                        <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-rose-700">
                          {r.kind === 'return_out' ? formatGrams(convertG999(r.out_grams_999 || 0, purity)) : ''}
                        </td>
                        <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-orange-700">
                          {r.kind === 'consumption_out' ? formatGrams(convertG999(r.out_grams_999 || 0, purity)) : ''}
                        </td>
                        <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-amber-900 font-semibold">
                          {formatGrams(convertG999(r.balance_g999 || 0, purity))}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 font-mono">{r.attribution || '—'}</td>
                      </tr>
                    );
                  })}
                  {ledgerView?.sortDir === 'desc' && openingRow}
                  {hasRange && displayRows.length === 0 && (
                    <tr data-testid="gold-ledger-range-empty">
                      <td colSpan={9} className="px-3 py-6 text-center text-xs text-slate-500">
                        No gold movements in the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Footnote */}
          <div className="text-[11px] text-slate-500 px-1 pt-1">
            <span className="font-mono text-slate-400">*</span> Overflow rows mark gold reclassified
            from an order to the goldsmith's unattributed pool during a Receive Jewellery receipt.
            The running balance is unchanged.
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button
              onClick={() => setPdfOpen(true)}
              className="luxury-button"
              disabled={loading || rows.length === 0}
              data-testid="gold-ledger-pdf-button"
            >
              <Printer size={14} className="mr-1.5" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Branding-toggle PDF download, identical pattern to other ledger PDFs.
          ``extraParams.purity`` makes the printed PDF match the on-screen
          toggle (999 vs 995). */}
      <PdfDownloadDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title={`Gold Ledger — ${goldsmithName || 'Goldsmith'}`}
        description={`A4 landscape PDF (Gold ${unit} basis) with date, type, purity, issued/returned/consumed/balance columns.`}
        companyId={effectiveCompanyId}
        endpoint={`/goldsmiths/${goldsmithId}/gold-ledger/pdf`}
        extraParams={{ purity, sort: ledgerView?.sortDir || 'asc', date_from: ledgerView?.from || '', date_to: ledgerView?.to || '' }}
        filename={`Gold_Ledger_${(goldsmithName || 'goldsmith').replace(/\s+/g, '_')}_${unit}.pdf`}
        mode="download"
      />
    </>
  );
}


// ============================================================================
// Wave 3.30 — Labour Ledger section (gated by goldsmiths.amount_ledger.view permission)
// ----------------------------------------------------------------------------
// Mirrors the Vendor Amount Ledger pattern. Shows running credit (owed to
// goldsmith) vs payments (already paid) with a "Record Payment" CTA that
// opens a payment dialog. Read-only roles (manager / staff / accountant)
// do not see this card at all. Each row also exposes a "Void" action for
// payments (restricted to the same allowed roles).
// ============================================================================
function LabourLedgerSection({ goldsmith, ledgerView }) {
  const { user, hasPermission, effectiveCompanyId } = useAuth();
  // Wave 3.30 — Access gated by goldsmiths.amount_ledger.view permission.
  // Super Admin always has access (hasPermission returns true for SA).
  const allowed = hasPermission('goldsmiths.amount_ledger.view');
  // Wave 4.10 — Write access can now be delegated via the
  // ``goldsmiths.amount_ledger.manage`` permission (checked against the raw
  // grant so Company Admin's blanket bypass does not implicitly unlock
  // Super-Admin-governed PAYMENT recording). Super Admin always has both.
  const hasLabourManage = (user?.permissions || []).includes('goldsmiths.amount_ledger.manage');
  // Payments (record / void): Super Admin, or a user granted the labour
  // ledger manage permission.
  const canWrite = user?.role === 'super_admin' || hasLabourManage;
  // Charges (create / delete): SA + CA, or a user granted the manage permission.
  const canWriteCharges = user?.role === 'super_admin' || user?.role === 'company_admin' || hasLabourManage;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  // Wave 3.99i — Record Charges dialog state.
  const [chargeOpen, setChargeOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  // Wave 3.36 — Per-goldsmith Pending Bills dialog state.
  const [pendingDlgOpen, setPendingDlgOpen] = useState(false);
  const [pendingCutoff, setPendingCutoff] = useState('');
  const [pendingBusy, setPendingBusy] = useState(false);

  const reload = async () => {
    if (!goldsmith?.id || !effectiveCompanyId) return;
    setLoading(true);
    try {
      const res = await api.get(`/goldsmiths/${goldsmith.id}/labour-ledger`, {
        params: { company_id: effectiveCompanyId },
      });
      setData(res.data);
    } catch (e) {
      // 403 is expected for non-admin users; swallow without toasting.
      if (e?.response?.status !== 403) {
        toast.error(extractError(e, 'Failed to load labour ledger'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goldsmith?.id, effectiveCompanyId, allowed]);

  // Wave 3.36 — Download per-goldsmith Pending Bills PDF for chosen cutoff.
  const downloadPendingBills = async () => {
    if (!pendingCutoff) {
      toast.error('Please pick a cutoff date.');
      return;
    }
    setPendingBusy(true);
    try {
      const params = { company_id: effectiveCompanyId, due_on_or_before: pendingCutoff };
      const res = await api.get(`/goldsmiths/${goldsmith.id}/pending-bills/pdf`, {
        params, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (goldsmith?.name || 'goldsmith').replace(/\s+/g, '_').replace(/\//g, '-');
      a.download = `pending_bills_${safe}_${pendingCutoff}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Pending bills report downloaded');
      setPendingDlgOpen(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate report'));
    } finally {
      setPendingBusy(false);
    }
  };

  if (!allowed) return null;

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  // Shared date-range + sort view. Rows arrive chronologically with a running
  // ``running_balance``; filter to the window, flip order for display, and
  // surface the pre-range balance as the Opening Balance. Header totals stay
  // full-ledger (per user spec) — only the row list is filtered.
  const { display: displayRows, openingBalance, showOpening, hasRange } = applyLedgerView(rows, {
    from: ledgerView?.from,
    to: ledgerView?.to,
    sortDir: ledgerView?.sortDir || 'asc',
    balanceKey: 'running_balance',
  });
  const labourOpeningRow = showOpening ? (
    <tr className="bg-amber-50/60 border-t border-[var(--divider)]" data-testid="labour-ledger-opening-row">
      <td className="px-3 py-2 text-xs text-slate-600 font-mono whitespace-nowrap">{ledgerView?.from ? formatDate(`${ledgerView.from}T00:00:00`) : ''}</td>
      <td className="px-3 py-2" colSpan={8}>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">Opening Balance</span>
        <span className="text-[11px] text-slate-500 ml-2">(carried into {ledgerView?.from ? formatDate(`${ledgerView.from}T00:00:00`) : 'range'})</span>
      </td>
      <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-amber-900 font-semibold" data-testid="labour-ledger-opening-balance">
        {formatINR(openingBalance)}
      </td>
      <td className="px-3 py-2"></td>
    </tr>
  ) : null;

  const voidPayment = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Void this labour payment of ${formatINR(row.amount)}? This cannot be undone.`)) return;
    try {
      await api.delete(`/goldsmiths/${goldsmith.id}/labour-payments/${row.id}`, {
        params: { company_id: effectiveCompanyId },
      });
      toast.success('Payment voided');
      await reload();
    } catch (e) {
      toast.error(extractError(e, 'Failed to void payment'));
    }
  };

  // Wave 3.99i — Delete a manual labour charge. SA + CA only.
  const deleteCharge = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Delete this labour charge of ${formatINR(row.amount)}? The goldsmith's payable balance will be reduced accordingly.`)) return;
    try {
      await api.delete(`/goldsmiths/${goldsmith.id}/labour-charges/${row.id}`, {
        params: { company_id: effectiveCompanyId },
      });
      toast.success('Charge deleted');
      await reload();
    } catch (e) {
      toast.error(extractError(e, 'Failed to delete charge'));
    }
  };

  return (
    <Card className="p-5" data-testid="labour-ledger-section">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Labour Ledger</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Labour due for received jewellery vs. payments recorded. A positive closing balance
            means the company still owes the goldsmith.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Wave 3.36 — Pending Bills (per goldsmith) */}
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => { setPendingCutoff(''); setPendingDlgOpen(true); }}
            disabled={loading}
            data-testid="goldsmith-pending-bills-button"
          >
            <Receipt size={14} className="mr-1.5" /> Pending Bills
          </Button>
          <Button
            variant="outline"
            className="h-8 text-xs"
            onClick={() => setPdfOpen(true)}
            disabled={loading || rows.length === 0}
            data-testid="labour-ledger-pdf-button"
          >
            <Printer size={14} className="mr-1.5" /> PDF
          </Button>
          {canWriteCharges && (
            <Button
              variant="outline"
              className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
              onClick={() => setChargeOpen(true)}
              data-testid="labour-ledger-record-charges-button"
            >
              <Plus size={14} className="mr-1.5" /> Record Charges
            </Button>
          )}
          {canWrite && (
            <Button
              className="h-8 text-xs luxury-button"
              onClick={() => setPayOpen(true)}
              data-testid="labour-ledger-record-payment-button"
            >
              <Plus size={14} className="mr-1.5" /> Record Payment
            </Button>
          )}
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-800 text-[11px] font-mono tabular-nums">
          <span className="text-[10px] uppercase tracking-wide font-sans">Total Credits</span>
          <span className="font-medium" data-testid="labour-ledger-total-credit">
            {formatINR(totals.total_credit_inr || 0)}
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-rose-200 bg-rose-50 text-rose-800 text-[11px] font-mono tabular-nums">
          <span className="text-[10px] uppercase tracking-wide font-sans">Total Paid</span>
          <span className="font-medium" data-testid="labour-ledger-total-debit">
            {formatINR(totals.total_debit_inr || 0)}
          </span>
        </span>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-mono tabular-nums border ${
            (totals.closing_balance_inr || 0) > 0
              ? 'border-amber-300 bg-amber-100 text-amber-900'
              : 'border-emerald-300 bg-emerald-100 text-emerald-900'
          }`}
        >
          <span className="text-[10px] uppercase tracking-wide font-sans">
            {(totals.closing_balance_inr || 0) > 0 ? 'Owed to Goldsmith' : 'Settled'}
          </span>
          <span className="font-bold" data-testid="labour-ledger-closing-balance">
            {formatINR(Math.abs(totals.closing_balance_inr || 0))}
          </span>
        </span>
      </div>

      {/* Date range + sort controls (shared with the Gold Ledger). */}
      {ledgerView && (
        <div className="mb-3">
          <LedgerViewControls view={ledgerView} testId="labour-ledger-view-controls" />
        </div>
      )}

      {/* Table */}
      <div className="border border-[var(--divider)] rounded-md overflow-x-auto">
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center" data-testid="labour-ledger-loading">
            Loading labour ledger…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center" data-testid="labour-ledger-empty">
            No labour movements yet. Credits are auto-posted when a jewellery is received.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="labour-ledger-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Date</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Type</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Particulars</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Reference</th>
                {/* Wave 3.36 — Bill due-date for credit (labour-due) rows. */}
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">Due Date</th>
                <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50">Credit</th>
                <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50">Debit</th>
                {/* Wave 3.36 — Paid / Outstanding per labour-credit bill. */}
                <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50">Paid</th>
                <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50">Outstanding</th>
                <th className="text-right px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50">Balance</th>
                <th className="text-center px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {ledgerView?.sortDir !== 'desc' && labourOpeningRow}
              {displayRows.map((r) => {
                const isCredit = r.direction === 'credit';
                // Wave 3.99i — Distinguish manual charges from
                // auto-posted labour_credit rows so the UI can show an
                // amber "Charges" chip + a delete affordance (for
                // SA/CA) alongside the standard Credit / Payment chips.
                const isCharge = r.entry_type === 'labour_charge';
                const ref = r.ref || {};
                const refDisplay = isCharge
                  ? (ref.reference || '—')
                  : isCredit
                  ? (ref.jewellery_no || '—')
                  : (ref.payment_mode
                      ? `${ref.payment_mode.toUpperCase()}${ref.payment_reference ? ` · ${ref.payment_reference}` : ''}`
                      : (ref.payment_reference || '—'));
                // Wave 3.36 — Compute paid / outstanding / overdue flags for
                // credit (labour_credit) rows. The backend enriches each
                // row with paid_amount + outstanding_amount when applicable.
                const billAmount = Number(r.amount || 0);
                const paid = isCredit ? Number(r.paid_amount || 0) : 0;
                const outstanding = isCredit
                  ? Number(r.outstanding_amount ?? Math.max(0, billAmount - paid))
                  : 0;
                // IST today (UTC+5:30) date-only ISO string.
                const todayIso = new Date(new Date().getTime() + (5.5 * 3600 * 1000))
                  .toISOString().slice(0, 10);
                const isOverdue = isCredit && r.due_date && r.due_date < todayIso && outstanding > 0.005;
                const outstandingTone = !isCredit
                  ? 'text-slate-400'
                  : outstanding <= 0.001
                  ? 'text-emerald-700'
                  : paid > 0
                  ? 'text-amber-700'
                  : 'text-rose-700';
                return (
                  <tr
                    key={r.id}
                    className={`border-t border-[var(--divider)] hover:bg-slate-50/40 ${isOverdue ? 'bg-rose-50 text-red-600 font-bold' : ''}`}
                    data-testid={`labour-ledger-row-${r.id}`}
                  >
                    <td className="px-3 py-2 text-xs text-slate-700 font-mono whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                    <td className="px-3 py-2">
                      {isCharge ? (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200" data-testid={`labour-ledger-charge-chip-${r.id}`}>
                          Charges
                        </Badge>
                      ) : (
                        <Badge className={isCredit ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' : 'bg-rose-100 text-rose-700 hover:bg-rose-100'}>
                          {isCredit ? 'Credit' : 'Payment'}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {isCredit && !isCharge && ref.jewellery_id ? (
                        <Link to={`/jewellery/${ref.jewellery_id}`} className="hover:underline text-[var(--ink-navy)]">
                          {r.note || `Labour for ${ref.jewellery_no || ''}`}
                        </Link>
                      ) : (
                        <span>
                          {r.note ||
                            (isCharge
                              ? 'Manual labour charge'
                              : isCredit
                              ? `Labour for ${ref.jewellery_no || ''}`
                              : 'Labour payment')}
                        </span>
                      )}
                      {isCredit && !isCharge && ref.source_order_no && (
                        <div className="text-[10px] text-slate-400 mt-0.5">Order {ref.source_order_no}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 font-mono">{refDisplay}</td>
                    {/* Wave 3.36 — Due date cell (DD-MM-YYYY display). */}
                    <td className="px-3 py-2 text-xs">
                      {isCredit && r.due_date ? (
                        <span
                          className={`font-mono ${isOverdue ? 'text-red-700 font-bold' : 'text-slate-700'}`}
                          title={r.credit_days ? `Credit ${r.credit_days} day(s)` : undefined}
                          data-testid={`labour-ledger-due-${r.id}`}
                        >
                          {(() => {
                            const [y, m, d] = r.due_date.split('-');
                            return `${d}-${m}-${y}`;
                          })()}
                          {isOverdue && <span className="ml-1 text-[10px] uppercase tracking-wide">overdue</span>}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-blue-700">
                      {isCredit ? formatINR(r.amount) : ''}
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-rose-700">
                      {!isCredit ? formatINR(r.amount) : ''}
                    </td>
                    {/* Wave 3.36 — Paid cell (credit rows only). */}
                    <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-emerald-700">
                      {isCredit ? formatINR(paid) : ''}
                    </td>
                    {/* Wave 3.36 — Outstanding cell. For payment rows show
                        any unallocated amount as informational credit. */}
                    <td className={`px-3 py-2 text-xs text-right font-mono tabular-nums ${outstandingTone}`}>
                      {isCredit
                        ? formatINR(outstanding)
                        : (Number(r.unallocated_amount || 0) > 0
                            ? <span className="text-amber-700" title="Unallocated credit">{formatINR(r.unallocated_amount)} cr</span>
                            : '—')}
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-mono tabular-nums text-amber-900 font-semibold">
                      {formatINR(r.running_balance || 0)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isCharge && canWriteCharges && (
                        <button
                          className="text-rose-500 hover:text-rose-700"
                          onClick={() => deleteCharge(r)}
                          title="Delete this charge"
                          data-testid={`labour-ledger-delete-charge-${r.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                      {!isCredit && canWrite && (
                        <button
                          className="text-rose-500 hover:text-rose-700"
                          onClick={() => voidPayment(r)}
                          title="Void this payment"
                          data-testid={`labour-ledger-void-${r.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {ledgerView?.sortDir === 'desc' && labourOpeningRow}
              {hasRange && displayRows.length === 0 && (
                <tr data-testid="labour-ledger-range-empty">
                  <td colSpan={11} className="px-3 py-6 text-center text-xs text-slate-500">
                    No labour movements in the selected date range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Record Payment dialog */}
      <RecordLabourPaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        goldsmithId={goldsmith?.id}
        goldsmithName={goldsmith?.name}
        currentBalance={totals.closing_balance_inr || 0}
        onSaved={reload}
      />

      {/* Wave 3.99i — Record Charges dialog (manual credit / payable). */}
      <RecordLabourChargesDialog
        open={chargeOpen}
        onOpenChange={setChargeOpen}
        goldsmithId={goldsmith?.id}
        goldsmithName={goldsmith?.name}
        onSaved={reload}
      />

      {/* PDF download dialog */}
      <PdfDownloadDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        title={`Labour Ledger — ${goldsmith?.name || 'Goldsmith'}`}
        description="A4 landscape PDF with credits, payments, and running balance."
        companyId={effectiveCompanyId}
        endpoint={`/goldsmiths/${goldsmith?.id}/labour-ledger/pdf`}
        extraParams={{ sort: ledgerView?.sortDir || 'asc', date_from: ledgerView?.from || '', date_to: ledgerView?.to || '' }}
        filename={`Labour_Ledger_${(goldsmith?.name || 'goldsmith').replace(/\s+/g, '_')}.pdf`}
        mode="download"
      />

      {/* Wave 3.36 — Per-goldsmith Pending Bills dialog. Cutoff is mandatory
          and triggers a direct PDF download on submit. */}
      <Dialog open={pendingDlgOpen} onOpenChange={setPendingDlgOpen}>
        <DialogContent className="max-w-md" data-testid="goldsmith-pending-bills-dialog">
          <DialogHeader>
            <DialogTitle>Pending Bills — {goldsmith?.name || 'Goldsmith'}</DialogTitle>
            <DialogDescription>
              Print every outstanding labour bill for this goldsmith whose due date falls on or before the chosen cutoff.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs text-slate-500">Due on or before *</Label>
              <DatePicker
                value={pendingCutoff}
                onChange={(e) => setPendingCutoff(e.target.value)}
                data-testid="goldsmith-pending-bills-cutoff"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">
                Bills with no due date are excluded.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDlgOpen(false)}>Cancel</Button>
            <Button
              onClick={downloadPendingBills}
              disabled={pendingBusy || !pendingCutoff}
              className="luxury-button"
              data-testid="goldsmith-pending-bills-download"
            >
              {pendingBusy ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ----------------------------------------------------------------------------
// Wave 3.22 — Record Labour Payment dialog
// ----------------------------------------------------------------------------
function RecordLabourPaymentDialog({ open, onOpenChange, goldsmithId, goldsmithName, currentBalance, onSaved }) {
  const { effectiveCompanyId } = useAuth();
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('bank');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount('');
      setMode('bank');
      setReference('');
      setNote('');
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await api.post(
        `/goldsmiths/${goldsmithId}/labour-payments`,
        {
          amount: amt,
          payment_mode: mode,
          payment_reference: reference || null,
          note: note || null,
        },
        { params: { company_id: effectiveCompanyId } },
      );
      toast.success(`Recorded ₹${amt.toLocaleString('en-IN')} payment to ${goldsmithName}`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(extractError(e, 'Failed to record payment'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="labour-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Labour Payment</DialogTitle>
          <DialogDescription>
            Manual payment to <span className="font-medium text-[var(--ink-navy)]">{goldsmithName || 'goldsmith'}</span>.
            {currentBalance > 0 && (
              <> Current outstanding: <span className="font-mono font-medium">{formatINR(currentBalance)}</span>.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="lp-amount">Amount (₹)</Label>
            <Input
              id="lp-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={currentBalance > 0 ? `e.g. ${currentBalance.toFixed(0)}` : '0.00'}
              data-testid="labour-payment-amount-input"
              autoFocus
            />
            {currentBalance > 0 && Number(amount) > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                After payment: <span className="font-mono">{formatINR(Math.max(0, currentBalance - Number(amount)))}</span> outstanding
              </div>
            )}
          </div>
          <div>
            <Label>Payment mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="labour-payment-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank">Bank Transfer</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="lp-ref">Reference (optional)</Label>
            <Input
              id="lp-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Txn ID, Cheque no, UTR…"
              data-testid="labour-payment-reference-input"
            />
          </div>
          <div>
            <Label htmlFor="lp-note">Note (optional)</Label>
            <Textarea
              id="lp-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Any internal notes for this payment"
              data-testid="labour-payment-note-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="luxury-button" data-testid="labour-payment-submit-button">
            {saving ? 'Saving…' : 'Record Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ----------------------------------------------------------------------------
// Wave 3.99i — Record Labour Charges dialog.
//
// Manually adds an amount to the goldsmith's payable balance (a
// ``labour_charge`` entry — direction=credit). Distinct from an
// auto-posted labour_credit (which comes from jewellery receipt) and
// from a labour_payment (which decreases payable). Available to both
// Super Admin and Company Admin.
// ----------------------------------------------------------------------------
function RecordLabourChargesDialog({ open, onOpenChange, goldsmithId, goldsmithName, onSaved }) {
  const { effectiveCompanyId } = useAuth();
  const [date, setDate] = useState('');
  const [particulars, setParticulars] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Default the date to today (IST) so the operator can just fill
      // in the amount + particulars and be done.
      const nowIst = new Date(new Date().getTime() + (5.5 * 3600 * 1000));
      setDate(nowIst.toISOString().slice(0, 10));
      setParticulars('');
      setAmount('');
      setReference('');
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount greater than zero.');
      return;
    }
    if (!particulars.trim()) {
      toast.error('Particulars are required.');
      return;
    }
    setSaving(true);
    try {
      // Convert the picked calendar date (YYYY-MM-DD) into an ISO
      // datetime pinned to noon IST so the row sorts naturally
      // between other same-day movements.
      let ts = null;
      if (date) {
        // Noon IST → subtract 5h30m to reach UTC.
        const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
        const noonIstUtcMs = Date.UTC(y, m - 1, d, 6, 30, 0);
        ts = new Date(noonIstUtcMs).toISOString();
      }
      await api.post(
        `/goldsmiths/${goldsmithId}/labour-charges`,
        {
          amount: amt,
          particulars: particulars.trim(),
          reference: reference.trim() || null,
          timestamp: ts,
        },
        { params: { company_id: effectiveCompanyId } },
      );
      toast.success(`Recorded ₹${amt.toLocaleString('en-IN')} charge on ${goldsmithName || 'goldsmith'}`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(extractError(e, 'Failed to record charge'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="labour-charges-dialog">
        <DialogHeader>
          <DialogTitle>Record Labour Charges</DialogTitle>
          <DialogDescription>
            Add a manual payable to{' '}
            <span className="font-medium text-[var(--ink-navy)]">{goldsmithName || 'goldsmith'}</span>.
            The entered amount is posted as a Credit and increases the outstanding
            balance owed to the goldsmith.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="lc-date">Date</Label>
            <DatePicker
              id="lc-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="labour-charges-date-input"
            />
          </div>
          <div>
            <Label htmlFor="lc-particulars">Particulars</Label>
            <Textarea
              id="lc-particulars"
              value={particulars}
              onChange={(e) => setParticulars(e.target.value)}
              rows={2}
              placeholder="e.g. Extra polish work for order OF-XXX, small repair, agreed adjustment…"
              data-testid="labour-charges-particulars-input"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="lc-amount">Amount (₹)</Label>
            <Input
              id="lc-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              data-testid="labour-charges-amount-input"
            />
          </div>
          <div>
            <Label htmlFor="lc-ref">Reference (optional)</Label>
            <Input
              id="lc-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Bill no, order no, or any internal reference…"
              data-testid="labour-charges-reference-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="labour-charges-submit-button"
          >
            {saving ? 'Saving…' : 'Record Charges'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
